import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { languageLabel } from '../i18n/languageNames';
import { targetingLabel as tv } from '../i18n';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { MapContainer, TileLayer, CircleMarker, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import AdvancedTargeting from './AdvancedTargeting';
import GroupCreativesSection from './GroupCreativesSection';
import '../styles/group-creatives.css';
import { cleanName } from '../utils/names';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import { extractErrorMessage } from '../utils/errorMessage';

interface Campaign {
  id: string;
  name: string;
  advertiser: string;
  advertiser_id: string | null;
  ads_campaign_id: string | null;
  entity_status: string | null;
  start_date: string | null;
  end_date: string | null;
  frequency_cap: any | null;
}

interface GeoSearchResult {
  id: string;
  name: string;
  type: string;
}

interface LanguageOption {
  id: string;
  name: string;
}

interface CategoryOption {
  id: string;
  name: string;
  fullName?: string;
  hasChildren: boolean;
}

interface CategoryBreadcrumb {
  id: string;
  name: string;
}

interface FrequencyCap {
  unlimited?: boolean;
  time_unit?: string | null;
  time_unit_count?: number | null;
  max_impressions?: number | null;
}

interface GeoLocation {
  id: string;
  name: string;
  type: string; // 'Country' | 'Region' | 'City' | 'District' etc.
}

interface Channel {
  channel_id: string;
  name: string;
  positive_count: number;
  negative_count: number;
  sites_count: number;
}

interface NegativeKeywordList {
  id: string;
  name: string;
  targeted_count: number;
  keywords_count?: number;
}

interface DayTimeSlot {
  dayOfWeek: string;
  startHour: number;
  endHour: number;
}

interface AdvancedTargetingItem {
  id: number;
  name: string;
  full_path: string;
  google_audience_id: string | null;
  google_audience_type: string | null;
  type?: string;
}

interface ConversionGoal {
  id: string;
  name: string;
  type: string;
  tracking_activity_id: string | null;
  status: string;
}

interface RemarketingList {
  id: string;
  name: string;
  description: string | null;
  life_span: number;
  list_size: number;
  remote_id: string;
}

interface RemarketingAudiencesConfig {
  include: {
    operator: 'AND' | 'OR';
    ids: string[];  // remote_ids
  };
  exclude: {
    ids: string[];  // remote_ids
  };
}

interface Targeting {
  geo: (string | {id?: string, name?: string, type?: string})[];
  geo_included?: {targetingOptionId?: string, displayName?: string, geoRegionType?: string}[];  // API format
  geo_excluded: (string | {id?: string, name?: string, type?: string})[];
  geo_mode?: 'regions' | 'proximity';
  proximity_lists?: {proximity_list_id: number | string, radius_km: number}[];
  devices: string[];
  device_makes?: string[];
  operating_systems?: string[];
  browsers?: string[];
  environments?: string[];
  // Demographics - age/gender now stored in demographic_split for LI creation
  demographic_split?: {age_ranges?: string[], genders?: string[]};
  age_ranges?: string[];  // Legacy support
  genders?: string[];     // Legacy support
  parental_statuses: string[];
  household_income?: string[];
  languages?: string[];
  language_names?: Record<string, string>;
  categories?: string[];  // List of option_ids from ads_content_categories
  categories_data?: {id: string, name: string}[];  // Full objects for draft groups
  // Viewability & Brand Safety
  viewability?: string[];
  on_screen_position?: string[];
  content_labels_excluded?: string[];
  sensitive_categories_excluded?: string[];
  authorized_sellers?: string[];
  video_player_sizes?: string[];
  content_instream_position?: string[];
  content_outstream_position?: string[];
  content_stream_type?: string[];
  user_rewarded_content?: string[];
  omid?: string[];
  negative_keyword_lists?: string[];
  // Inventory / Channels
  channels_included?: string[];
  channels_excluded?: string[];
  // Day & Time
  day_and_time?: DayTimeSlot[];
  // Advanced Targeting (Google Audiences)
  advanced_targeting?: AdvancedTargetingItem[];
  // Remarketing Audiences (first-party audiences)
  remarketing_audiences?: RemarketingAudiencesConfig;
}

interface AdGroup {
  id: string;
  ads_io_id?: string | null;  // Remote IO ID (null for draft groups)
  name: string;
  status: string; // 'active' | 'paused' | 'ended'
  type: string; // display type indicator
  line_item_type?: 'display' | 'video';
  creative_count: number;
  creative_ids?: number[];  // IDs of assigned creatives
  start_date: string | null;
  end_date: string | null;
  budget?: number;
  impressions?: number;
  clicks?: number;
  spend?: number;
  bid_strategy?: string | null;
  // 'fixed' when the saved strategy is a fixedBid — the Pricing selector reads it
  bid_mode?: 'target' | 'fixed';
  kpi_type?: string | null;
  kpi_amount?: number | null;
  optimization_goal_ids?: string[] | null;  // Conversion goal IDs for CPA optimization
  frequency_cap?: FrequencyCap | null;
  targeting?: Targeting | null;
  daily_max_impressions?: number | null;
  targeting_push_pending?: boolean;  // True if targeting is being pushed in background
  push_pending?: boolean;  // True if group is being published in background (IO + LI + targeting)
  media_plan_type?: 'budget' | 'impressions' | 'clicks';  // Planning type
  media_plan_target?: number | null;  // Target impressions or clicks
  media_plan_daily_paused?: boolean;  // Paused until tomorrow due to reaching daily target
  budget_original?: number | null;  // Original budget in billing currency (before EUR conversion)
  kpi_amount_original?: number | null;  // Original KPI in billing currency
  budget_currency?: string;  // Billing currency (UAH, EUR)
  source?: 'dv360' | 'google' | 'meta';  // 'google'/'meta' = virtual external group, non-editable
}

interface ProximityLocation {
  id: string;
  lat: number;
  lng: number;
  address?: string;
}

interface ProximityLocationList {
  id: string;  // Sense location list ID
  name: string;
  locations: ProximityLocation[];
  locationCount?: number;  // Count from API (before locations are loaded)
  radius: number;  // Radius for targeting (stored locally, not in the list itself)
}

// Helper: sanitize numeric input (replace comma with dot, allow only digits and one dot)
const sanitizeNumericInput = (value: string, allowDecimal: boolean = true): string => {
  // Replace comma with dot
  let sanitized = value.replace(',', '.');

  if (allowDecimal) {
    // Remove all non-digit characters except dot
    sanitized = sanitized.replace(/[^\d.]/g, '');
    // Keep only the first dot
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }
  } else {
    // Integer only - remove all non-digits
    sanitized = sanitized.replace(/[^\d]/g, '');
  }

  return sanitized;
};

// DV rejects an AUDIENCE_GROUP with too many audiences (HTTP 500 "Internal error"
// above ~35 unique google audiences). Hard-cap selection at 35 UNIQUE.
const MAX_UNIQUE_AUDIENCES = 35;

const EditCampaignPage: React.FC = () => {
  const { t } = useTranslation();
  const { campaignId, groupId } = useParams<{ campaignId: string; groupId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiser: contextAdvertiser, isAllClients } = useAdvertiser();
  // Campaign currency (from advertiser's regional office, not user's)
  const [campaignCurrency, setCampaignCurrency] = useState('EUR');
  const [campaignCurrencyRate, setCampaignCurrencyRate] = useState(1);
  const currency = campaignCurrency;
  const currencyRate = campaignCurrencyRate;

  // Locked = campaign belongs to a legacy EUR advertiser. Render in read-only mode.
  const [isLocked, setIsLocked] = useState(false);

  // Create mode: no campaignId in URL
  const isCreateMode = !campaignId;

  // Navigate back to campaigns list with appropriate filter
  const navigateToCampaigns = () => {
    navigate(isDraft || isCreateMode ? '/campaigns?filter=draft' : '/campaigns');
  };

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [defaultAdvertiserId, setDefaultAdvertiserId] = useState<string | null>(null);

  // Draft/Publish state
  const [isDraft, setIsDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<AdGroup | null>(null);
  const [isDuplicatingGroup, setIsDuplicatingGroup] = useState(false);
  const [isDuplicatingCampaign, setIsDuplicatingCampaign] = useState(false);
  const [adGroups, setAdGroups] = useState<AdGroup[]>([]);
  const [selectedAdGroup, setSelectedAdGroup] = useState<AdGroup | null>(null);
  const [targetingReloadKey, setTargetingReloadKey] = useState(0); // Increment to force reload targeting from DB
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Advertisers for create mode
  const [advertisers, setAdvertisers] = useState<{advertiser_id: string, display_name: string}[]>([]);
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState<string>('');
  // Targeting templates: a saved arrangement of targeting for this client, so a
  // new group does not start from an empty form. Targeting only — budget, dates,
  // bidding and creatives are decided per group.
  const [targetingTemplates, setTargetingTemplates] = useState<
    { id: number; name: string; line_item_type: string | null; settings_count: number;
      from_other_client?: boolean; updated_at?: string }[]>([]);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);

  // Campaign form state
  const [campaignName, setCampaignName] = useState('');
  const [campaignStatus, setCampaignStatus] = useState<'active' | 'paused' | 'ended'>('active');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [frequencyCount, setFrequencyCount] = useState<number | ''>('');
  const [frequencyPeriod, setFrequencyPeriod] = useState('day');

  // Ad group form state
  const [adGroupName, setAdGroupName] = useState('');
  const [adGroupLineItemType, setAdGroupLineItemType] = useState<'display' | 'video'>('display');
  const [adGroupStatus, setAdGroupStatus] = useState<'active' | 'paused' | 'ended' | 'scheduled'>('active');
  const [adGroupStartDate, setAdGroupStartDate] = useState('');
  const [adGroupEndDate, setAdGroupEndDate] = useState('');
  const [adGroupBudget, setAdGroupBudget] = useState('');
  const [adGroupBidStrategy, setAdGroupBidStrategy] = useState('');
  // 'target' = auto-bid toward the goal (maximizeSpendAutoBid), 'fixed' = pay exactly
  // this much per unit (fixedBid). The amount field and every calculation around it
  // are identical; only the strategy object sent to the platform differs.
  const [adGroupBidMode, setAdGroupBidMode] = useState<'target' | 'fixed'>('target');
  const [adGroupKpiAmount, setAdGroupKpiAmount] = useState('');
  const [adGroupFrequencyCount, setAdGroupFrequencyCount] = useState<number | ''>('');
  const [adGroupFrequencyPeriod, setAdGroupFrequencyPeriod] = useState('week');
  const [adGroupPlanTarget, setAdGroupPlanTarget] = useState<number | ''>('');
  const [adGroupMediaPlanType, setAdGroupMediaPlanType] = useState<'budget' | 'impressions' | 'clicks'>('budget');
  const [adGroupCreativeIds, setAdGroupCreativeIds] = useState<number[]>([]);

  // Conversion goals for CPA optimization
  const [conversionGoals, setConversionGoals] = useState<ConversionGoal[]>([]);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [originalCreativeIds, setOriginalCreativeIds] = useState<number[]>([]);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [showGeoInput, setShowGeoInput] = useState(false);
  const [geoInputValue, setGeoInputValue] = useState('');
  const [geoSearchResults, setGeoSearchResults] = useState<GeoSearchResult[]>([]);
  const [geoSearchLoading, setGeoSearchLoading] = useState(false);

  // Targeting state (editable)
  const [targetingAgeRanges, setTargetingAgeRanges] = useState<string[]>([]);
  const [targetingGenders, setTargetingGenders] = useState<string[]>([]);
  const [targetingParentalStatuses, setTargetingParentalStatuses] = useState<string[]>([]);
  const [targetingHouseholdIncome, setTargetingHouseholdIncome] = useState<string[]>([]);
  const [targetingLanguages, setTargetingLanguages] = useState<string[]>([]);
  const [availableLanguages, setAvailableLanguages] = useState<LanguageOption[]>([]);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [languageSearchValue, setLanguageSearchValue] = useState('');
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const [targetingCategories, setTargetingCategories] = useState<{id: string; name: string}[]>([]);
  const [currentCategories, setCurrentCategories] = useState<CategoryOption[]>([]);
  const [categoryBreadcrumb, setCategoryBreadcrumb] = useState<CategoryBreadcrumb[]>([]);
  const [categoryParentId, setCategoryParentId] = useState<string | null>(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorySearchValue, setCategorySearchValue] = useState('');
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // Filter categories by search
  const filteredCategories = useMemo(() => {
    if (!categorySearchValue) return currentCategories;
    const search = categorySearchValue.toLowerCase();
    return currentCategories.filter(cat => cat.name.toLowerCase().includes(search));
  }, [currentCategories, categorySearchValue]);

  // Track previous ad group to detect draft→published transitions
  const prevAdGroupRef = useRef<{ id: string; ads_io_id?: string | null } | null>(null);
  // Which group the form fields were last filled from. The selectedAdGroup OBJECT
  // is replaced whenever polling refreshes the list, and refilling on that wiped
  // whatever the user had just picked — the Pricing selector snapped back to
  // Target before Save could notice a change.
  const formFilledForRef = useRef<string | null>(null);

  // Advanced Targeting (Google Audiences)
  const [advancedTargetingItems, setAdvancedTargetingItems] = useState<AdvancedTargetingItem[]>([]);
  const [originalAdvancedTargetingItems, setOriginalAdvancedTargetingItems] = useState<AdvancedTargetingItem[]>([]);

  // Remarketing Audiences (first-party audiences)
  const [availableRemarketingLists, setAvailableRemarketingLists] = useState<RemarketingList[]>([]);
  const [remarketingIncludeIds, setRemarketingIncludeIds] = useState<string[]>([]);  // remote_ids to include
  const [remarketingIncludeOperator, setRemarketingIncludeOperator] = useState<'AND' | 'OR'>('OR');
  const [remarketingExcludeIds, setRemarketingExcludeIds] = useState<string[]>([]);  // remote_ids to exclude
  const [originalRemarketingConfig, setOriginalRemarketingConfig] = useState<RemarketingAudiencesConfig | null>(null);
  const [showRemarketingSection, setShowRemarketingSection] = useState(false);
  // Look-Alike (Optimized Targeting) - only works with CPC/CPA strategies
  const [enableLookAlike, setEnableLookAlike] = useState(false);
  const [originalEnableLookAlike, setOriginalEnableLookAlike] = useState(false);

  const [targetingDevices, setTargetingDevices] = useState<string[]>([]);
  const [targetingDeviceMakes, setTargetingDeviceMakes] = useState<string[]>([]);
  const [targetingOS, setTargetingOS] = useState<string[]>([]);
  const [showDeviceAdvanced, setShowDeviceAdvanced] = useState(false);

  // Advanced Targeting Section
  const [showAdvancedTargetingSection, setShowAdvancedTargetingSection] = useState(false);

  // Viewability & Brand Safety
  const [showViewabilitySection, setShowViewabilitySection] = useState(false);
  const [targetingViewability, setTargetingViewability] = useState<string[]>([]);
  const [targetingOnScreenPosition, setTargetingOnScreenPosition] = useState<string[]>([]);
  const [targetingContentLabels, setTargetingContentLabels] = useState<string[]>([]);
  const [targetingSensitiveCategories, setTargetingSensitiveCategories] = useState<string[]>([]);
  const [targetingAuthorizedSellers, setTargetingAuthorizedSellers] = useState<string[]>([]);
  const [targetingVideoPlayerSize, setTargetingVideoPlayerSize] = useState<string[]>([]);
  // Default: all positions selected. At least 1 must be selected across instream+outstream
  const ALL_INSTREAM = ['Pre Roll', 'Mid Roll', 'Post Roll'];
  const ALL_OUTSTREAM = ['In Article', 'In Banner', 'In Feed', 'Interstitial'];
  const [targetingContentInstreamPosition, setTargetingContentInstreamPosition] = useState<string[]>(ALL_INSTREAM);
  const [targetingContentOutstreamPosition, setTargetingContentOutstreamPosition] = useState<string[]>(ALL_OUTSTREAM);
  const [targetingContentStreamType, setTargetingContentStreamType] = useState<string[]>([]);
  const [targetingUserRewardedContent, setTargetingUserRewardedContent] = useState<string[]>([]);
  const [targetingOmid, setTargetingOmid] = useState<string[]>([]);
  const [targetingBrowsers, setTargetingBrowsers] = useState<string[]>([]);
  const [targetingEnvironment, setTargetingEnvironment] = useState<string[]>([]);

  // Day & Time targeting (always uses advertiser's timezone)
  const [showDayTimeSection, setShowDayTimeSection] = useState(false);
  // Grid state: Set of "DAY-HOUR" strings for selected cells (e.g., "MONDAY-9", "TUESDAY-14")
  const [targetingDayTime, setTargetingDayTime] = useState<Set<string>>(new Set());
  // Drag selection state
  const [isDayTimeDragging, setIsDayTimeDragging] = useState(false);
  const [dayTimeDragMode, setDayTimeDragMode] = useState<'select' | 'deselect'>('select');

  const [targetingGeo, setTargetingGeo] = useState<GeoLocation[]>([]);
  const [targetingGeoExcluded, setTargetingGeoExcluded] = useState<GeoLocation[]>([]);

  // Separate input states for excluded geo
  const [showGeoExcludedInput, setShowGeoExcludedInput] = useState(false);
  const [geoExcludedInputValue, setGeoExcludedInputValue] = useState('');
  const [geoExcludedSearchResults, setGeoExcludedSearchResults] = useState<GeoSearchResult[]>([]);
  const [geoExcludedSearchLoading, setGeoExcludedSearchLoading] = useState(false);

  // Proximity Location List state
  const [geoMode, setGeoMode] = useState<'regions' | 'proximity'>('regions');
  const [proximityLists, setProximityLists] = useState<ProximityLocationList[]>([]);
  const [proximityListsLoading, setProximityListsLoading] = useState(false);
  const [savingProximityList, setSavingProximityList] = useState(false);
  const [selectedProximityList, setSelectedProximityList] = useState<ProximityLocationList | null>(null);
  const [editingProximityList, setEditingProximityList] = useState<ProximityLocationList | null>(null);
  const [proximityInput, setProximityInput] = useState('');
  const [proximityRadius, setProximityRadius] = useState(10);
  const [showSaveListModal, setShowSaveListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editingProximityListName, setEditingProximityListName] = useState('');
  const [isEditingProximityListName, setIsEditingProximityListName] = useState(false);
  const [isRenamingProximityList, setIsRenamingProximityList] = useState(false);
  const [showDeleteProximityListConfirm, setShowDeleteProximityListConfirm] = useState(false);
  const [deletingProximityList, setDeletingProximityList] = useState(false);
  const [proximityValidationError, setProximityValidationError] = useState<string | null>(null);

  // Inventory / Channels state
  const [showInventorySection, setShowInventorySection] = useState(false);
  const [allChannels, setAllChannels] = useState<Channel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  // White List
  const [whiteListChannelIds, setWhiteListChannelIds] = useState<string[]>([]); // assigned to group
  const [originalWhiteListChannelIds, setOriginalWhiteListChannelIds] = useState<string[]>([]);
  const [editingWhiteListChannel, setEditingWhiteListChannel] = useState<Channel | null>(null);
  const [whiteListInput, setWhiteListInput] = useState('');
  // Black List
  const [blackListChannelIds, setBlackListChannelIds] = useState<string[]>([]); // assigned to group
  const [originalBlackListChannelIds, setOriginalBlackListChannelIds] = useState<string[]>([]);
  const [editingBlackListChannel, setEditingBlackListChannel] = useState<Channel | null>(null);
  const [blackListInput, setBlackListInput] = useState('');
  // Editor visibility for Inventory (shown when editing existing or creating new)
  const [isCreatingWhiteList, setIsCreatingWhiteList] = useState(false);
  const [isCreatingBlackList, setIsCreatingBlackList] = useState(false);
  // Save modals for Inventory
  const [showWhiteListSaveModal, setShowWhiteListSaveModal] = useState(false);
  const [showBlackListSaveModal, setShowBlackListSaveModal] = useState(false);
  const [newWhiteListName, setNewWhiteListName] = useState('');
  const [newBlackListName, setNewBlackListName] = useState('');
  const [originalWhiteListInput, setOriginalWhiteListInput] = useState('');
  const [originalBlackListInput, setOriginalBlackListInput] = useState('');
  // Channel editing states
  const [editingWhiteListChannelName, setEditingWhiteListChannelName] = useState('');
  const [editingBlackListChannelName, setEditingBlackListChannelName] = useState('');
  const [isEditingWhiteListChannelName, setIsEditingWhiteListChannelName] = useState(false);
  const [isEditingBlackListChannelName, setIsEditingBlackListChannelName] = useState(false);
  const [isRenamingWhiteListChannel, setIsRenamingWhiteListChannel] = useState(false);
  const [isRenamingBlackListChannel, setIsRenamingBlackListChannel] = useState(false);
  const [savingWhiteList, setSavingWhiteList] = useState(false);
  const [savingBlackList, setSavingBlackList] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [campaignSaved, setCampaignSaved] = useState(false);
  const [savingAdGroup, setSavingAdGroup] = useState(false);
  const [targetingPushPending, setTargetingPushPending] = useState(false);  // Separate state for pending status
  const targetingPushPendingRef = useRef(false);  // Ref backup for closure issues
  const [adGroupSaved, setAdGroupSaved] = useState(false);
  const [publishingGroup, setPublishingGroup] = useState(false);
  const [publishingAllGroups, setPublishingAllGroups] = useState(false);
  const [showDeleteWhiteListConfirm, setShowDeleteWhiteListConfirm] = useState(false);
  const [showDeleteBlackListConfirm, setShowDeleteBlackListConfirm] = useState(false);
  const [isDeletingWhiteListChannel, setIsDeletingWhiteListChannel] = useState(false);
  const [isDeletingBlackListChannel, setIsDeletingBlackListChannel] = useState(false);
  // Message modal
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalText, setMessageModalText] = useState('');
  const [messageModalType, setMessageModalType] = useState<'info' | 'error'>('info');
  // Negative Keyword Lists
  const [allNegativeKeywordLists, setAllNegativeKeywordLists] = useState<NegativeKeywordList[]>([]);
  const [negativeKeywordListIds, setNegativeKeywordListIds] = useState<string[]>([]);
  const [originalNegativeKeywordListIds, setOriginalNegativeKeywordListIds] = useState<string[]>([]);
  const [editingNegKwList, setEditingNegKwList] = useState<NegativeKeywordList | null>(null);
  const [isCreatingNegKwList, setIsCreatingNegKwList] = useState(false);
  const [negKwListInput, setNegKwListInput] = useState('');
  const [originalNegKwListInput, setOriginalNegKwListInput] = useState('');
  const [showNegKwListSaveModal, setShowNegKwListSaveModal] = useState(false);
  const [newNegKwListName, setNewNegKwListName] = useState('');
  const [editingNegKwListName, setEditingNegKwListName] = useState('');
  const [isRenamingNegKwList, setIsRenamingNegKwList] = useState(false);
  const [isEditingNegKwListName, setIsEditingNegKwListName] = useState(false);

  // Check if there are unsaved changes in proximity list
  const hasProximityChanges = (): boolean => {
    if (!editingProximityList) return false;

    // Compare radius
    if (proximityRadius !== editingProximityList.radius) return true;

    // Compare locations - convert original to text format for comparison
    const originalText = editingProximityList.locations.map(l => `${l.lat}, ${l.lng}`).join('\n');
    const currentText = proximityInput.trim();

    return originalText !== currentText;
  };

  // Check if there are unsaved changes in White List
  const hasWhiteListChanges = (): boolean => {
    if (!editingWhiteListChannel) return false;
    return whiteListInput.trim() !== originalWhiteListInput.trim();
  };

  // Check if there are unsaved changes in Black List
  const hasBlackListChanges = (): boolean => {
    if (!editingBlackListChannel) return false;
    return blackListInput.trim() !== originalBlackListInput.trim();
  };

  // Check if group has all required fields for publishing (from form state)
  const getGroupPublishValidation = (): { canPublish: boolean; missingFields: string[] } => {
    const missingFields: string[] = [];

    if (!adGroupName.trim()) missingFields.push(t('edit.fieldName'));
    if (!adGroupStartDate) missingFields.push(t('edit.startDate'));
    if (!adGroupEndDate) missingFields.push(t('edit.endDate'));
    if (!adGroupBudget || adGroupBudget === '0') missingFields.push(t('edit.budget'));
    if (!adGroupKpiAmount || adGroupKpiAmount === '0') missingFields.push(t('edit.fieldTargetCpmCpc'));
    // CPA strategy requires at least one conversion goal
    if (adGroupBidStrategy === 'CPA' && selectedGoalIds.length === 0) missingFields.push(t('edit.fieldConversionGoals'));
    // Media plan requires planned value when type is impressions or clicks
    if (adGroupMediaPlanType !== 'budget' && !adGroupPlanTarget) missingFields.push(t('edit.fieldPlannedValue'));
    // Geography is MANDATORY — publishing with no INCLUDED geo serves globally
    // and drains budget (the PMI_Zenith incident). Only INCLUDED counts: a filled
    // exclusion still leaves the rest of the world targeted. Proximity counts.
    const geoEmpty = geoMode === 'proximity'
      ? proximityLists.length === 0
      : targetingGeo.length === 0;
    if (geoEmpty) missingFields.push(t('edit.geography'));
    // Too many audiences → DV 500s and line items stay DRAFT. Block publish.
    const uniqueAudiences = new Set(
      advancedTargetingItems.map(a => a.google_audience_id).filter(Boolean)
    ).size;
    if (uniqueAudiences > MAX_UNIQUE_AUDIENCES) {
      missingFields.push(t('edit.tooManyAudiencesShort', { n: uniqueAudiences, max: MAX_UNIQUE_AUDIENCES }));
    }

    return {
      canPublish: missingFields.length === 0,
      missingFields
    };
  };

  // Enforce the audience cap AT SELECTION time: once 35 UNIQUE google audiences
  // are picked, block adding another (DV 500s above ~35 and the line items get
  // stuck in DRAFT). Removing or re-picking an already-selected google id is fine.
  const handleAudienceSelectionChange = (items: AdvancedTargetingItem[]) => {
    const uniqueNext = new Set(items.map(a => a.google_audience_id).filter(Boolean)).size;
    const uniqueCurr = new Set(advancedTargetingItems.map(a => a.google_audience_id).filter(Boolean)).size;
    if (uniqueNext > MAX_UNIQUE_AUDIENCES && uniqueNext > uniqueCurr) {
      setMessageModalText(t('edit.audienceLimitReached'));
      setMessageModalType('info');
      setShowMessageModal(true);
      return; // reject: keep the current selection unchanged
    }
    setAdvancedTargetingItems(items);
  };

  // Check if a group object can be published (from saved group data, not form state)
  const canGroupBePublished = (group: AdGroup): boolean => {
    if (!group.name?.trim()) return false;
    if (!group.start_date) return false;
    if (!group.end_date) return false;
    if (!group.budget || group.budget === 0) return false;
    if (!group.kpi_amount || group.kpi_amount === 0) return false;
    // CPA strategy requires at least one conversion goal
    if (group.bid_strategy === 'CPA' && (!group.optimization_goal_ids || group.optimization_goal_ids.length === 0)) return false;
    // Media plan requires planned value when type is impressions or clicks
    if (group.media_plan_type && group.media_plan_type !== 'budget' && !group.media_plan_target) return false;
    return true;
  };

  // Get validation result for saved group data (for Publish button)
  const getSavedGroupPublishValidation = (group: AdGroup | null): { canPublish: boolean; missingFields: string[] } => {
    if (!group) return { canPublish: false, missingFields: [t('edit.groupNotSelected')] };
    const missingFields: string[] = [];

    if (!group.name?.trim()) missingFields.push(t('edit.fieldName'));
    if (!group.start_date) missingFields.push(t('edit.startDate'));
    if (!group.end_date) missingFields.push(t('edit.endDate'));
    if (!group.budget || group.budget === 0) missingFields.push(t('edit.budget'));
    if (!group.kpi_amount || group.kpi_amount === 0) missingFields.push(t('edit.fieldTargetCpmCpc'));

    // CPA strategy requires at least one conversion goal
    if (group.bid_strategy === 'CPA' && (!group.optimization_goal_ids || group.optimization_goal_ids.length === 0)) {
      missingFields.push(t('edit.fieldConversionGoals'));
    }

    // Media plan requires planned value when type is impressions or clicks
    if (group.media_plan_type && group.media_plan_type !== 'budget' && !group.media_plan_target) {
      missingFields.push(t('edit.fieldPlannedValue'));
    }

    return {
      canPublish: missingFields.length === 0,
      missingFields
    };
  };

  // Get publishable draft groups (draft + all required fields filled + not already publishing)
  const publishableDraftGroups = adGroups.filter(ag => !ag.ads_io_id && !ag.push_pending && canGroupBePublished(ag));

  // Helper to infer location type from string format
  const inferLocationType = (name: string): string => {
    // "Kyiv city, Ukraine" or "Vinnytsia Oblast, Ukraine" → Region
    // "Kyiv, Kyiv city, Ukraine" or "Vinnytsia, Vinnytsia Oblast, Ukraine" → City
    // "Ukraine" → Country
    const parts = name.split(', ');
    if (parts.length === 1) return 'Country';
    if (parts.length === 2) {
      if (parts[0].includes('Oblast') || parts[0].includes('city')) return 'Region';
      return 'Country';
    }
    if (parts.length >= 3) return 'City';
    return 'Other';
  };

  // Convert geo data to GeoLocation array
  // Accepts either string array (legacy) or object array (new format with id)
  const convertToGeoLocations = (geoData: (string | {id?: string, name?: string, type?: string})[]): GeoLocation[] => {
    return geoData.map((item, idx) => {
      if (typeof item === 'string') {
        // Legacy format: just name string
        return {
          id: `existing-${idx}`,
          name: item,
          type: inferLocationType(item)
        };
      } else {
        // New format: object with id, name, type
        return {
          id: item.id || `existing-${idx}`,
          name: item.name || '',
          type: item.type || inferLocationType(item.name || '')
        };
      }
    });
  };

  // Group locations by type
  const groupLocationsByType = (locations: GeoLocation[]): Record<string, GeoLocation[]> => {
    const groups: Record<string, GeoLocation[]> = {};
    const typeOrder = ['Country', 'Region', 'City', 'District', 'Other'];

    locations.forEach(loc => {
      const type = loc.type || 'Other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(loc);
    });

    // Return in order
    const ordered: Record<string, GeoLocation[]> = {};
    typeOrder.forEach(type => {
      if (groups[type]) ordered[type] = groups[type];
    });
    // Add any remaining types
    Object.keys(groups).forEach(type => {
      if (!ordered[type]) ordered[type] = groups[type];
    });

    return ordered;
  };

  // Parse proximity input textarea into locations (format: lat, lng per line)
  const parseProximityInput = (input: string, _format?: string): ProximityLocation[] => {
    const lines = input.split('\n').filter(line => line.trim());
    const locations: ProximityLocation[] = [];

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Parse coordinates (lat, lng)
      const parts = trimmed.split(/[,\s]+/).filter(p => p);
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lng)) {
          locations.push({
            id: `loc-${idx}-${Date.now()}`,
            lat,
            lng
          });
        }
      }
    });

    return locations;
  };

  // Remove error marker from line if present
  const cleanErrorMarker = (line: string): string => {
    return line.replace(/^⚠️\s*/, '').trim();
  };

  // Validate proximity input and return valid/invalid lines
  const validateProximityInput = (input: string): {
    valid: ProximityLocation[],
    invalidLines: string[],
    isValid: boolean
  } => {
    const lines = input.split('\n').filter(line => line.trim());
    const valid: ProximityLocation[] = [];
    const invalidLines: string[] = [];

    lines.forEach((line, idx) => {
      const trimmed = cleanErrorMarker(line);
      if (!trimmed) return;

      // Parse coordinates (lat, lng)
      const parts = trimmed.split(/[,\s]+/).filter(p => p);
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        // Validate ranges: lat -90 to 90, lng -180 to 180
        if (!isNaN(lat) && !isNaN(lng) &&
            lat >= -90 && lat <= 90 &&
            lng >= -180 && lng <= 180) {
          valid.push({
            id: `loc-${idx}-${Date.now()}`,
            lat,
            lng
          });
        } else {
          invalidLines.push(trimmed);
        }
      } else {
        invalidLines.push(trimmed);
      }
    });

    return { valid, invalidLines, isValid: invalidLines.length === 0 };
  };

  // Reorder proximity input: valid lines first, then invalid with marker
  const reorderProximityInputWithErrors = (input: string): string => {
    const lines = input.split('\n').filter(line => line.trim());
    const validLines: string[] = [];
    const invalidLines: string[] = [];

    lines.forEach((line) => {
      const trimmed = cleanErrorMarker(line);
      if (!trimmed) return;

      const parts = trimmed.split(/[,\s]+/).filter(p => p);
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lng) &&
            lat >= -90 && lat <= 90 &&
            lng >= -180 && lng <= 180) {
          validLines.push(trimmed);
        } else {
          invalidLines.push(`⚠️ ${trimmed}`);
        }
      } else {
        invalidLines.push(`⚠️ ${trimmed}`);
      }
    });

    return [...validLines, ...invalidLines].join('\n');
  };

  useEffect(() => {
    if (isCreateMode) {
      // Create mode: setup empty form with defaults
      setLoading(true);
      loadDefaultAdvertiser();
    } else if (campaignId) {
      fetchCampaignData();
    }
  }, [campaignId, isCreateMode]);

  // Update advertiser when user switches client in create mode
  useEffect(() => {
    if (isCreateMode && contextAdvertiser && contextAdvertiser.advertiser_id !== '__all__') {
      setDefaultAdvertiserId(contextAdvertiser.advertiser_id);
      setSelectedAdvertiserId(contextAdvertiser.advertiser_id);
    }
  }, [isCreateMode, contextAdvertiser?.advertiser_id]);

  // Polling for pending groups - refresh ad groups list when any group has targeting_push_pending or push_pending
  useEffect(() => {
    const hasPendingGroups = adGroups.some(ag => ag.targeting_push_pending || ag.push_pending);
    if (!hasPendingGroups || !campaignId) return;

    const pollInterval = setInterval(async () => {
      try {
        // Try published campaign endpoint first
        let response;
        try {
          response = await api.get(`/api/reports/campaign/${campaignId}/ad-groups`);
        } catch (err: any) {
          if (err.response?.status === 404) {
            // Try sense groups endpoint
            response = await api.get(`/api/sense/groups?campaign_id=${campaignId}`);
          } else {
            throw err;
          }
        }

        const newGroups = response.data?.ad_groups || response.data?.groups || [];

        // Update pending status AND ads_io_id for groups that completed background publish
        setAdGroups(prev => prev.map(ag => {
          const updated = newGroups.find((ng: any) =>
            (ng.id?.toString() === ag.id?.toString()) ||
            (ng.ads_io_id?.toString() === ag.ads_io_id?.toString())
          );
          if (updated) {
            const hasChanges =
              ag.targeting_push_pending !== (updated.targeting_push_pending ?? false) ||
              ag.push_pending !== (updated.push_pending ?? false) ||
              (!ag.ads_io_id && updated.ads_io_id);  // ads_io_id appeared (background publish completed)

            if (hasChanges) {
              return {
                ...ag,
                targeting_push_pending: updated.targeting_push_pending ?? false,
                push_pending: updated.push_pending ?? false,
                ads_io_id: updated.ads_io_id || ag.ads_io_id
              };
            }
          }
          return ag;
        }));

        // Also update selectedAdGroup if it has pending status
        if (selectedAdGroup?.targeting_push_pending || selectedAdGroup?.push_pending || targetingPushPending) {
          const updatedSelected = newGroups.find((ng: any) =>
            (ng.id?.toString() === selectedAdGroup?.id?.toString()) ||
            (ng.ads_io_id?.toString() === selectedAdGroup?.ads_io_id?.toString())
          );
          if (updatedSelected) {
            const serverPushPending = updatedSelected.push_pending ?? false;
            const serverTargetingPending = updatedSelected.targeting_push_pending ?? false;
            const pendingCleared = !serverTargetingPending && !serverPushPending;
            const ioIdAppeared = !selectedAdGroup?.ads_io_id && updatedSelected.ads_io_id;

            // Update selectedAdGroup with server state
            if (pendingCleared || ioIdAppeared) {
              setSelectedAdGroup(prev => prev ? {
                ...prev,
                // Use server values, not always false
                targeting_push_pending: serverTargetingPending,
                push_pending: serverPushPending,
                ads_io_id: updatedSelected.ads_io_id || prev.ads_io_id
              } : null);

              // Update URL when ads_io_id appears (background publish completed)
              if (ioIdAppeared && updatedSelected.ads_io_id) {
                navigate(`/campaigns/${campaignId}/edit/${updatedSelected.ads_io_id}`, { replace: true });
              }

              // Only clear targetingPushPending state when server confirms it's done
              if (pendingCleared) {
                setTargetingPushPending(false);
                targetingPushPendingRef.current = false;
                // Background targeting push finished — now reload from DB so the UI
                // converges to the DV-confirmed state. Until this point the
                // optimistic local state (set on save) is shown, so the user never
                // sees their selection disappear.
                if (updatedSelected.ads_io_id || selectedAdGroup?.ads_io_id) {
                  setTargetingReloadKey(k => k + 1);
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [adGroups, campaignId, targetingPushPending, selectedAdGroup]);

  // Close delete modals on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDeleteGroupModal && !isDeletingGroup) {
          setShowDeleteGroupModal(false);
          setGroupToDelete(null);
        } else if (showDeleteModal && !isDeleting) {
          setShowDeleteModal(false);
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showDeleteModal, isDeleting, showDeleteGroupModal, isDeletingGroup]);

  // Helper functions for default dates
  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const getEndOfMonthDate = () => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return endOfMonth.toISOString().split('T')[0];
  };

  // Load advertisers for create mode
  const loadAdvertisers = async () => {
    try {
      const response = await api.get(`/ads/tree/advertisers`);
      const allAdvs = response.data.data || response.data.advertisers || [];
      const advList = allAdvs.filter((a: any) => a.entity_status === 'ENTITY_STATUS_ACTIVE');
      setAdvertisers(advList);
      return advList;
    } catch (err) {
      console.error('Failed to load advertisers:', err);
      return [];
    }
  };

  // Load default advertiser for create mode — use context advertiser
  const loadDefaultAdvertiser = async () => {
    try {
      // Still load list for backward compat, but use context advertiser
      await loadAdvertisers();

      const defaultStartDate = getTodayDate();
      const defaultEndDate = getEndOfMonthDate();

      const advId = contextAdvertiser?.advertiser_id || '';
      const advName = contextAdvertiser?.display_name || '';
      if (advId && advId !== '__all__') {
        setSelectedAdvertiserId(advId);
        setDefaultAdvertiserId(advId);

        setCampaign({
          id: '',
          name: '',
          advertiser: advName,
          advertiser_id: advId,
          ads_campaign_id: null,
          entity_status: 'ENTITY_STATUS_DRAFT',
          start_date: defaultStartDate,
          end_date: defaultEndDate,
          frequency_cap: null
        });
      }

      // Set form defaults
      setCampaignName('');
      setCampaignStatus('active');
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);
      setFrequencyCount('');
      setFrequencyPeriod('day');
      setIsDraft(true);
    } catch (err) {
      console.error('Failed to initialize create mode:', err);
      setError(t('edit.failedInit'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch available languages when campaign is loaded
  useEffect(() => {
    const fetchLanguages = async () => {
      if (!campaign?.advertiser_id) return;
      try {
        const response = await api.get(`/api/targeting/languages`, {
          params: { advertiser_id: campaign.advertiser_id }
        });
        setAvailableLanguages(response.data.languages || []);
      } catch (err) {
        console.error('Failed to fetch languages:', err);
      }
    };
    fetchLanguages();
  }, [campaign?.advertiser_id]);

  // Fetch conversion goals for CPA optimization
  useEffect(() => {
    const fetchConversionGoals = async () => {
      if (!campaign?.advertiser_id) return;
      try {
        const response = await api.get(`/api/conversions/goals`, {
          params: { advertiser_id: campaign.advertiser_id, status: 'active' }
        });
        // Filter to only goals with tracking_activity_id (platform tracking configured)
        const goals = (response.data.goals || []).filter(
          (g: ConversionGoal) => g.tracking_activity_id
        );
        setConversionGoals(goals);
      } catch (err) {
        console.error('Failed to fetch conversion goals:', err);
      }
    };
    fetchConversionGoals();
  }, [campaign?.advertiser_id]);

  // Click outside handler for language dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };
    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown]);

  // Fetch custom categories (tree navigation)
  const fetchCategories = async (parentId: string | null) => {
    // Only show loader after 150ms delay to avoid flicker on fast responses
    const loaderTimeout = setTimeout(() => setCategoriesLoading(true), 150);
    try {
      const params: Record<string, any> = {};
      if (parentId !== null) {
        params.parent_id = parentId;
      }
      const response = await api.get(`/api/targeting/custom-categories`, { params });
      setCurrentCategories(response.data.categories || []);
      setCategoryBreadcrumb(response.data.breadcrumb || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCurrentCategories([]);
    } finally {
      clearTimeout(loaderTimeout);
      setCategoriesLoading(false);
    }
  };

  // Fetch categories when dropdown opens or parent changes
  useEffect(() => {
    if (showCategoryDropdown) {
      fetchCategories(categoryParentId);
    }
  }, [showCategoryDropdown, categoryParentId]);

  // Click outside handler for category dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };
    if (showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryDropdown]);

  // Global mouseup handler for day/time drag selection
  useEffect(() => {
    const handleMouseUp = () => {
      setIsDayTimeDragging(false);
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const fetchCampaignData = async () => {
    try {
      setLoading(true);

      let campaignInfo: any = null;
      let adGroupsData: any = { ad_groups: [] };
      let isLocalSenseCampaign = false;

      // Try regular API first
      try {
        const infoResponse = await api.get(`/api/reports/campaign/${campaignId}/info`);
        campaignInfo = infoResponse.data.campaign;
        // Reports API returns ads_campaign_id as 'id', need to set ads_campaign_id explicitly
        // If we got here, the campaign is published (has ads_campaign_id)
        campaignInfo.ads_campaign_id = campaignInfo.id;

        // Set campaign currency from advertiser
        if (infoResponse.data.currency) {
          setCampaignCurrency(infoResponse.data.currency);
          setCampaignCurrencyRate(infoResponse.data.currency_rate || 1);
        }
        // Read-only mode for legacy EUR advertisers
        setIsLocked(Boolean(infoResponse.data.is_locked));

        // Fetch ad groups
        const adGroupsResponse = await api.get(`/api/reports/campaign/${campaignId}/ad-groups`);
        adGroupsData = adGroupsResponse.data;
      } catch (err: any) {
        // If 410 (archived), redirect to campaigns list
        if (err.response?.status === 410) {
          navigateToCampaigns();
          return;
        }
        // If 404, try sense API (local campaign)
        if (err.response?.status === 404) {
          try {
            const senseResponse = await api.get(`/api/sense/campaigns/${campaignId}`);
            const senseData = senseResponse.data;
            isLocalSenseCampaign = true;

            // Set campaign currency from advertiser
            if (senseData.currency) {
              setCampaignCurrency(senseData.currency);
              setCampaignCurrencyRate(senseData.currency_rate || 1);
            }

          // Map sense campaign to campaignInfo format
          campaignInfo = {
            id: senseData.id,
            name: senseData.display_name,
            advertiser: senseData.advertiser_name || '',
            advertiser_id: senseData.advertiser_id,
            ads_campaign_id: senseData.ads_campaign_id,
            entity_status: senseData.entity_status,
            start_date: senseData.start_date,
            end_date: senseData.end_date,
            frequency_cap: senseData.frequency_cap
          };

          // Fetch sense groups for this campaign
          try {
            const groupsResponse = await api.get(`/api/sense/groups?campaign_id=${campaignId}`);
            const senseGroups = groupsResponse.data?.groups || groupsResponse.data?.data || [];
            adGroupsData = {
              ad_groups: senseGroups.map((g: any) => {
                // Parse budget to get amount
                let budgetAmount: number | undefined;
                if (g.budget) {
                  if (g.budget.budgetAmountMicros) {
                    budgetAmount = Number(g.budget.budgetAmountMicros) / 1000000;
                  } else if (g.budget.budgetSegments?.[0]?.budgetAmountMicros) {
                    budgetAmount = Number(g.budget.budgetSegments[0].budgetAmountMicros) / 1000000;
                  }
                }

                // Parse KPI
                let kpiAmount: number | null = null;
                if (g.kpi?.kpiAmountMicros) {
                  kpiAmount = Number(g.kpi.kpiAmountMicros) / 1000000;
                }

                // Parse bid strategy - API returns string ('CPA', 'CPC', etc.) or object
                let bidStrategy: string | null = null;
                if (typeof g.bid_strategy === 'string') {
                  // Already parsed by API
                  bidStrategy = g.bid_strategy;
                } else if (g.bid_strategy?.fixedBid) {
                  // Fixed price: the goal type is carried by the KPI, not the bid strategy
                  bidStrategy = 'AV_VIEWED';
                } else if (g.bid_strategy?.maximizeSpendAutoBid?.performanceGoalType) {
                  // Legacy object format - parse it
                  const perfGoal = g.bid_strategy.maximizeSpendAutoBid.performanceGoalType;
                  if (perfGoal.includes('CPA')) bidStrategy = 'CPA';
                  else if (perfGoal.includes('CPC')) bidStrategy = 'CPC';
                  else if (perfGoal.includes('AV_VIEWED')) bidStrategy = 'AV_VIEWED';
                  else if (perfGoal.includes('CIVA')) bidStrategy = 'CIVA';
                  else bidStrategy = 'CPM';
                }

                // Parse frequency cap
                let frequencyCap: any = null;
                if (g.frequency_cap && !g.frequency_cap.unlimited) {
                  frequencyCap = {
                    unlimited: false,
                    max_impressions: g.frequency_cap.maxImpressions,
                    time_unit: g.frequency_cap.timeUnit?.includes('DAY') ? 'day' :
                               g.frequency_cap.timeUnit?.includes('WEEK') ? 'week' :
                               g.frequency_cap.timeUnit?.includes('MONTH') ? 'month' : 'week',
                    time_unit_count: g.frequency_cap.timeUnitCount || 1
                  };
                }

                return {
                  id: g.id,
                  ads_io_id: g.ads_io_id || null,  // null for draft groups
                  name: g.display_name,
                  status: g.entity_status?.includes('PAUSED') ? 'paused' :
                          g.entity_status?.includes('DRAFT') ? 'draft' : 'active',
                  type: g.line_item_type === 'video' ? 'Video' : 'Display',
                  line_item_type: g.line_item_type || 'display',
                  creative_count: g.creative_ids?.length || 0,
                  creative_ids: g.creative_ids || [],
                  start_date: g.start_date,
                  end_date: g.end_date,
                  budget: budgetAmount,
                  bid_strategy: bidStrategy,
                  bid_mode: g.bid_strategy?.fixedBid ? 'fixed' : 'target',
                  optimization_goal_ids: g.optimization_goal_ids || null,
                  kpi_amount: kpiAmount,
                  frequency_cap: frequencyCap,
                  targeting: g.targeting || {},
                  media_plan_type: g.media_plan_type || 'budget',
                  media_plan_target: g.media_plan_target || null,
                  media_plan_daily_paused: g.media_plan_daily_paused || false
                };
              })
            };
          } catch (groupsErr) {
            console.log('No groups found for sense campaign');
          }
          } catch (senseErr: any) {
            // Handle archived campaign (410 status)
            if (senseErr.response?.status === 410) {
              navigateToCampaigns();
              return;
            }
            throw senseErr;
          }
        } else {
          throw err;
        }
      }

      setCampaign({
        id: campaignInfo.id,
        name: campaignInfo.name,
        advertiser: campaignInfo.advertiser || '',
        advertiser_id: campaignInfo.advertiser_id || null,
        ads_campaign_id: campaignInfo.ads_campaign_id || null,
        entity_status: campaignInfo.entity_status,
        start_date: campaignInfo.start_date,
        end_date: campaignInfo.end_date,
        frequency_cap: campaignInfo.frequency_cap
      });

      // Check if campaign is a draft (no ads_campaign_id means it's local only)
      // A campaign is a draft only if it doesn't have an ads_campaign_id (not yet published to Sense)
      const isLocalDraft = !campaignInfo.ads_campaign_id;
      setIsDraft(isLocalDraft);

      // Set form values (clean name from timestamp suffix)
      setCampaignName(cleanName(campaignInfo.name) || '');
      setStartDate(campaignInfo.start_date || '');
      setEndDate(campaignInfo.end_date || '');

      // Set status - check end_date first, then entity_status
      const entityStatus = campaignInfo.entity_status || '';
      const endDateStr = campaignInfo.end_date;
      // Compare dates only (not time) - campaign is ended if end_date is before today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (endDateStr) {
        const endDate = new Date(endDateStr);
        endDate.setHours(0, 0, 0, 0);
        if (endDate < today) {
          setCampaignStatus('ended');
        } else if (entityStatus.includes('PAUSED')) {
          setCampaignStatus('paused');
        } else {
          setCampaignStatus('active');
        }
      } else if (entityStatus.includes('PAUSED')) {
        setCampaignStatus('paused');
      } else {
        setCampaignStatus('active');
      }

      // Set frequency cap (empty if unlimited or not set)
      if (campaignInfo.frequency_cap && !campaignInfo.frequency_cap.unlimited) {
        setFrequencyCount(campaignInfo.frequency_cap.maxImpressions || '');
        const timeUnit = campaignInfo.frequency_cap.timeUnit || '';
        if (timeUnit.includes('DAY')) setFrequencyPeriod('day');
        else if (timeUnit.includes('WEEK')) setFrequencyPeriod('week');
        else if (timeUnit.includes('MONTH')) setFrequencyPeriod('month');
      } else {
        setFrequencyCount('');
      }

      // Map ad groups - API returns {id, name, status, type, ...}
      const mappedAdGroups: AdGroup[] = adGroupsData.ad_groups.map((ag: any) => {
        // Convert type to simple 'display' or 'video'
        let lineItemType: 'display' | 'video' = 'display';
        const typeStr = (ag.type || ag.line_item_type || '').toLowerCase();
        if (typeStr.includes('video') || typeStr.includes('youtube')) {
          lineItemType = 'video';
        }

        return {
          id: ag.id,
          // For published groups from Sense API, ag.id IS the ads_io_id
          // For sense groups, ag.ads_io_id is explicitly set (null for drafts)
          ads_io_id: ag.ads_io_id !== undefined ? ag.ads_io_id : ag.id,
          name: ag.name,
          status: ag.status, // 'active', 'paused', 'ended'
          type: ag.type || 'Display',
          line_item_type: lineItemType,
          creative_count: ag.creative_count || 0,
          creative_ids: ag.creative_ids || [],
          start_date: ag.start_date,
          end_date: ag.end_date,
          budget: ag.budget,
          impressions: ag.impressions,
          clicks: ag.clicks,
          spend: ag.spend,
          bid_strategy: ag.bid_strategy,
          // Carried alongside the strategy so the Pricing selector restores correctly
          // and counts as a change; this list endpoint may hand back either the raw
          // object or an already-parsed string.
          bid_mode: (ag.bid_strategy && typeof ag.bid_strategy === 'object' && ag.bid_strategy.fixedBid)
            ? 'fixed' : (ag.bid_mode || 'target'),
          optimization_goal_ids: ag.optimization_goal_ids || null,
          kpi_type: ag.kpi_type,
          kpi_amount: ag.kpi_amount,
          frequency_cap: ag.frequency_cap,
          targeting: ag.targeting,
          media_plan_type: ag.media_plan_type || 'budget',
          media_plan_target: ag.media_plan_target || null,
          media_plan_daily_paused: ag.media_plan_daily_paused || false,
          source: ag.source || 'dv360',
        };
      });

      // Sort: active first, then paused, then ended
      const statusOrder: Record<string, number> = { active: 0, paused: 1, ended: 2 };
      mappedAdGroups.sort((a, b) => (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3));

      setAdGroups(mappedAdGroups);
      setError(null);

      // Restore selected group from URL if present
      if (groupId) {
        const groupToSelect = mappedAdGroups.find(ag =>
          ag.id?.toString() === groupId || ag.ads_io_id?.toString() === groupId
        );
        if (groupToSelect) {
          setSelectedAdGroup(groupToSelect);
        }
      }
    } catch (err: any) {
      setError(err.message || t('edit.failedLoadCampaign'));
    } finally {
      setLoading(false);
    }
  };

  // Effect to populate form when selectedAdGroup changes (handles URL restore)
  useEffect(() => {
    if (selectedAdGroup) {
      // Only refill when a DIFFERENT group is selected (or on first load).
      if (formFilledForRef.current === selectedAdGroup.id) return;
      formFilledForRef.current = selectedAdGroup.id;
      // Reset saved state when switching ad groups
      setAdGroupSaved(false);
      setSavingAdGroup(false);
      setAdGroupName(cleanName(selectedAdGroup.name));
      setAdGroupLineItemType(selectedAdGroup.line_item_type || 'display');
      // Use real API status for form (scheduled means active in API)
      const realStatus = selectedAdGroup.status === 'scheduled' ? 'active' : selectedAdGroup.status;
      setAdGroupStatus(realStatus as 'active' | 'paused' | 'ended');
      setAdGroupStartDate(selectedAdGroup.start_date || '');
      setAdGroupEndDate(selectedAdGroup.end_date || '');
      setAdGroupBudget(selectedAdGroup.budget_original != null ? selectedAdGroup.budget_original.toString() : (selectedAdGroup.budget ? selectedAdGroup.budget.toString() : ''));
      setAdGroupBidStrategy(selectedAdGroup.bid_strategy || 'AV_VIEWED');
      setAdGroupBidMode(selectedAdGroup.bid_mode === 'fixed' ? 'fixed' : 'target');
      setSelectedGoalIds(selectedAdGroup.optimization_goal_ids || []);
      setAdGroupKpiAmount(selectedAdGroup.kpi_amount_original != null ? selectedAdGroup.kpi_amount_original.toString() : (selectedAdGroup.kpi_amount ? selectedAdGroup.kpi_amount.toString() : ''));
      // Frequency cap
      if (selectedAdGroup.frequency_cap && !selectedAdGroup.frequency_cap.unlimited) {
        setAdGroupFrequencyCount(selectedAdGroup.frequency_cap.max_impressions || '');
        setAdGroupFrequencyPeriod(selectedAdGroup.frequency_cap.time_unit || 'week');
      } else {
        setAdGroupFrequencyCount('');
        setAdGroupFrequencyPeriod('week');
      }
      // Media plan settings
      setAdGroupMediaPlanType(selectedAdGroup.media_plan_type || 'budget');
      setAdGroupPlanTarget(selectedAdGroup.media_plan_target || '');
      // Creative IDs
      const creativeIds = selectedAdGroup.creative_ids || [];
      setAdGroupCreativeIds(creativeIds);
      setOriginalCreativeIds(creativeIds);
      // Reset expanded locations
      setShowAllLocations(false);
      // Load targeting into editable state
      // Age/gender can be in demographic_split (new format) or top-level (legacy)
      const demoSplit = selectedAdGroup.targeting?.demographic_split || {};
      setTargetingAgeRanges(demoSplit.age_ranges || selectedAdGroup.targeting?.age_ranges || []);
      setTargetingGenders(demoSplit.genders || selectedAdGroup.targeting?.genders || []);
      setTargetingParentalStatuses(selectedAdGroup.targeting?.parental_statuses || []);
      setTargetingHouseholdIncome(selectedAdGroup.targeting?.household_income || []);
      setTargetingLanguages(selectedAdGroup.targeting?.languages || []);
      // Categories will be loaded with names from loadTargetingFromDB
      // Don't reset here - let loadTargetingFromDB handle it
      setTargetingDevices(selectedAdGroup.targeting?.devices || []);
      setTargetingDeviceMakes(selectedAdGroup.targeting?.device_makes || []);
      setTargetingOS(selectedAdGroup.targeting?.operating_systems || []);
      setTargetingBrowsers(selectedAdGroup.targeting?.browsers || []);
      setTargetingEnvironment(selectedAdGroup.targeting?.environments || []);
      // Geo - check both 'geo' (local format) and 'geo_included' (API format)
      const geoData = selectedAdGroup.targeting?.geo || selectedAdGroup.targeting?.geo_included || [];
      const geoExcludedData = selectedAdGroup.targeting?.geo_excluded || [];
      // Convert from API format (displayName) to local format (name) if needed
      const normalizeGeo = (items: any[]) => items.map((g: any) => ({
        id: g.id || g.targetingOptionId || '',
        name: g.name || g.displayName || '',
        type: g.type || g.geoRegionType || ''
      }));
      setTargetingGeo(convertToGeoLocations(normalizeGeo(geoData)));
      setTargetingGeoExcluded(convertToGeoLocations(normalizeGeo(geoExcludedData)));
      // Proximity Geo mode - set immediately, proximity list will be loaded in separate useEffect
      const loadedGeoMode = selectedAdGroup.targeting?.geo_mode || 'regions';
      setGeoMode(loadedGeoMode);
      // Reset proximity state - will be populated by separate useEffect when lists load
      if (loadedGeoMode !== 'proximity') {
        setSelectedProximityList(null);
        setEditingProximityList(null);
        setProximityInput('');
        setProximityRadius(10);
      } else {
        // Initialize the radius from the group's stored proximity assignment
        // right now, synchronously with the group load. The proximity-lists
        // fetch resolves later; if we left the radius for that async effect to
        // set, it would overwrite a radius the user had already changed in the
        // meantime (the "radius reverts to old value" bug).
        const storedRadius = selectedAdGroup.targeting?.proximity_lists?.[0]?.radius_km;
        setProximityRadius(typeof storedRadius === 'number' ? storedRadius : 10);
      }
      // Viewability & Brand Safety
      const viewability = selectedAdGroup.targeting?.viewability || [];
      setTargetingViewability(viewability);
      // Auto-expand Viewability section if has targeting
      if (viewability.length > 0) {
        setShowViewabilitySection(true);
      }
      setTargetingOnScreenPosition(selectedAdGroup.targeting?.on_screen_position || []);
      setTargetingContentLabels(selectedAdGroup.targeting?.content_labels_excluded || []);
      setTargetingSensitiveCategories(selectedAdGroup.targeting?.sensitive_categories_excluded || []);
      // Authorized Sellers: empty = 'Any'
      const authSellers = selectedAdGroup.targeting?.authorized_sellers || [];
      setTargetingAuthorizedSellers(authSellers.length > 0 ? authSellers : ['Any']);
      setTargetingVideoPlayerSize(selectedAdGroup.targeting?.video_player_sizes || []);
      // Load saved values or default to all selected
      setTargetingContentInstreamPosition(selectedAdGroup.targeting?.content_instream_position?.length ? selectedAdGroup.targeting.content_instream_position : ALL_INSTREAM);
      setTargetingContentOutstreamPosition(selectedAdGroup.targeting?.content_outstream_position?.length ? selectedAdGroup.targeting.content_outstream_position : ALL_OUTSTREAM);
      setTargetingContentStreamType(selectedAdGroup.targeting?.content_stream_type || []);
      setTargetingUserRewardedContent(selectedAdGroup.targeting?.user_rewarded_content || []);
      setTargetingOmid(selectedAdGroup.targeting?.omid || []);

      // Day & Time - convert from DayTimeSlot[] to Set<string>
      // If no day_and_time targeting exists, it means 24/7 (all cells selected)
      const dayTimeSlots = selectedAdGroup.targeting?.day_and_time || [];
      const dayTimeSet = new Set<string>();
      const allDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

      if (dayTimeSlots.length === 0) {
        // No restrictions = 24/7, select all cells
        allDays.forEach(day => {
          for (let h = 0; h < 24; h++) {
            dayTimeSet.add(`${day}-${h}`);
          }
        });
      } else {
        // Has specific slots, only select those
        dayTimeSlots.forEach((slot: DayTimeSlot) => {
          // Convert dayOfWeek to uppercase (data may be "Monday" or "MONDAY")
          const dayUpper = slot.dayOfWeek.toUpperCase();
          for (let hour = slot.startHour; hour < slot.endHour; hour++) {
            dayTimeSet.add(`${dayUpper}-${hour}`);
          }
        });
      }
      setTargetingDayTime(dayTimeSet);
      // Auto-expand Day & Time section if not 24/7 (has restrictions)
      if (dayTimeSlots.length > 0) {
        setShowDayTimeSection(true);
      }

      setShowGeoInput(false);
      setGeoInputValue('');
      setGeoSearchResults([]);
      setShowGeoExcludedInput(false);
      setGeoExcludedInputValue('');
      setGeoExcludedSearchResults([]);
      setShowLanguageDropdown(false);
      setLanguageSearchValue('');
      setShowCategoryDropdown(false);
      setCategorySearchValue('');

      // Channels and negative keyword lists are loaded in loadTargetingFromDB

      // Reset editors
      setEditingWhiteListChannel(null);
      setWhiteListInput('');
      setEditingBlackListChannel(null);
      setBlackListInput('');
    }
  }, [selectedAdGroup, allChannels]);

  // Load targeting from database when ad group is selected
  useEffect(() => {
    if (!selectedAdGroup?.id) return;

    // Check if this is a draft→published transition (same group, just got ads_io_id)
    const prevGroup = prevAdGroupRef.current;
    const isDraftToPublishedTransition = prevGroup &&
      prevGroup.id === selectedAdGroup.id &&
      !prevGroup.ads_io_id &&
      selectedAdGroup.ads_io_id;

    // Update ref for next comparison
    prevAdGroupRef.current = { id: selectedAdGroup.id, ads_io_id: selectedAdGroup.ads_io_id };

    // If transitioning from draft to published, keep current state (don't reload)
    if (isDraftToPublishedTransition) {
      console.log('[targeting] Draft→Published transition, keeping current state');
      return;
    }

    // Check if switching to a DIFFERENT group
    const isSwitchingGroups = prevGroup && prevGroup.id !== selectedAdGroup.id;

    // Clear all targeting states only when switching to a different group
    if (isSwitchingGroups || !prevGroup) {
      setTargetingCategories([]);
      setAdvancedTargetingItems([]);
      setOriginalAdvancedTargetingItems([]);
      setNegativeKeywordListIds([]);
      setOriginalNegativeKeywordListIds([]);
      setWhiteListChannelIds([]);
      setOriginalWhiteListChannelIds([]);
      setBlackListChannelIds([]);
      setOriginalBlackListChannelIds([]);
    }

    // Check if this is a draft group (no ads_io_id means local only)
    const isDraftGroup = !selectedAdGroup.ads_io_id && selectedAdGroup.id && !String(selectedAdGroup.id).match(/^\d{10,}$/);

    const loadTargetingFromLocalStorage = () => {
      // For draft groups, load from saved targeting object
      const t: any = selectedAdGroup.targeting || {};

      // Categories - use categories_data if available, otherwise empty
      if (t.categories_data && t.categories_data.length > 0) {
        setTargetingCategories(t.categories_data);
      } else {
        setTargetingCategories([]);
      }

      // Negative keyword lists
      const nkl = t.negative_keyword_lists || [];
      setNegativeKeywordListIds(nkl);
      setOriginalNegativeKeywordListIds(nkl);

      // Channels
      const chIncl = t.channels_included || [];
      const chExcl = t.channels_excluded || [];
      setWhiteListChannelIds(chIncl);
      setOriginalWhiteListChannelIds(chIncl);
      setBlackListChannelIds(chExcl);
      setOriginalBlackListChannelIds(chExcl);

      // Auto-expand Inventory section if has data
      if (chIncl.length > 0 || chExcl.length > 0 || nkl.length > 0) {
        setShowInventorySection(true);
      }

      // Languages (stored as names for draft groups)
      if (t.languages && t.languages.length > 0) {
        setTargetingLanguages(t.languages);
      }

      // Advanced targeting (audiences)
      const audiences = t.advanced_targeting || [];
      setAdvancedTargetingItems(audiences);
      setOriginalAdvancedTargetingItems(audiences);
      if (audiences.length > 0) {
        setShowAdvancedTargetingSection(true);
      }
    };

    const loadTargetingFromDB = async () => {
      try {
        const response = await api.get(`/api/targeting/line-item/${selectedAdGroup.id}`);
        const data = response.data;

        // Load languages (convert to string array of names for compatibility)
        if (data.languages && data.languages.length > 0) {
          setTargetingLanguages(data.languages.map((l: { id: string; name: string }) => l.name));
        }

        // Load categories (already in correct format: {id, name})
        if (data.categories && data.categories.length > 0) {
          setTargetingCategories(data.categories);
        } else {
          setTargetingCategories([]);
        }

        // Load negative keyword lists (array of IDs)
        if (data.negative_keyword_lists && data.negative_keyword_lists.length > 0) {
          setNegativeKeywordListIds(data.negative_keyword_lists);
          setOriginalNegativeKeywordListIds(data.negative_keyword_lists);
        } else {
          setNegativeKeywordListIds([]);
          setOriginalNegativeKeywordListIds([]);
        }

        // Load channels (array of IDs)
        if (data.channels_included && data.channels_included.length > 0) {
          setWhiteListChannelIds(data.channels_included);
          setOriginalWhiteListChannelIds(data.channels_included);
        } else {
          setWhiteListChannelIds([]);
          setOriginalWhiteListChannelIds([]);
        }
        if (data.channels_excluded && data.channels_excluded.length > 0) {
          setBlackListChannelIds(data.channels_excluded);
          setOriginalBlackListChannelIds(data.channels_excluded);
        } else {
          setBlackListChannelIds([]);
          setOriginalBlackListChannelIds([]);
        }

        // Auto-expand Inventory section if has channels or keyword lists
        const hasInventoryTargeting =
          (data.channels_included && data.channels_included.length > 0) ||
          (data.channels_excluded && data.channels_excluded.length > 0) ||
          (data.negative_keyword_lists && data.negative_keyword_lists.length > 0);
        if (hasInventoryTargeting) {
          setShowInventorySection(true);
        }

        // Load remarketing audiences from API
        if (data.remarketing_audiences) {
          const config = data.remarketing_audiences as RemarketingAudiencesConfig;
          setRemarketingIncludeIds(config.include?.ids || []);
          setRemarketingIncludeOperator(config.include?.operator || 'OR');
          setRemarketingExcludeIds(config.exclude?.ids || []);
          setOriginalRemarketingConfig(config);
          // Auto-expand section if there are remarketing audiences
          if ((config.include?.ids?.length || 0) > 0 || (config.exclude?.ids?.length || 0) > 0) {
            setShowRemarketingSection(true);
          }
        } else {
          setRemarketingIncludeIds([]);
          setRemarketingIncludeOperator('OR');
          setRemarketingExcludeIds([]);
          setOriginalRemarketingConfig(null);
        }

        // Load Look-Alike (Optimized Targeting) from targeting_expansion
        if (data.targeting_expansion && data.targeting_expansion.enableOptimizedTargeting !== undefined) {
          setEnableLookAlike(data.targeting_expansion.enableOptimizedTargeting);
          setOriginalEnableLookAlike(data.targeting_expansion.enableOptimizedTargeting);
        } else {
          setEnableLookAlike(false);
          setOriginalEnableLookAlike(false);
        }
      } catch (err) {
        console.error('Failed to load targeting from DB:', err);
      }
    };

    // Load remarketing config from targeting
    const loadRemarketingConfig = () => {
      const targeting = selectedAdGroup?.targeting;
      if (targeting?.remarketing_audiences) {
        const config = targeting.remarketing_audiences;
        setRemarketingIncludeIds(config.include?.ids || []);
        setRemarketingIncludeOperator(config.include?.operator || 'OR');
        setRemarketingExcludeIds(config.exclude?.ids || []);
        setOriginalRemarketingConfig(config);
        // Auto-expand section if there are remarketing audiences
        if ((config.include?.ids?.length || 0) > 0 || (config.exclude?.ids?.length || 0) > 0) {
          setShowRemarketingSection(true);
        }
      } else {
        setRemarketingIncludeIds([]);
        setRemarketingIncludeOperator('OR');
        setRemarketingExcludeIds([]);
        setOriginalRemarketingConfig(null);
      }
    };

    const loadAudiences = async () => {
      try {
        console.log('[loadAudiences] Loading for io_id:', selectedAdGroup.id);
        const response = await api.get(`/api/insertion-orders/${selectedAdGroup.id}/audiences`);
        const data = response.data;
        console.log('[loadAudiences] Response:', data);

        if (data.audiences && data.audiences.length > 0) {
          // Convert to AdvancedTargetingItem format
          const items: AdvancedTargetingItem[] = data.audiences
            .filter((a: any) => a.mapping_id) // Only include items with mapping
            .map((a: any) => ({
              id: a.mapping_id,
              name: a.name,
              full_path: a.full_path || '',
              google_audience_id: a.google_audience_id,
              google_audience_type: a.google_audience_type || null
            }));
          console.log('[loadAudiences] Setting items:', items);
          setAdvancedTargetingItems(items);
          setOriginalAdvancedTargetingItems(items);
          // Auto-expand section if there are audiences
          if (items.length > 0) {
            setShowAdvancedTargetingSection(true);
          }
        } else {
          setAdvancedTargetingItems([]);
          setOriginalAdvancedTargetingItems([]);
        }
      } catch (err) {
        console.error('Failed to load audiences:', err);
        setAdvancedTargetingItems([]);
        setOriginalAdvancedTargetingItems([]);
      }
    };

    if (isDraftGroup) {
      // For draft groups, load from local targeting object
      loadTargetingFromLocalStorage();
      loadRemarketingConfig();
    } else {
      // For published groups, load from API
      loadTargetingFromDB();
      loadAudiences();
      loadRemarketingConfig();
    }
  }, [selectedAdGroup?.id, selectedAdGroup?.targeting, selectedAdGroup?.ads_io_id, targetingReloadKey]);

  // Load available remarketing lists when advertiser is known (separate from targeting)
  useEffect(() => {
    const loadRemarketingLists = async () => {
      if (!campaign?.advertiser_id) return;
      try {
        const response = await api.get(`/api/remarketing-lists/for-targeting/${campaign.advertiser_id}`);
        setAvailableRemarketingLists(response.data || []);
      } catch (err) {
        console.error('Failed to load remarketing lists:', err);
        setAvailableRemarketingLists([]);
      }
    };
    loadRemarketingLists();
  }, [campaign?.advertiser_id]);

  const handleAdGroupSelect = (adGroup: AdGroup) => {
    // Block selection if targeting push is in progress
    if (adGroup.targeting_push_pending) {
      return;
    }
    setSelectedAdGroup(adGroup);
    const pending = adGroup.targeting_push_pending ?? false;
    setTargetingPushPending(pending);  // Sync separate state
    targetingPushPendingRef.current = pending;
    // Save selected group ID to URL (replace to avoid history spam)
    navigate(`/campaigns/${campaignId}/edit/${adGroup.id}`, { replace: true });
  };

  const handleCloseAdGroup = () => {
    setSelectedAdGroup(null);
    setTargetingPushPending(false);  // Clear pending state
    targetingPushPendingRef.current = false;
    // Clear advanced targeting items
    setAdvancedTargetingItems([]);
    setOriginalAdvancedTargetingItems([]);
    // Clear remarketing audiences
    setRemarketingIncludeIds([]);
    setRemarketingIncludeOperator('OR');
    setRemarketingExcludeIds([]);
    setOriginalRemarketingConfig(null);
    // Clear Look-Alike
    setEnableLookAlike(false);
    setOriginalEnableLookAlike(false);
    // Reset collapsible sections
    setShowAdvancedTargetingSection(false);
    setShowRemarketingSection(false);
    setShowViewabilitySection(false);
    setShowInventorySection(false);
    setShowDayTimeSection(false);
    // Remove group ID from URL
    navigate(`/campaigns/${campaignId}/edit`, { replace: true });
  };

  // Handle placement type change (Display ↔ Video)
  const handlePlacementTypeChange = (newType: 'display' | 'video') => {
    if (newType === adGroupLineItemType) return; // No change
    if (!isDraft && selectedAdGroup && selectedAdGroup.id !== '') return; // Can't change published groups

    setAdGroupLineItemType(newType);

    // Reset creatives when switching type (Display and Video use different creative types)
    setAdGroupCreativeIds([]);

    if (newType === 'display') {
      // Switching to Display - reset Video-specific settings
      setTargetingVideoPlayerSize([]);
      setTargetingContentInstreamPosition(ALL_INSTREAM);
      setTargetingContentOutstreamPosition(ALL_OUTSTREAM);
      setTargetingContentStreamType([]);
      setTargetingUserRewardedContent([]);
      // Reset bid strategy to default if CIVA was selected (Video-only strategy)
      if (adGroupBidStrategy === 'CIVA') {
        setAdGroupBidStrategy('AV_VIEWED');
      }
    } else {
      // Switching to Video - reset Display-specific settings
      setTargetingOnScreenPosition([]);
    }
  };

  // State for creating new groups
  const handleAddGroup = () => {
    // Create a new empty ad group with default values
    const today = getTodayDate();
    // Start date: use today, or campaign start date if it's in the future
    const defaultStartDate = campaign?.start_date && campaign.start_date > today ? campaign.start_date : today;
    // End date: use campaign end date if available and valid, otherwise end of month
    const defaultEndDate = campaign?.end_date && campaign.end_date >= defaultStartDate ? campaign.end_date : getEndOfMonthDate();

    // If campaign is paused, default to paused status for new groups
    const defaultStatus = campaignStatus === 'paused' ? 'paused' : 'active';

    const newGroup: AdGroup = {
      id: '', // Empty id means it's a new group
      name: '',
      status: defaultStatus,
      type: 'Display',
      line_item_type: 'display',
      creative_count: 0,
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      budget: undefined,
      impressions: 0,
      clicks: 0,
      spend: 0,
      bid_strategy: 'AV_VIEWED',
      kpi_type: null,
      kpi_amount: null,
      frequency_cap: null,
      targeting: {
        geo: [],
        geo_excluded: [],
        geo_mode: 'regions',
        devices: [],
        device_makes: [],
        operating_systems: [],
        browsers: [],
        environments: [],
        age_ranges: [],
        genders: [],
        parental_statuses: [],
        household_income: [],
        languages: [],
        categories: [],
        viewability: [],
        on_screen_position: [],
        content_labels_excluded: [],
        sensitive_categories_excluded: [],
        authorized_sellers: [],
        video_player_sizes: [],
        content_instream_position: ['Pre Roll', 'Mid Roll', 'Post Roll'],
        content_outstream_position: ['In Article', 'In Banner', 'In Feed', 'Interstitial'],
        content_stream_type: [],
        user_rewarded_content: [],
        omid: [],
        day_and_time: [],
        proximity_lists: []
      },
      daily_max_impressions: null
    };
    setSelectedAdGroup(newGroup);
    // Reset all targeting states for new group
    setAdvancedTargetingItems([]);
    setOriginalAdvancedTargetingItems([]);
    setTargetingCategories([]);
    setNegativeKeywordListIds([]);
    setOriginalNegativeKeywordListIds([]);
    setWhiteListChannelIds([]);
    setOriginalWhiteListChannelIds([]);
    setBlackListChannelIds([]);
    setOriginalBlackListChannelIds([]);
    // Reset remarketing audiences
    setRemarketingIncludeIds([]);
    setRemarketingExcludeIds([]);
    setRemarketingIncludeOperator('OR');
    setOriginalRemarketingConfig(null);
    // Reset Look-Alike
    setEnableLookAlike(false);
    setOriginalEnableLookAlike(false);
    // Collapse sections (will auto-expand when data is loaded)
    setShowAdvancedTargetingSection(false);
    setShowViewabilitySection(false);
    setShowInventorySection(false);
  };

  const getAdGroupStatus = (adGroup: AdGroup): 'active' | 'paused' | 'ended' | 'scheduled' => {
    // Check end_date first - compare dates only (not time)
    if (adGroup.end_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endDate = new Date(adGroup.end_date);
      endDate.setHours(0, 0, 0, 0);
      if (endDate < today) {
        return 'ended';
      }
    }
    // Check if scheduled (start_date in the future)
    if (adGroup.status === 'scheduled') return 'scheduled';
    if (adGroup.status === 'active') return 'active';
    if (adGroup.status === 'paused') return 'paused';
    return 'ended';
  };

  const isAdGroupActive = (adGroup: AdGroup) => {
    return getAdGroupStatus(adGroup) === 'active';
  };

  // Geo search with debounce
  useEffect(() => {
    if (!geoInputValue || geoInputValue.length < 2 || !campaign?.advertiser_id) {
      setGeoSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setGeoSearchLoading(true);
      try {
        const response = await api.get(`/api/targeting/geo/search`, {
          params: {
            q: geoInputValue,
            advertiser_id: campaign.advertiser_id,
            limit: 10
          }
        });
        setGeoSearchResults(response.data.results || []);
      } catch (err) {
        console.error('Geo search error:', err);
        setGeoSearchResults([]);
      } finally {
        setGeoSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [geoInputValue, campaign?.advertiser_id]);

  const handleSelectGeoResult = (result: GeoSearchResult) => {
    // Check if already exists by name
    if (!targetingGeo.some(loc => loc.name === result.name)) {
      const newLocation: GeoLocation = {
        id: result.id,
        name: result.name,
        type: result.type
      };
      setTargetingGeo([...targetingGeo, newLocation]);
      // Remove from Excluded if exists (can't be in both lists)
      setTargetingGeoExcluded(targetingGeoExcluded.filter(loc => loc.name !== result.name));
    }
    setGeoInputValue('');
    setGeoSearchResults([]);
    setShowGeoInput(false);
  };

  // Geo excluded search with debounce
  useEffect(() => {
    if (!geoExcludedInputValue || geoExcludedInputValue.length < 2 || !campaign?.advertiser_id) {
      setGeoExcludedSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setGeoExcludedSearchLoading(true);
      try {
        const response = await api.get(`/api/targeting/geo/search`, {
          params: {
            q: geoExcludedInputValue,
            advertiser_id: campaign.advertiser_id,
            limit: 10
          }
        });
        setGeoExcludedSearchResults(response.data.results || []);
      } catch (err) {
        console.error('Geo excluded search error:', err);
        setGeoExcludedSearchResults([]);
      } finally {
        setGeoExcludedSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [geoExcludedInputValue, campaign?.advertiser_id]);

  // Fetch channels, negative keyword lists, and proximity lists when advertiser_id is available
  useEffect(() => {
    if (campaign?.advertiser_id) {
      fetchChannels(campaign.advertiser_id);
      fetchNegativeKeywordLists(campaign.advertiser_id);
      fetchProximityLists(campaign.advertiser_id);
    }
  }, [campaign?.advertiser_id]);

  // The campaign carries the advertiser when one is being edited; the state
  // above only holds it while a campaign is being created.
  const templateAdvertiserId = campaign?.advertiser_id || selectedAdvertiserId || '';

  const loadTargetingTemplates = async (advertiserId: string) => {
    if (!advertiserId) return;
    try {
      const response = await api.get('/api/sense/group-templates', {
        params: { advertiser_id: advertiserId },
      });
      setTargetingTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error loading targeting templates:', err);
      setTargetingTemplates([]);
    }
  };

  const handleSaveTargetingTemplate = async () => {
    if (!selectedAdGroup?.id) return;
    const name = window.prompt(t('edit.templateName'), selectedAdGroup.name || '');
    if (!name || !name.trim()) return;
    setTemplateBusy(true);
    try {
      await api.post(`/api/sense/groups/${selectedAdGroup.id}/save-as-template`, { name: name.trim() });
      await loadTargetingTemplates(templateAdvertiserId);
      setMessageModalText(t('edit.savedAsTemplate', { name: name.trim() }));
      setMessageModalType('info');
      setShowMessageModal(true);
    } catch (error: any) {
      setMessageModalText(extractErrorMessage(error, t('edit.couldNotSaveTemplate')));
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleApplyTargetingTemplate = async (templateId: number) => {
    if (!selectedAdGroup?.id) return;
    setShowTemplateMenu(false);
    setTemplateBusy(true);
    try {
      const applied = await api.post(`/api/sense/groups/${selectedAdGroup.id}/apply-template/${templateId}`);
      // The template replaces the whole targeting, so the form is re-read from
      // the database rather than patched field by field — anything left over
      // from the previous setup would be a mix nobody chose.
      await fetchCampaignData();
      setTargetingReloadKey((k) => k + 1);
      // A template captured on another client of the same agency cannot bring
      // its site lists, keyword lists, proximity lists or audiences — those are
      // ids in that account. Say which, rather than let it look complete.
      const dropped: string[] = applied.data?.dropped || [];
      const LABEL_KEYS: Record<string, string> = {
        channels_included: 'edit.droppedWhitelists',
        channels_excluded: 'edit.droppedBlacklists',
        negative_keyword_lists: 'edit.droppedNegKwLists',
        proximity_lists: 'edit.droppedProximityLists',
        remarketing_audiences: 'edit.droppedRemarketing',
      };
      setMessageModalText(
        dropped.length
          ? t('edit.templateAppliedDropped', {
            items: dropped.map((k) => (LABEL_KEYS[k] ? t(LABEL_KEYS[k]) : k)).join(', '),
          })
          : t('edit.templateApplied')
      );
      setMessageModalType('info');
      setShowMessageModal(true);
    } catch (error: any) {
      setMessageModalText(extractErrorMessage(error, t('edit.couldNotApplyTemplate')));
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleDeleteTargetingTemplate = async (templateId: number) => {
    setTemplateBusy(true);
    try {
      await api.delete(`/api/sense/group-templates/${templateId}`);
      await loadTargetingTemplates(templateAdvertiserId);
    } catch (error: any) {
      setMessageModalText(extractErrorMessage(error, t('edit.couldNotDeleteTemplate')));
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setTemplateBusy(false);
    }
  };

  const fetchChannels = async (advertiserId: string) => {
    setChannelsLoading(true);
    try {
      const response = await api.get(`/api/reports/channels/${advertiserId}`);
      setAllChannels(response.data.white_list || []);
    } catch (err) {
      console.error('Failed to fetch channels:', err);
    } finally {
      setChannelsLoading(false);
    }
  };

  // Ensure the RU service blacklist channel (RU_BL_AutoUpdated) exists for this
  // advertiser and is loaded into the channel list, so it can be defaulted into new
  // groups. Runs for any advertiser when the editor opens — the endpoint returns null
  // for non-UA advertisers, so it's a no-op there.
  useEffect(() => {
    const adv = campaign?.advertiser_id;
    if (!adv || adv === '__all__') return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/api/channels/ru-bl?advertiser_id=${adv}`);
        if (cancelled || !r.data?.channel_id) return;
        await fetchChannels(adv);  // make sure the channel is in the list
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.advertiser_id]);

  // Pre-select RU_BL_AutoUpdated in the excluded (black) channel list for a NEW
  // (unpublished/draft) group. Groups are created inside an already-saved campaign,
  // so this is keyed on the group being unpublished — NOT on campaign create-mode.
  // Published groups keep their own channels_excluded; re-applies on channel-list/
  // group changes so a new-group targeting reset doesn't drop it.
  useEffect(() => {
    if (!selectedAdGroup || selectedAdGroup.ads_io_id) return;
    const ch = allChannels.find(c => c.name === 'RU_BL_AutoUpdated');
    if (!ch) return;
    setBlackListChannelIds(prev => (prev.includes(ch.channel_id) ? prev : [...prev, ch.channel_id]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChannels, selectedAdGroup?.id, selectedAdGroup?.ads_io_id]);

  const fetchChannelUrls = async (channelId: string): Promise<string[]> => {
    if (!campaign?.advertiser_id) return [];
    try {
      const response = await api.get(`/api/reports/channels/${campaign.advertiser_id}/${channelId}/sites`);
      return response.data.sites || [];
    } catch (err) {
      console.error('Failed to fetch channel URLs:', err);
      return [];
    }
  };

  const fetchNegativeKeywordLists = async (advertiserId: string) => {
    try {
      const response = await api.get(`/api/reports/negative-keyword-lists/${advertiserId}`);
      setAllNegativeKeywordLists(response.data.lists || []);
    } catch (err) {
      console.error('Failed to fetch negative keyword lists:', err);
    }
  };

  // Fetch proximity location lists
  const fetchProximityLists = async (advertiserId: string) => {
    setProximityListsLoading(true);
    try {
      const response = await api.get(`/api/proximity-lists?advertiser_id=${advertiserId}`);
      const lists = response.data.lists || [];
      // Transform to frontend format
      const formattedLists: ProximityLocationList[] = lists.map((list: any) => ({
        id: list.id,
        name: list.display_name,
        locations: [],  // Locations are loaded when editing
        locationCount: list.location_count || 0,  // Count from API for display
        radius: 10,  // Default radius (stored separately per targeting assignment)
      }));
      setProximityLists(formattedLists);
    } catch (err) {
      console.error('Failed to fetch proximity lists:', err);
    } finally {
      setProximityListsLoading(false);
    }
  };

  // Load full proximity list with locations
  const loadProximityListLocations = async (listId: string): Promise<ProximityLocation[]> => {
    if (!campaign?.advertiser_id) return [];
    try {
      const response = await api.get(`/api/proximity-lists/${listId}/locations?advertiser_id=${campaign.advertiser_id}`);
      return (response.data.locations || []).map((loc: any) => ({
        lat: loc.lat,
        lng: loc.lng,
        address: loc.address
      }));
    } catch (err) {
      console.error('Failed to load proximity list locations:', err);
      return [];
    }
  };

  // Initialize proximity list selection when lists are loaded and ad group has saved proximity targeting
  useEffect(() => {
    const savedProximity = selectedAdGroup?.targeting?.proximity_lists;
    const savedGeoMode = selectedAdGroup?.targeting?.geo_mode;

    if (savedGeoMode === 'proximity' && savedProximity && savedProximity.length > 0 && proximityLists.length > 0) {
      const firstSavedProximity = savedProximity[0];
      const listId = String(firstSavedProximity.proximity_list_id);

      // Find the list in loaded proximity lists
      const foundList = proximityLists.find(l => String(l.id) === listId);

      if (foundList && (!selectedProximityList || String(selectedProximityList.id) !== listId)) {
        // Load locations for this list
        loadProximityListLocations(listId).then(locations => {
          const updatedList = { ...foundList, locations };
          setSelectedProximityList(updatedList);
          setEditingProximityList(updatedList);
          setProximityInput(locations.map(l => `${l.lat}, ${l.lng}`).join('\n'));
          // NOTE: do NOT set proximityRadius here. This async effect resolves
          // after the user may have already edited the radius; the radius is
          // initialized synchronously on group load instead. Re-setting it here
          // would clobber the user's change and revert it to the stored value.
          // Update list in state with loaded locations
          setProximityLists(prev => prev.map(l => l.id === listId ? updatedList : l));
        });
      }
    }
  }, [selectedAdGroup?.id, selectedAdGroup?.targeting?.geo_mode, proximityLists.length]);

  // Resolve the proximity list that should be saved with the group.
  // Prefer the actively selected/edited list, but fall back to the list already
  // assigned to the group's targeting. Without this fallback the save silently
  // drops proximity_lists whenever the chip isn't actively selected (e.g. the
  // list belongs to a group that loaded without auto-selecting it), so a radius
  // change never reaches the backend and the old radius sticks.
  const resolveActiveProximityList = (): { id: number | string } | null => {
    if (selectedProximityList) return selectedProximityList;
    if (editingProximityList) return editingProximityList;
    const assignedId = selectedAdGroup?.targeting?.proximity_lists?.[0]?.proximity_list_id;
    if (assignedId === undefined || assignedId === null) return null;
    return proximityLists.find(l => String(l.id) === String(assignedId)) || { id: assignedId };
  };

  // Create proximity list via API (creates in Sense immediately)
  const createProximityList = async (name: string, locations: ProximityLocation[], _radius: number): Promise<ProximityLocationList | null> => {
    if (!campaign?.advertiser_id) return null;
    setSavingProximityList(true);
    try {
      const response = await api.post(`/api/proximity-lists/create`, {
        advertiser_id: campaign.advertiser_id,
        display_name: name,
        locations: locations.map(loc => ({
          lat: loc.lat,
          lng: loc.lng,
          address: loc.address
        }))
      });
      if (response.data.success) {
        const newList: ProximityLocationList = {
          id: response.data.id,
          name: response.data.display_name,
          locations: locations,
          radius: 10,  // Default radius
        };
        setProximityLists([...proximityLists, newList]);
        return newList;
      }
      return null;
    } catch (err) {
      console.error('Failed to create proximity list:', err);
      return null;
    } finally {
      setSavingProximityList(false);
    }
  };

  // Update proximity list locations via API
  const updateProximityListLocations = async (listId: string, locations: ProximityLocation[]): Promise<boolean> => {
    if (!campaign?.advertiser_id) return false;
    setSavingProximityList(true);
    try {
      const response = await api.put(
        `/api/proximity-lists/${listId}/locations?advertiser_id=${campaign.advertiser_id}`,
        {
          locations: locations.map(loc => ({
            lat: loc.lat,
            lng: loc.lng,
            address: loc.address
          }))
        }
      );
      if (response.data.success) {
        // Update local state
        setProximityLists(proximityLists.map(l =>
          l.id === listId ? { ...l, locations } : l
        ));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to update proximity list:', err);
      return false;
    } finally {
      setSavingProximityList(false);
    }
  };

  // Rename proximity list via API
  const handleRenameProximityList = async () => {
    if (!editingProximityList || !campaign?.advertiser_id || !editingProximityListName.trim()) {
      setIsEditingProximityListName(false);
      return;
    }
    if (editingProximityListName === editingProximityList.name) {
      setIsEditingProximityListName(false);
      return;
    }
    setIsRenamingProximityList(true);
    try {
      const response = await api.patch(
        `/api/proximity-lists/${editingProximityList.id}?advertiser_id=${campaign.advertiser_id}`,
        { display_name: editingProximityListName.trim() }
      );
      if (response.data.success) {
        const newName = editingProximityListName.trim();
        setProximityLists(proximityLists.map(l =>
          l.id === editingProximityList.id ? { ...l, name: newName } : l
        ));
        setEditingProximityList({ ...editingProximityList, name: newName });
      }
    } catch (err) {
      console.error('Failed to rename proximity list:', err);
      setEditingProximityListName(editingProximityList.name);
    } finally {
      setIsRenamingProximityList(false);
      setIsEditingProximityListName(false);
    }
  };

  // Delete proximity list handler
  const handleDeleteProximityList = async () => {
    if (!editingProximityList || !campaign?.advertiser_id) return;
    setDeletingProximityList(true);
    try {
      const success = await deleteProximityList(editingProximityList.id);
      if (success) {
        setEditingProximityList(null);
        setProximityInput('');
        setShowDeleteProximityListConfirm(false);
      }
    } finally {
      setDeletingProximityList(false);
    }
  };

  // Delete proximity list via API
  const deleteProximityList = async (listId: string): Promise<boolean> => {
    if (!campaign?.advertiser_id) return false;
    try {
      const response = await api.delete(
        `/api/proximity-lists/${listId}?advertiser_id=${campaign.advertiser_id}`
      );
      if (response.data.success) {
        setProximityLists(proximityLists.filter(l => l.id !== listId));
        if (editingProximityList?.id === listId) {
          setEditingProximityList(null);
          setProximityInput('');
        }
        if (selectedProximityList?.id === listId) {
          setSelectedProximityList(null);
        }
        // Update selectedAdGroup to reflect deletion (backend already removed from DB)
        if (selectedAdGroup?.targeting?.proximity_lists) {
          const updatedProximityLists = selectedAdGroup.targeting.proximity_lists.filter(
            p => String(p.proximity_list_id) !== String(listId)
          );
          const updatedTargeting = {
            ...selectedAdGroup.targeting,
            proximity_lists: updatedProximityLists.length > 0 ? updatedProximityLists : undefined,
            geo_mode: (updatedProximityLists.length > 0 ? 'proximity' : 'regions') as 'regions' | 'proximity'
          };
          const updatedAdGroup = { ...selectedAdGroup, targeting: updatedTargeting };
          setSelectedAdGroup(updatedAdGroup);
          setAdGroups(adGroups.map(ag => ag.id === selectedAdGroup.id ? updatedAdGroup : ag));
          // Reset geo mode if no proximity lists left
          if (updatedProximityLists.length === 0) {
            setGeoMode('regions');
          }
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to delete proximity list:', err);
      return false;
    }
  };

  // White List handlers
  const handleSelectWhiteListChannel = async (channel: Channel) => {
    // Toggle: if same channel is already open, close it
    if (editingWhiteListChannel?.channel_id === channel.channel_id) {
      setEditingWhiteListChannel(null);
      setWhiteListInput('');
      setOriginalWhiteListInput('');
      setEditingWhiteListChannelName('');
      setIsEditingWhiteListChannelName(false);
      return;
    }
    setEditingWhiteListChannel(channel);
    setEditingWhiteListChannelName(channel.name);
    setIsEditingWhiteListChannelName(false);
    setIsCreatingWhiteList(false);
    const urls = await fetchChannelUrls(channel.channel_id);
    const urlsText = urls.join('\n');
    setWhiteListInput(urlsText);
    setOriginalWhiteListInput(urlsText);
    setShowWhiteListSaveModal(false);
    setNewWhiteListName('');
  };

  const handleNewWhiteList = () => {
    setEditingWhiteListChannel(null);
    setWhiteListInput('');
    setOriginalWhiteListInput('');
    setShowWhiteListSaveModal(false);
    setNewWhiteListName('');
    setEditingWhiteListChannelName('');
    setIsCreatingWhiteList(true);
  };

  const handleRemoveWhiteListChannel = (channelId: string) => {
    setWhiteListChannelIds(whiteListChannelIds.filter(id => id !== channelId));
    if (editingWhiteListChannel?.channel_id === channelId) {
      setEditingWhiteListChannel(null);
      setWhiteListInput('');
    }
  };

  // Save Campaign settings to API
  const handleSaveCampaign = async () => {
    if (savingCampaign) return;

    // Create mode - validate required fields
    if (isCreateMode) {
      if (!campaignName.trim()) {
        setMessageModalText(t('edit.campaignNameRequired'));
        setMessageModalType('error');
        setShowMessageModal(true);
        return;
      }
      if (!defaultAdvertiserId) {
        setMessageModalText(t('edit.noAdvertiserSelected'));
        setMessageModalType('error');
        setShowMessageModal(true);
        return;
      }
    } else if (!campaignId) {
      return;
    }

    setSavingCampaign(true);
    try {
      const hasFrequencyLimit = frequencyCount !== '' && Number(frequencyCount) > 0;

      if (isCreateMode) {
        // CREATE MODE - POST to create new campaign
        const createBody: any = {
          advertiser_id: defaultAdvertiserId,
          display_name: campaignName.trim(),
          entity_status: campaignStatus === 'active' ? 'ENTITY_STATUS_ACTIVE' :
                         campaignStatus === 'paused' ? 'ENTITY_STATUS_PAUSED' : 'ENTITY_STATUS_DRAFT'
        };

        if (startDate && startDate.length >= 10) {
          createBody.start_date = startDate;
        }
        if (endDate && endDate.length >= 10) {
          createBody.end_date = endDate;
        }
        if (hasFrequencyLimit) {
          createBody.frequency_cap = {
            unlimited: false,
            maxImpressions: Number(frequencyCount),
            timeUnit: mapUIToTimeUnit(frequencyPeriod),
            timeUnitCount: 1
          };
        }

        const response = await api.post(
          `/api/sense/campaigns`,
          createBody
        );

        if (response.data.id) {
          // Redirect to edit mode with new campaign ID
          navigate(`/campaigns/${response.data.id}/edit`, { replace: true });
        } else {
          throw new Error(t('edit.failedCreateCampaignNoId'));
        }
      } else {
        // EDIT MODE - PATCH to update existing campaign
        const requestBody: any = {};

        // Check if name changed
        if (campaign && campaignName !== campaign.name) {
          requestBody.display_name = campaignName;
        }

        // Check if status changed (only active/paused can be changed)
        if (campaign && campaignStatus !== 'ended') {
          const originalStatus = campaign.entity_status === 'ENTITY_STATUS_ACTIVE' ? 'active' :
                                 campaign.entity_status === 'ENTITY_STATUS_PAUSED' ? 'paused' : 'ended';
          if (campaignStatus !== originalStatus) {
            // Send full enum value for API
            requestBody.entity_status = campaignStatus === 'active' ? 'ENTITY_STATUS_ACTIVE' :
                                        campaignStatus === 'paused' ? 'ENTITY_STATUS_PAUSED' : campaign.entity_status;
          }
        }

        // Check dates - only send if valid non-empty date string
        // Start date can only be changed if campaign hasn't actually started (published AND past start date)
        const todayStr = new Date().toISOString().split('T')[0];
        const campaignIsPublished = !!campaign?.ads_campaign_id;
        const campaignHasStarted = campaignIsPublished && campaign?.start_date && campaign.start_date < todayStr;
        if (campaign && startDate && startDate.length >= 10 && startDate !== campaign.start_date && !campaignHasStarted) {
          requestBody.start_date = startDate;
        }
        if (campaign && endDate && endDate.length >= 10 && endDate !== campaign.end_date) {
          requestBody.end_date = endDate;
        }

        // Check frequency cap (API returns camelCase: maxImpressions, timeUnit)
        const originalFreqCap = campaign?.frequency_cap;
        const originalUnlimited = originalFreqCap?.unlimited ?? true;

        if (hasFrequencyLimit !== !originalUnlimited ||
            (hasFrequencyLimit && (
              Number(frequencyCount) !== originalFreqCap?.maxImpressions ||
              frequencyPeriod !== mapTimeUnitToUI(originalFreqCap?.timeUnit)
            ))) {
          if (hasFrequencyLimit) {
            requestBody.frequency_cap = {
              unlimited: false,
              maxImpressions: Number(frequencyCount),
              timeUnit: mapUIToTimeUnit(frequencyPeriod),
              timeUnitCount: 1
            };
          } else {
            requestBody.frequency_cap = { unlimited: true };
          }
        }

        // If nothing changed, just return (button should be disabled anyway)
        if (Object.keys(requestBody).length === 0) {
          setSavingCampaign(false);
          return;
        }

        // Always use sense API for local campaigns (campaignId is internal ID)
        const apiEndpoint = `/api/sense/campaigns/${campaignId}`;

        // Same as the ad group: show the new values now. The campaign push runs
        // in the background, so waiting for the response would keep the old name
        // and dates on screen for the whole publish.
        const previousCampaign = campaign;
        setCampaign(prev => prev ? {
          ...prev,
          name: campaignName,
          entity_status: campaignStatus === 'active' ? 'ENTITY_STATUS_ACTIVE' :
                        campaignStatus === 'paused' ? 'ENTITY_STATUS_PAUSED' : prev.entity_status,
          start_date: startDate,
          end_date: endDate,
        } : prev);

        let response;
        try {
          response = await api.patch(apiEndpoint, requestBody);
        } catch (err) {
          setCampaign(previousCampaign);
          throw err;
        }

        if (response.data.success || response.data.message) {
          // Update local campaign state
          setCampaign(prev => prev ? {
            ...prev,
            name: campaignName,
            entity_status: campaignStatus === 'active' ? 'ENTITY_STATUS_ACTIVE' :
                          campaignStatus === 'paused' ? 'ENTITY_STATUS_PAUSED' : prev.entity_status,
            start_date: startDate,
            end_date: endDate,
            frequency_cap: hasFrequencyLimit ? {
              unlimited: false,
              maxImpressions: Number(frequencyCount),
              timeUnit: mapUIToTimeUnit(frequencyPeriod),
              timeUnitCount: 1
            } : { unlimited: true }
          } : null);

          // Show success state on button (no modal)
          setCampaignSaved(true);
          setTimeout(() => setCampaignSaved(false), 2500);
        } else {
          throw new Error(response.data.message || t('edit.failedUpdateCampaign'));
        }
      }
    } catch (error: any) {
      console.error('Error saving campaign:', error);
      const errorMessage = extractErrorMessage(error, t('edit.failedSaveCampaign'));
      setMessageModalText(errorMessage);
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setSavingCampaign(false);
    }
  };

  // Publish campaign to remote (Sense)
  const handlePublishCampaign = async () => {
    if (!campaignId || isPublishing) return;

    setIsPublishing(true);
    try {
      const response = await api.post(
        `/api/sense/campaigns/${campaignId}/push`
      );

      if (response.data.success) {
        setIsDraft(false);
        setPublishSuccess(true);
        setTimeout(() => setPublishSuccess(false), 2500);
        // Update campaign object with new ads_campaign_id
        if (campaign && response.data.ads_campaign_id) {
          setCampaign({ ...campaign, ads_campaign_id: response.data.ads_campaign_id });
        }
        // No modal - button shows success state
      } else {
        throw new Error(response.data.message || t('edit.failedPublishCampaign'));
      }
    } catch (error: any) {
      console.error('Error publishing campaign:', error);
      const errorMessage = extractErrorMessage(error, t('edit.failedPublishCampaign'));
      setMessageModalText(errorMessage);
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!campaignId || isDeleting) return;

    setIsDeleting(true);
    try {
      // For published campaigns (has ads_campaign_id), archive instead of delete
      const isPublished = !!campaign?.ads_campaign_id;
      const response = isPublished
        ? await api.post(`/api/sense/campaigns/${campaignId}/archive`)
        : await api.delete(`/api/sense/campaigns/${campaignId}`);

      if (response.data.success) {
        setShowDeleteModal(false);
        // Navigate back to campaigns list
        navigateToCampaigns();
      } else {
        throw new Error(response.data.message
          || t(isPublished ? 'edit.failedArchiveCampaignAction' : 'edit.failedDeleteCampaignAction'));
      }
    } catch (error: any) {
      console.error('Error deleting/archiving campaign:', error);
      const isPublished = !!campaign?.ads_campaign_id;
      const errorMessage = extractErrorMessage(error,
        t(isPublished ? 'edit.failedArchiveCampaignAction' : 'edit.failedDeleteCampaignAction'));
      setMessageModalText(errorMessage);
      setMessageModalType('error');
      setShowMessageModal(true);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicateCampaign = async () => {
    if (!campaignId || isDuplicatingCampaign) return;

    setIsDuplicatingCampaign(true);
    try {
      const response = await api.post(`/api/sense/campaigns/${campaignId}/duplicate`);

      if (response.data.success) {
        // Navigate to the new campaign
        navigate(`/campaigns/${response.data.id}/edit`);
      } else {
        throw new Error(response.data.message || t('edit.failedDuplicateCampaign'));
      }
    } catch (error: any) {
      console.error('Error duplicating campaign:', error);
      const errorMessage = extractErrorMessage(error, t('edit.failedDuplicateCampaign'));
      setMessageModalText(errorMessage);
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setIsDuplicatingCampaign(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete || isDeletingGroup) return;

    setIsDeletingGroup(true);
    try {
      // For published groups (has ads_io_id), archive instead of delete
      const isPublished = !!groupToDelete.ads_io_id;
      const response = isPublished
        ? await api.post(`/api/sense/groups/${groupToDelete.id}/archive`)
        : await api.delete(`/api/sense/groups/${groupToDelete.id}`);

      if (response.data.success) {
        setShowDeleteGroupModal(false);
        // Remove the group from the list (archived groups are hidden from UI)
        setAdGroups(adGroups.filter(g => g.id !== groupToDelete.id));
        // Clear selection if this was the selected group
        if (selectedAdGroup?.id === groupToDelete.id) {
          setSelectedAdGroup(null);
        }
        setGroupToDelete(null);
      } else {
        throw new Error(response.data.message
          || t(isPublished ? 'edit.failedArchiveGroupAction' : 'edit.failedDeleteGroupAction'));
      }
    } catch (error: any) {
      console.error('Error deleting/archiving group:', error);
      const isPublished = !!groupToDelete.ads_io_id;
      const errorMessage = extractErrorMessage(error,
        t(isPublished ? 'edit.failedArchiveGroupAction' : 'edit.failedDeleteGroupAction'));
      setMessageModalText(errorMessage);
      setMessageModalType('error');
      setShowMessageModal(true);
      setShowDeleteGroupModal(false);
    } finally {
      setIsDeletingGroup(false);
    }
  };

  const handleDuplicateGroup = async (group: AdGroup) => {
    if (isDuplicatingGroup) return;

    setIsDuplicatingGroup(true);
    try {
      const response = await api.post(`/api/sense/groups/${group.id}/duplicate`);

      if (response.data.success) {
        // Reload ad groups list to get fresh data from server
        try {
          const adGroupsResponse = await api.get(`/api/reports/campaign/${campaignId}/ad-groups`);
          const newGroups = adGroupsResponse.data?.ad_groups || [];
          setAdGroups(newGroups);

          // Find and select the new group
          const newGroup = newGroups.find((g: AdGroup) => g.id?.toString() === response.data.id?.toString());
          if (newGroup) {
            setSelectedAdGroup(newGroup);
            // Force reload targeting from DB for the new group
            setTargetingReloadKey(k => k + 1);
          }
        } catch (err) {
          console.error('Error reloading ad groups after duplication:', err);
        }
      } else {
        throw new Error(response.data.message || t('edit.failedDuplicateGroup'));
      }
    } catch (error: any) {
      console.error('Error duplicating group:', error);
      const errorMessage = extractErrorMessage(error, t('edit.failedDuplicateGroup'));
      setMessageModalText(errorMessage);
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setIsDuplicatingGroup(false);
    }
  };

  // Helper functions for frequency cap time unit mapping
  const mapTimeUnitToUI = (timeUnit: string | null | undefined): string => {
    switch (timeUnit) {
      case 'TIME_UNIT_DAYS': return 'day';
      case 'TIME_UNIT_WEEKS': return 'week';
      case 'TIME_UNIT_MONTHS': return 'month';
      case 'TIME_UNIT_LIFETIME': return 'lifetime';
      default: return 'day';
    }
  };

  const mapUIToTimeUnit = (uiValue: string): string => {
    switch (uiValue) {
      case 'day': return 'TIME_UNIT_DAYS';
      case 'week': return 'TIME_UNIT_WEEKS';
      case 'month': return 'TIME_UNIT_MONTHS';
      case 'lifetime': return 'TIME_UNIT_LIFETIME';
      default: return 'TIME_UNIT_DAYS';
    }
  };

  // Check if campaign form has unsaved changes
  const hasCampaignChanges = (): boolean => {
    // In create mode, require name and dates
    if (isCreateMode) {
      return campaignName.trim().length > 0 && startDate.length >= 10 && endDate.length >= 10;
    }

    if (!campaign) return false;

    // Check name
    if (campaignName !== campaign.name) return true;

    // Check status
    const originalStatus = campaign.entity_status === 'ENTITY_STATUS_ACTIVE' ? 'active' :
                           campaign.entity_status === 'ENTITY_STATUS_PAUSED' ? 'paused' : 'ended';
    if (campaignStatus !== originalStatus && campaignStatus !== 'ended') return true;

    // Check start date (only if campaign hasn't actually started - published AND past start date)
    const today = new Date().toISOString().split('T')[0];
    const campaignIsPublished = !!campaign.ads_campaign_id;
    const campaignHasStarted = campaignIsPublished && campaign.start_date && campaign.start_date < today;
    if (!campaignHasStarted && startDate !== (campaign.start_date || '')) return true;

    // Check end date
    if (endDate !== (campaign.end_date || '')) return true;

    // Check frequency cap
    const originalFreqCap = campaign.frequency_cap;
    const originalUnlimited = originalFreqCap?.unlimited ?? true;
    const hasFrequencyLimit = frequencyCount !== '' && Number(frequencyCount) > 0;

    if (hasFrequencyLimit !== !originalUnlimited) return true;
    if (hasFrequencyLimit && (
      Number(frequencyCount) !== originalFreqCap?.maxImpressions ||
      frequencyPeriod !== mapTimeUnitToUI(originalFreqCap?.timeUnit)
    )) return true;

    return false;
  };

  // Check if ad group form has unsaved changes
  const hasAdGroupChanges = (): boolean => {
    if (!selectedAdGroup) return false;

    // Check name
    if (adGroupName !== selectedAdGroup.name) return true;

    // Check status
    const originalStatus = selectedAdGroup.status;
    if (adGroupStatus !== originalStatus && adGroupStatus !== 'ended') return true;

    // Check start date (only if group hasn't actually started - published AND past start date)
    const todayForGroup = new Date().toISOString().split('T')[0];
    const groupIsPublished = !!selectedAdGroup.ads_io_id;
    const groupHasStarted = groupIsPublished && selectedAdGroup.start_date && selectedAdGroup.start_date < todayForGroup;
    if (!groupHasStarted && adGroupStartDate !== (selectedAdGroup.start_date || '')) return true;

    // Check end date
    if (adGroupEndDate !== (selectedAdGroup.end_date || '')) return true;

    // Check budget
    const originalBudget = selectedAdGroup.budget ? String(selectedAdGroup.budget) : '';
    if (adGroupBudget !== originalBudget) return true;

    // Check frequency cap
    const originalFreqCap = selectedAdGroup.frequency_cap;
    const originalUnlimited = originalFreqCap?.unlimited ?? true;
    const hasFrequencyLimit = adGroupFrequencyCount !== '' && Number(adGroupFrequencyCount) > 0;

    if (hasFrequencyLimit !== !originalUnlimited) return true;
    if (hasFrequencyLimit && (
      Number(adGroupFrequencyCount) !== originalFreqCap?.max_impressions ||
      adGroupFrequencyPeriod !== (originalFreqCap?.time_unit || 'week')
    )) return true;

    // Check bid strategy
    if (adGroupBidStrategy !== (selectedAdGroup.bid_strategy || '')) return true;
    if (adGroupBidMode !== (selectedAdGroup.bid_mode || 'target')) return true;

    // Check KPI amount
    const originalKpiAmount = selectedAdGroup.kpi_amount ? String(selectedAdGroup.kpi_amount) : '';
    if (adGroupKpiAmount !== originalKpiAmount) return true;

    // Check media plan changes
    const origMediaPlanType = selectedAdGroup.media_plan_type || 'budget';
    const origMediaPlanTarget = selectedAdGroup.media_plan_target || '';
    if (adGroupMediaPlanType !== origMediaPlanType) return true;
    if (adGroupPlanTarget !== origMediaPlanTarget) return true;

    // Check targeting changes
    const arraysEqual = (a: string[], b: string[]) => {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, i) => val === sortedB[i]);
    };

    const originalTargeting: Partial<Targeting> = selectedAdGroup.targeting || {};

    // Age ranges and genders - check in demographic_split or legacy top-level fields
    const origDemoSplit = originalTargeting.demographic_split || {};
    const origAgeRanges = origDemoSplit.age_ranges || originalTargeting.age_ranges || [];
    if (!arraysEqual(targetingAgeRanges, origAgeRanges)) return true;

    const origGenders = origDemoSplit.genders || originalTargeting.genders || [];
    if (!arraysEqual(targetingGenders, origGenders)) return true;

    // Parental statuses
    const origParentalStatuses = originalTargeting.parental_statuses || [];
    if (!arraysEqual(targetingParentalStatuses, origParentalStatuses)) return true;

    // Household income
    const origHouseholdIncome = originalTargeting.household_income || [];
    if (!arraysEqual(targetingHouseholdIncome, origHouseholdIncome)) return true;

    // Devices (device types)
    const origDevices = originalTargeting.devices || [];
    if (!arraysEqual(targetingDevices, origDevices)) return true;

    // Device makes, Operating systems, Browsers - NOT YET SUPPORTED

    // Environments
    const origEnvironments = originalTargeting.environments || [];
    if (!arraysEqual(targetingEnvironment, origEnvironments)) return true;

    // Languages
    const origLanguages = originalTargeting.languages || [];
    if (!arraysEqual(targetingLanguages, origLanguages)) return true;

    // Viewability
    const origViewability = originalTargeting.viewability || [];
    if (!arraysEqual(targetingViewability, origViewability)) return true;

    // On Screen Position
    const origOnScreenPosition = originalTargeting.on_screen_position || [];
    if (!arraysEqual(targetingOnScreenPosition, origOnScreenPosition)) return true;

    // Categories (compare option_ids)
    const origCategoryIds = [...(originalTargeting.categories || [])].sort();
    const currentCategoryIds = targetingCategories.map(c => c.id).sort();
    if (JSON.stringify(origCategoryIds) !== JSON.stringify(currentCategoryIds)) return true;

    // Authorized Sellers (single select, empty in DB = 'Any')
    const origAuthSellers = originalTargeting.authorized_sellers || [];
    const normalizedOrig = origAuthSellers.length > 0 ? origAuthSellers : ['Any'];
    if (!arraysEqual(targetingAuthorizedSellers, normalizedOrig)) return true;

    // Content Labels Exclusions (empty = empty, no exclusions)
    const origContentLabels = originalTargeting.content_labels_excluded || [];
    if (!arraysEqual(targetingContentLabels, origContentLabels)) return true;

    // Sensitive Categories Exclusions (empty = empty, no exclusions)
    const origSensitiveCategories = originalTargeting.sensitive_categories_excluded || [];
    if (!arraysEqual(targetingSensitiveCategories, origSensitiveCategories)) return true;

    // Negative Keyword Lists
    if (!arraysEqual(negativeKeywordListIds, originalNegativeKeywordListIds)) return true;

    // Channels
    if (!arraysEqual(whiteListChannelIds, originalWhiteListChannelIds)) return true;
    if (!arraysEqual(blackListChannelIds, originalBlackListChannelIds)) return true;

    // Geo targeting - compare by names
    const getGeoNames = (geoArr: any[]): string[] => {
      return (geoArr || []).map(g => typeof g === 'string' ? g : g.name).filter(Boolean).sort();
    };
    const origGeoNames = getGeoNames(originalTargeting.geo || []);
    const currentGeoNames = targetingGeo.map(g => g.name).sort();
    if (JSON.stringify(origGeoNames) !== JSON.stringify(currentGeoNames)) return true;

    const origGeoExcludedNames = getGeoNames(originalTargeting.geo_excluded || []);
    const currentGeoExcludedNames = targetingGeoExcluded.map(g => g.name).sort();
    if (JSON.stringify(origGeoExcludedNames) !== JSON.stringify(currentGeoExcludedNames)) return true;

    // Geo mode (regions vs proximity)
    const origGeoMode = originalTargeting.geo_mode || 'regions';
    if (geoMode !== origGeoMode) return true;

    // Proximity lists - check if selected list or radius changed
    if (geoMode === 'proximity') {
      const origProximity = originalTargeting.proximity_lists || [];
      const activeList = resolveActiveProximityList();
      const origListId = origProximity[0]?.proximity_list_id;
      const origRadius = origProximity[0]?.radius_km || 10;

      // Check if list changed
      if (activeList) {
        if (activeList.id !== origListId) return true;
        if (proximityRadius !== origRadius) return true;
      } else if (origProximity.length > 0) {
        // Had proximity, now none selected
        return true;
      }
      // No active list and no original - that's the initial state, no change
    }

    // Day & Time - compare current grid state with original slots
    const origDayTimeSlots = originalTargeting.day_and_time || [];
    const allDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    const totalCells = 24 * 7;

    // Build set from original slots
    const origDayTimeSet = new Set<string>();
    if (origDayTimeSlots.length === 0) {
      // No slots = 24/7, all cells selected
      allDays.forEach(day => {
        for (let h = 0; h < 24; h++) {
          origDayTimeSet.add(`${day}-${h}`);
        }
      });
    } else {
      origDayTimeSlots.forEach((slot: DayTimeSlot) => {
        for (let hour = slot.startHour; hour < slot.endHour; hour++) {
          origDayTimeSet.add(`${slot.dayOfWeek}-${hour}`);
        }
      });
    }

    // Compare sets
    if (targetingDayTime.size !== origDayTimeSet.size) return true;
    const currentCells = Array.from(targetingDayTime);
    for (let i = 0; i < currentCells.length; i++) {
      if (!origDayTimeSet.has(currentCells[i])) return true;
    }

    // Advanced Targeting (Audiences) - compare by internal IDs
    const origAudienceIds = originalAdvancedTargetingItems.map(a => a.id).sort((a, b) => a - b);
    const currentAudienceIds = advancedTargetingItems.map(a => a.id).sort((a, b) => a - b);
    if (JSON.stringify(origAudienceIds) !== JSON.stringify(currentAudienceIds)) return true;

    // Creative IDs - compare sorted arrays
    const origCreativeIds = [...originalCreativeIds].sort((a, b) => a - b);
    const currentCreativeIds = [...adGroupCreativeIds].sort((a, b) => a - b);
    if (JSON.stringify(origCreativeIds) !== JSON.stringify(currentCreativeIds)) return true;

    // Optimization Goal IDs (for CPA strategy)
    const origGoalIds = [...(selectedAdGroup.optimization_goal_ids || [])].sort();
    const currentGoalIds = [...selectedGoalIds].sort();
    if (JSON.stringify(origGoalIds) !== JSON.stringify(currentGoalIds)) return true;

    // Remarketing Audiences
    const currentRemarketingConfig = {
      include: { operator: remarketingIncludeOperator, ids: [...remarketingIncludeIds].sort() },
      exclude: { ids: [...remarketingExcludeIds].sort() }
    };
    const origRemarketingConfig = originalRemarketingConfig ? {
      include: { operator: originalRemarketingConfig.include?.operator || 'OR', ids: [...(originalRemarketingConfig.include?.ids || [])].sort() },
      exclude: { ids: [...(originalRemarketingConfig.exclude?.ids || [])].sort() }
    } : { include: { operator: 'OR' as const, ids: [] as string[] }, exclude: { ids: [] as string[] } };
    if (JSON.stringify(currentRemarketingConfig) !== JSON.stringify(origRemarketingConfig)) return true;

    // Look-Alike (Optimized Targeting)
    if (enableLookAlike !== originalEnableLookAlike) return true;

    // Video targeting — only meaningful for video line items, but the
    // dirty-check has to compare regardless so the Save button enables
    // when the user toggles any of these.
    const origVideoPlayerSizes = originalTargeting.video_player_sizes || [];
    console.log('[dirty][video_player_size]', {
      current: targetingVideoPlayerSize,
      orig: origVideoPlayerSizes,
      equal: arraysEqual(targetingVideoPlayerSize, origVideoPlayerSizes),
    });
    if (!arraysEqual(targetingVideoPlayerSize, origVideoPlayerSizes)) return true;

    const origInstream = originalTargeting.content_instream_position?.length
      ? originalTargeting.content_instream_position
      : ALL_INSTREAM;
    if (!arraysEqual(targetingContentInstreamPosition, origInstream)) return true;

    const origOutstream = originalTargeting.content_outstream_position?.length
      ? originalTargeting.content_outstream_position
      : ALL_OUTSTREAM;
    if (!arraysEqual(targetingContentOutstreamPosition, origOutstream)) return true;

    const origStreamType = originalTargeting.content_stream_type || [];
    if (!arraysEqual(targetingContentStreamType, origStreamType)) return true;

    const origUserRewarded = originalTargeting.user_rewarded_content || [];
    if (!arraysEqual(targetingUserRewardedContent, origUserRewarded)) return true;

    return false;
  };

  // Save Ad Group settings to API
  const handleSaveAdGroup = async () => {
    // Block if targeting push is in progress
    if (targetingPushPending || targetingPushPendingRef.current) {
      return;
    }
    if (!selectedAdGroup || savingAdGroup) return;

    const isNewGroup = selectedAdGroup.id === '';

    // A LIVE (published) group must keep geography — saving it with empty
    // INCLUDED geo pushes the live campaign to serve globally (the PMI_Zenith
    // drain). Block immediately in the UI, before any backend call. (Only
    // INCLUDED counts; a filled exclusion does not. Proximity counts as geo.)
    const isPublishedGroup = !!selectedAdGroup.ads_io_id;
    const geoEmpty = geoMode === 'proximity'
      ? proximityLists.length === 0
      : targetingGeo.length === 0;
    if (isPublishedGroup && geoEmpty) {
      setMessageModalText(t('edit.addGeoBeforeSave'));
      setMessageModalType('error');
      setShowMessageModal(true);
      return;
    }

    // DV rejects an AUDIENCE_GROUP with too many audiences (HTTP 500 "Internal
    // error" above ~35 unique). Cap at 35 UNIQUE google audiences here so the
    // push can't silently fail and leave line items stuck in DRAFT.
    const uniqueAudiences = new Set(
      advancedTargetingItems.map(a => a.google_audience_id).filter(Boolean)
    ).size;
    if (uniqueAudiences > MAX_UNIQUE_AUDIENCES) {
      setMessageModalText(t('edit.tooManyAudiencesLong', {
        n: uniqueAudiences,
        max: MAX_UNIQUE_AUDIENCES,
        excess: uniqueAudiences - MAX_UNIQUE_AUDIENCES,
      }));
      setMessageModalType('error');
      setShowMessageModal(true);
      return;
    }

    // For new groups, validate required fields
    if (isNewGroup) {
      if (!adGroupName.trim()) {
        setMessageModalText(t('edit.groupNameRequired'));
        setMessageModalType('error');
        setShowMessageModal(true);
        return;
      }
      if (!campaignId) {
        setMessageModalText(t('edit.campaignIdRequired'));
        setMessageModalType('error');
        setShowMessageModal(true);
        return;
      }
    }

    setSavingAdGroup(true);
    try {
      // Handle new group creation
      if (isNewGroup) {
        const createBody: any = {
          campaign_id: parseInt(campaignId!),
          display_name: adGroupName.trim(),
          entity_status: adGroupStatus === 'active' ? 'ENTITY_STATUS_ACTIVE' : 'ENTITY_STATUS_PAUSED'
        };

        // Dates
        if (adGroupStartDate && adGroupStartDate.length >= 10) {
          createBody.start_date = adGroupStartDate;
        }
        if (adGroupEndDate && adGroupEndDate.length >= 10) {
          createBody.end_date = adGroupEndDate;
        }

        // Budget (stored in local currency, backend converts to EUR on push)
        if (adGroupBudget && Number(adGroupBudget) > 0) {
          createBody.budget = {
            budgetUnit: 'BUDGET_UNIT_CURRENCY',
            budgetAmountMicros: Math.round(Number(adGroupBudget) * 1000000)
          };
        }

        // Frequency cap
        const hasFrequencyLimit = adGroupFrequencyCount !== '' && Number(adGroupFrequencyCount) > 0;
        if (hasFrequencyLimit) {
          createBody.frequency_cap = {
            unlimited: false,
            maxImpressions: Number(adGroupFrequencyCount),
            timeUnit: adGroupFrequencyPeriod === 'day' ? 'TIME_UNIT_DAYS' :
                      adGroupFrequencyPeriod === 'week' ? 'TIME_UNIT_WEEKS' :
                      adGroupFrequencyPeriod === 'month' ? 'TIME_UNIT_MONTHS' : 'TIME_UNIT_WEEKS',
            timeUnitCount: 1
          };
        }

        // KPI & Bid Strategy (stored in local currency, backend converts to EUR on push)
        if (adGroupBidStrategy) {
          const kpiAmountMicros = adGroupKpiAmount ? Math.round(Number(adGroupKpiAmount) * 1000000) : 0;
          let kpiType = 'KPI_TYPE_CPM';
          if (adGroupBidStrategy === 'CPC') kpiType = 'KPI_TYPE_CPC';
          else if (adGroupBidStrategy === 'AV_VIEWED') kpiType = 'KPI_TYPE_VCPM';
          else if (adGroupBidStrategy === 'CIVA') kpiType = 'KPI_TYPE_CPCV';
          else if (adGroupBidStrategy === 'CPA') kpiType = 'KPI_TYPE_CPA';

          createBody.kpi = {
            kpiType: kpiType,
            kpiAmountMicros: kpiAmountMicros
          };

          let performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_CPC';
          if (adGroupBidStrategy === 'AV_VIEWED') performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_AV_VIEWED';
          else if (adGroupBidStrategy === 'CPC') performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_CPC';
          else if (adGroupBidStrategy === 'CIVA') performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_CIVA';
          else if (adGroupBidStrategy === 'CPA') performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_CPA';

          createBody.bid_strategy = adGroupBidMode === 'fixed'
            ? { fixedBid: { bidAmountMicros: String(kpiAmountMicros) } }
            : { maximizeSpendAutoBid: { performanceGoalType: performanceGoalType } };

          // Include optimization_goal_ids for CPA optimization
          if (adGroupBidStrategy === 'CPA' && selectedGoalIds.length > 0) {
            createBody.optimization_goal_ids = selectedGoalIds;
          }
        }

        // Media plan
        if (adGroupMediaPlanType !== 'budget') {
          createBody.media_plan_type = adGroupMediaPlanType;
          createBody.media_plan_target = adGroupPlanTarget || null;
        }

        // Build targeting object from form state
        const targeting: any = {};

        // Demographic split - age/gender combinations for Line Item creation
        // NOTE: age_ranges and genders go into demographic_split, NOT targeting
        const demographicSplit: any = {};
        if (targetingAgeRanges.length > 0) demographicSplit.age_ranges = targetingAgeRanges;
        if (targetingGenders.length > 0) demographicSplit.genders = targetingGenders;
        if (Object.keys(demographicSplit).length > 0) {
          targeting.demographic_split = demographicSplit;
        }

        // Other demographics (parental status, household income) stay in targeting
        if (targetingParentalStatuses.length > 0) targeting.parental_statuses = targetingParentalStatuses;
        if (targetingHouseholdIncome.length > 0) targeting.household_income = targetingHouseholdIncome;

        // Devices & Environment
        if (targetingDevices.length > 0) targeting.devices = targetingDevices;
        if (targetingEnvironment.length > 0) targeting.environments = targetingEnvironment;

        // Languages - save as names for local loading compatibility
        if (targetingLanguages.length > 0) {
          targeting.languages = targetingLanguages;  // Save as names directly
        }

        // Viewability & Brand Safety
        if (targetingViewability.length > 0) targeting.viewability = targetingViewability;
        if (targetingOnScreenPosition.length > 0) targeting.on_screen_position = targetingOnScreenPosition;
        if (targetingContentLabels.length > 0) targeting.content_labels_excluded = targetingContentLabels;
        if (targetingSensitiveCategories.length > 0) targeting.sensitive_categories_excluded = targetingSensitiveCategories;
        if (targetingAuthorizedSellers.length > 0) targeting.authorized_sellers = targetingAuthorizedSellers;

        // Video-specific targeting (only for video line items)
        if (adGroupLineItemType === 'video') {
          if (targetingVideoPlayerSize.length > 0) targeting.video_player_sizes = targetingVideoPlayerSize;
          // Always save instream/outstream - at least 1 must be selected
          targeting.content_instream_position = targetingContentInstreamPosition;
          targeting.content_outstream_position = targetingContentOutstreamPosition;
          // Stream Type and Rewarded Content temporarily disabled - requires targetingOptionId lookup
          // if (targetingContentStreamType.length > 0) targeting.content_stream_type = targetingContentStreamType;
          // if (targetingUserRewardedContent.length > 0) targeting.user_rewarded_content = targetingUserRewardedContent;
          if (targetingOmid.length > 0) targeting.omid = targetingOmid;
        }

        // Categories - save both IDs and full data for proper loading
        if (targetingCategories.length > 0) {
          targeting.categories = targetingCategories.map(c => c.id);
          targeting.categories_data = targetingCategories;
        }

        // Geo
        targeting.geo_mode = geoMode;
        if (geoMode === 'regions') {
          if (targetingGeo.length > 0) {
            targeting.geo_included = targetingGeo.map(g => ({
              targetingOptionId: g.id && !g.id.startsWith('existing-') ? g.id : null,
              displayName: g.name,
              geoRegionType: g.type
            }));
          }
          if (targetingGeoExcluded.length > 0) {
            targeting.geo_excluded = targetingGeoExcluded.map(g => ({
              targetingOptionId: g.id && !g.id.startsWith('existing-') ? g.id : null,
              displayName: g.name,
              geoRegionType: g.type
            }));
          }
        } else if (geoMode === 'proximity') {
          const activeList = resolveActiveProximityList();
          if (activeList) {
            targeting.proximity_lists = [{
              proximity_list_id: activeList.id,
              radius_km: proximityRadius
            }];
          }
        }

        // Day & Time
        if (targetingDayTime.size > 0 && targetingDayTime.size < 168) {
          // Convert Set to day/time slots (only if not all 168 hours selected)
          const allDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
          const dayTimeSlots: any[] = [];
          const slotsPerDay: Record<string, number> = {};
          allDays.forEach(day => {
            slotsPerDay[day] = 0;
            const selectedHours: number[] = [];
            for (let h = 0; h < 24; h++) {
              if (targetingDayTime.has(`${day}-${h}`)) selectedHours.push(h);
            }
            if (selectedHours.length > 0) {
              let slotStart = selectedHours[0];
              let prevHour = selectedHours[0];
              for (let i = 1; i <= selectedHours.length; i++) {
                const currentHour = selectedHours[i];
                if (currentHour !== prevHour + 1 || i === selectedHours.length) {
                  dayTimeSlots.push({
                    dayOfWeek: day,  // Keep UPPERCASE for consistency
                    startHour: slotStart,
                    endHour: prevHour + 1
                  });
                  slotsPerDay[day]++;
                  if (i < selectedHours.length) slotStart = currentHour;
                }
                prevHour = currentHour;
              }
            }
          });

          // Validate: Sense allows max 6 periods per day
          const daysOver6 = Object.entries(slotsPerDay)
            .filter(([_, count]) => count > 6)
            .map(([day]) => day);
          if (daysOver6.length > 0) {
            setMessageModalText(t('edit.tooManyGaps'));
            setMessageModalType('error');
            setShowMessageModal(true);
            setSavingAdGroup(false);
            return;
          }

          targeting.day_and_time = dayTimeSlots;
        }

        // Inventory
        if (negativeKeywordListIds.length > 0) targeting.negative_keyword_lists = negativeKeywordListIds;
        if (whiteListChannelIds.length > 0) targeting.channels_included = whiteListChannelIds;
        if (blackListChannelIds.length > 0) targeting.channels_excluded = blackListChannelIds;

        // Advanced Targeting (Audiences) - save as advanced_targeting for consistency with loading
        if (advancedTargetingItems.length > 0) {
          targeting.advanced_targeting = advancedTargetingItems.map(a => ({
            id: a.id,
            name: a.name,
            full_path: a.full_path,
            google_audience_id: a.google_audience_id,
            google_audience_type: a.google_audience_type
          }));
        }

        // Remarketing Audiences
        if (remarketingIncludeIds.length > 0 || remarketingExcludeIds.length > 0) {
          targeting.remarketing_audiences = {
            include: { operator: remarketingIncludeOperator, ids: remarketingIncludeIds },
            exclude: { ids: remarketingExcludeIds }
          };
        }

        // Add targeting to request body if has any data
        if (Object.keys(targeting).length > 0) {
          createBody.targeting = targeting;
        }

        // Add line item type
        createBody.line_item_type = adGroupLineItemType;

        // Add creative IDs
        if (adGroupCreativeIds.length > 0) {
          createBody.creative_ids = adGroupCreativeIds;
        }

        const response = await api.post(`/api/sense/groups`, createBody);

        if (response.data.id) {
          const newGroupId = response.data.id;

          // Transform targeting for local state (API uses geo_included, local uses geo)
          const localTargeting = {
            ...targeting,
            // Convert geo_included to geo for local loading
            geo: targetingGeo.map(g => ({ id: g.id, name: g.name, type: g.type })),
            geo_excluded: targetingGeoExcluded.map(g => ({ id: g.id, name: g.name, type: g.type })),
          };

          // Build the new group object
          const newGroup: AdGroup = {
            id: String(newGroupId),
            name: adGroupName.trim(),
            status: adGroupStatus,
            type: adGroupLineItemType === 'video' ? 'Video' : 'Display',
            line_item_type: adGroupLineItemType,
            creative_count: adGroupCreativeIds.length,
            creative_ids: adGroupCreativeIds,
            start_date: adGroupStartDate || null,
            end_date: adGroupEndDate || null,
            budget: adGroupBudget ? Number(adGroupBudget) : undefined,
            impressions: 0,
            clicks: 0,
            spend: 0,
            bid_strategy: adGroupBidStrategy || null,
            optimization_goal_ids: selectedGoalIds.length > 0 ? selectedGoalIds : null,
            kpi_type: null,
            kpi_amount: adGroupKpiAmount ? Number(adGroupKpiAmount) : null,
            frequency_cap: hasFrequencyLimit ? {
              unlimited: false,
              max_impressions: Number(adGroupFrequencyCount),
              time_unit: adGroupFrequencyPeriod,
              time_unit_count: 1
            } : null,
            targeting: localTargeting,  // Use transformed targeting for local state
            daily_max_impressions: null,
            media_plan_type: adGroupMediaPlanType,
            media_plan_target: adGroupMediaPlanType !== 'budget' ? (adGroupPlanTarget || null) : null,
            media_plan_daily_paused: false
          };

          // Add to list and select
          setAdGroups([...adGroups, newGroup]);
          setSelectedAdGroup(newGroup);

          // Update original creative IDs to match saved state
          setOriginalCreativeIds([...adGroupCreativeIds]);

          // Show success
          setAdGroupSaved(true);
          setTimeout(() => setAdGroupSaved(false), 2500);
        } else {
          throw new Error(response.data.message || t('edit.failedCreateGroup'));
        }

        setSavingAdGroup(false);
        return;
      }

      // Existing group - update logic
      const requestBody: any = {};

      // Check name
      if (adGroupName !== selectedAdGroup.name) {
        requestBody.display_name = adGroupName;
      }

      // Check status
      if (adGroupStatus !== selectedAdGroup.status && adGroupStatus !== 'ended' && adGroupStatus !== 'scheduled') {
        requestBody.entity_status = adGroupStatus === 'active' ? 'ENTITY_STATUS_ACTIVE' : 'ENTITY_STATUS_PAUSED';
      }

      // Check dates (compare dates only, not time) - only block if published AND past start date
      const todayStrGroup = new Date().toISOString().split('T')[0];
      const groupIsPublished = !!selectedAdGroup.ads_io_id;
      const groupHasStarted = groupIsPublished && selectedAdGroup.start_date && selectedAdGroup.start_date < todayStrGroup;
      if (!groupHasStarted && adGroupStartDate && adGroupStartDate.length >= 10 && adGroupStartDate !== selectedAdGroup.start_date) {
        requestBody.start_date = adGroupStartDate;
      }
      if (adGroupEndDate && adGroupEndDate.length >= 10 && adGroupEndDate !== selectedAdGroup.end_date) {
        requestBody.end_date = adGroupEndDate;
      }

      // Check line item type (placement type) - only for draft groups (not published to Sense)
      const isGroupDraftForType = !selectedAdGroup.ads_io_id;
      if (isGroupDraftForType && adGroupLineItemType !== selectedAdGroup.line_item_type) {
        requestBody.line_item_type = adGroupLineItemType;
      }

      // Check budget - also handle clearing budget (newBudget = null when originalBudget was set)
      // Check budget - also handle clearing budget (newBudget = null when originalBudget was set)
      const originalBudget = selectedAdGroup.budget ? Number(selectedAdGroup.budget) : null;
      const newBudget = adGroupBudget ? Number(adGroupBudget) : null;
      if (newBudget !== originalBudget) {
        if (newBudget !== null) {
          requestBody.budget = {
            budgetUnit: 'BUDGET_UNIT_CURRENCY',
            budgetAmountMicros: Math.round(newBudget * 1000000)
          };
        } else {
          // Clear budget - send null to remove it
          requestBody.budget = null;
        }
      }

      // Check frequency cap
      const originalFreqCap = selectedAdGroup.frequency_cap;
      const originalUnlimited = originalFreqCap?.unlimited ?? true;
      const hasFrequencyLimit = adGroupFrequencyCount !== '' && Number(adGroupFrequencyCount) > 0;

      if (hasFrequencyLimit !== !originalUnlimited ||
          (hasFrequencyLimit && (
            Number(adGroupFrequencyCount) !== originalFreqCap?.max_impressions ||
            adGroupFrequencyPeriod !== (originalFreqCap?.time_unit || 'week')
          ))) {
        if (hasFrequencyLimit) {
          requestBody.frequency_cap = {
            unlimited: false,
            maxImpressions: Number(adGroupFrequencyCount),
            timeUnit: adGroupFrequencyPeriod === 'day' ? 'TIME_UNIT_DAYS' :
                      adGroupFrequencyPeriod === 'week' ? 'TIME_UNIT_WEEKS' :
                      adGroupFrequencyPeriod === 'month' ? 'TIME_UNIT_MONTHS' : 'TIME_UNIT_WEEKS',
            timeUnitCount: 1
          };
        } else {
          requestBody.frequency_cap = { unlimited: true };
        }
      }

      // Check bid strategy and KPI
      // Switching Target <-> Fixed changes the strategy object even when the goal
      // type is untouched, so it has to open this block too — otherwise the save
      // went through with every other field and the pricing silently stayed put.
      if (adGroupBidStrategy !== (selectedAdGroup.bid_strategy || '') ||
          adGroupBidMode !== (selectedAdGroup.bid_mode || 'target')) {
        // Map UI bid strategy to API format
        if (adGroupBidStrategy) {
          const kpiAmountMicros = adGroupKpiAmount ? Math.round(Number(adGroupKpiAmount) * 1000000) : 0;

          // Build KPI based on strategy - use compatible KPI types
          let kpiType = 'KPI_TYPE_CPM';
          if (adGroupBidStrategy === 'CPC') kpiType = 'KPI_TYPE_CPC';
          else if (adGroupBidStrategy === 'AV_VIEWED') kpiType = 'KPI_TYPE_VCPM';  // Viewable CPM
          else if (adGroupBidStrategy === 'CIVA') kpiType = 'KPI_TYPE_CPCV';       // Cost per complete video view
          else if (adGroupBidStrategy === 'CPA') kpiType = 'KPI_TYPE_CPA';         // Cost per action

          requestBody.kpi = {
            kpiType: kpiType,
            kpiAmountMicros: kpiAmountMicros
          };

          // Build bid strategy - using maximizeSpendAutoBid
          let performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_CPC';
          if (adGroupBidStrategy === 'AV_VIEWED') performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_AV_VIEWED';
          else if (adGroupBidStrategy === 'CPC') performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_CPC';
          else if (adGroupBidStrategy === 'CIVA') performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_CIVA';
          else if (adGroupBidStrategy === 'CPA') performanceGoalType = 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_CPA';

          // Fixed price pays exactly the amount entered; target price lets the
          // platform bid up and down toward the goal. Same field, same conversion
          // on the backend (_bid_to_dv) — only the strategy object differs.
          requestBody.bid_strategy = adGroupBidMode === 'fixed'
            ? { fixedBid: { bidAmountMicros: String(kpiAmountMicros) } }
            : { maximizeSpendAutoBid: { performanceGoalType: performanceGoalType } };

          // Include optimization_goal_ids for CPA optimization
          if (adGroupBidStrategy === 'CPA') {
            requestBody.optimization_goal_ids = selectedGoalIds.length > 0 ? selectedGoalIds : null;
          }
        }
      } else if (adGroupKpiAmount !== (selectedAdGroup.kpi_amount ? String(selectedAdGroup.kpi_amount) : '')) {
        // Only KPI amount changed, update it with compatible KPI type
        const kpiAmountMicros = adGroupKpiAmount ? Math.round(Number(adGroupKpiAmount) * 1000000) : 0;
        let kpiType = 'KPI_TYPE_CPM';
        if (adGroupBidStrategy === 'CPC') kpiType = 'KPI_TYPE_CPC';
        else if (adGroupBidStrategy === 'AV_VIEWED') kpiType = 'KPI_TYPE_VCPM';
        else if (adGroupBidStrategy === 'CIVA') kpiType = 'KPI_TYPE_CPCV';
        else if (adGroupBidStrategy === 'CPA') kpiType = 'KPI_TYPE_CPA';

        requestBody.kpi = {
          kpiType: kpiType,
          kpiAmountMicros: kpiAmountMicros
        };

        // With a fixed bid the amount lives in two places: the KPI is the IO's
        // goal, the bid is what the line items actually bid. Sending only the
        // KPI moved the goal and left every line item on its old bid forever —
        // the UI showed the new number and the platform kept charging the old
        // one. Target mode carries no amount of its own, so it needs nothing here.
        if (adGroupBidMode === 'fixed') {
          requestBody.bid_strategy = { fixedBid: { bidAmountMicros: String(kpiAmountMicros) } };
        }
      }

      // Check if optimization_goal_ids changed
      const origGoalIds = [...(selectedAdGroup.optimization_goal_ids || [])].sort();
      const currentGoalIds = [...selectedGoalIds].sort();
      const goalIdsChanged = JSON.stringify(currentGoalIds) !== JSON.stringify(origGoalIds);
      if (goalIdsChanged) {
        requestBody.optimization_goal_ids = selectedGoalIds.length > 0 ? selectedGoalIds : null;
      }

      // Check if media plan changed
      const origMediaPlanType = selectedAdGroup.media_plan_type || 'budget';
      const origMediaPlanTarget = selectedAdGroup.media_plan_target || null;
      if (adGroupMediaPlanType !== origMediaPlanType) {
        requestBody.media_plan_type = adGroupMediaPlanType;
      }
      if (adGroupMediaPlanType !== 'budget' && adGroupPlanTarget !== origMediaPlanTarget) {
        requestBody.media_plan_target = adGroupPlanTarget || null;
      } else if (adGroupMediaPlanType === 'budget' && origMediaPlanTarget !== null) {
        // Clear target when switching back to budget
        requestBody.media_plan_target = null;
      }

      // Build targeting object if any targeting has changed
      const originalTargeting: Partial<Targeting> = selectedAdGroup.targeting || {};
      const targeting: any = {};
      let hasTargetingChanges = false;

      // Helper to check array equality
      const arraysEqual = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, i) => val === sortedB[i]);
      };

      // Age ranges and Genders - go into demographic_split
      const origDemoSplit = originalTargeting.demographic_split || {};
      const origAgeRanges = origDemoSplit.age_ranges || originalTargeting.age_ranges || [];
      const origGenders = origDemoSplit.genders || originalTargeting.genders || [];

      const demographicSplit: any = {};
      if (!arraysEqual(targetingAgeRanges, origAgeRanges)) {
        demographicSplit.age_ranges = targetingAgeRanges;
        hasTargetingChanges = true;
      }
      if (!arraysEqual(targetingGenders, origGenders)) {
        demographicSplit.genders = targetingGenders;
        hasTargetingChanges = true;
      }
      if (Object.keys(demographicSplit).length > 0) {
        // Include both age_ranges and genders in demographic_split for consistency
        targeting.demographic_split = {
          age_ranges: targetingAgeRanges,
          genders: targetingGenders
        };
      }

      // Parental statuses
      const origParentalStatuses = originalTargeting.parental_statuses || [];
      if (!arraysEqual(targetingParentalStatuses, origParentalStatuses)) {
        targeting.parental_statuses = targetingParentalStatuses;
        hasTargetingChanges = true;
      }

      // Household income
      const origHouseholdIncome = originalTargeting.household_income || [];
      if (!arraysEqual(targetingHouseholdIncome, origHouseholdIncome)) {
        targeting.household_income = targetingHouseholdIncome;
        hasTargetingChanges = true;
      }

      // Devices (device types)
      const origDevices = originalTargeting.devices || [];
      if (!arraysEqual(targetingDevices, origDevices)) {
        targeting.devices = targetingDevices;
        hasTargetingChanges = true;
      }

      // Device makes, Operating systems, Browsers - NOT YET SUPPORTED

      // Environments
      const origEnvironments = originalTargeting.environments || [];
      if (!arraysEqual(targetingEnvironment, origEnvironments)) {
        targeting.environments = targetingEnvironment;
        hasTargetingChanges = true;
      }

      // Languages (convert names to IDs for backend)
      const origLanguages = originalTargeting.languages || [];
      if (!arraysEqual(targetingLanguages, origLanguages)) {
        // Convert language names to IDs and create id->name mapping
        const languageIds: string[] = [];
        const languageNames: Record<string, string> = {};
        targetingLanguages.forEach(name => {
          const lang = availableLanguages.find(l => l.name === name);
          if (lang) {
            languageIds.push(lang.id);
            languageNames[lang.id] = lang.name;
          }
        });
        targeting.languages = languageIds;
        targeting.language_names = languageNames;
        hasTargetingChanges = true;
      }

      // Viewability (single select - empty string means "remove targeting")
      const origViewability = originalTargeting.viewability || [];
      if (!arraysEqual(targetingViewability, origViewability)) {
        // Send single viewability value or empty string to clear
        targeting.viewability = targetingViewability.length > 0 ? targetingViewability[0] : '';
        hasTargetingChanges = true;
      }

      // On Screen Position (multi select)
      const origOnScreenPosition = originalTargeting.on_screen_position || [];
      if (!arraysEqual(targetingOnScreenPosition, origOnScreenPosition)) {
        targeting.on_screen_position = targetingOnScreenPosition;
        hasTargetingChanges = true;
      }

      // Categories - send just the option_ids array
      const origCategoryIds = [...(originalTargeting.categories || [])].sort();
      const currentCategoryIds = targetingCategories.map(c => c.id).sort();
      if (JSON.stringify(origCategoryIds) !== JSON.stringify(currentCategoryIds)) {
        targeting.categories = currentCategoryIds;  // Send just IDs
        hasTargetingChanges = true;
      }

      // Authorized Sellers (single select, empty in DB = 'Any')
      const origAuthSellers = originalTargeting.authorized_sellers || [];
      const normalizedOrigAuth = origAuthSellers.length > 0 ? origAuthSellers : ['Any'];
      if (!arraysEqual(targetingAuthorizedSellers, normalizedOrigAuth)) {
        // Send single value: 'Authorized Only' or 'Any'
        // Backend will handle: 'Any' = default state = no targeting in Sense
        targeting.authorized_sellers = targetingAuthorizedSellers[0] || 'Any';
        hasTargetingChanges = true;
      }

      // Content Labels Exclusions (empty = empty, no exclusions)
      const origContentLabels = originalTargeting.content_labels_excluded || [];
      if (!arraysEqual(targetingContentLabels, origContentLabels)) {
        targeting.content_labels_excluded = targetingContentLabels;
        hasTargetingChanges = true;
      }

      // Sensitive Categories Exclusions (empty = empty, no exclusions)
      const origSensitiveCategories = originalTargeting.sensitive_categories_excluded || [];
      if (!arraysEqual(targetingSensitiveCategories, origSensitiveCategories)) {
        targeting.sensitive_categories_excluded = targetingSensitiveCategories;
        hasTargetingChanges = true;
      }

      // Video-specific targeting (only for video line items)
      if (adGroupLineItemType === 'video') {
        const origVideoPlayerSizes = originalTargeting.video_player_sizes || [];
        const origOmid = originalTargeting.omid || [];

        // Compare arrays for instream/outstream (default to all if not set in original)
        const origInstream = originalTargeting.content_instream_position || ALL_INSTREAM;
        const origOutstream = originalTargeting.content_outstream_position || ALL_OUTSTREAM;

        const videoTargetingChanged =
          !arraysEqual(targetingVideoPlayerSize, origVideoPlayerSizes) ||
          !arraysEqual(targetingContentInstreamPosition, origInstream) ||
          !arraysEqual(targetingContentOutstreamPosition, origOutstream) ||
          !arraysEqual(targetingOmid, origOmid);

        if (videoTargetingChanged) {
          targeting.video_player_sizes = targetingVideoPlayerSize;
          targeting.content_instream_position = targetingContentInstreamPosition;
          targeting.content_outstream_position = targetingContentOutstreamPosition;
          // Stream Type and Rewarded Content temporarily disabled
          targeting.omid = targetingOmid;
          hasTargetingChanges = true;
        }
      }

      // Negative Keyword Lists
      if (!arraysEqual(negativeKeywordListIds, originalNegativeKeywordListIds)) {
        targeting.negative_keyword_lists = negativeKeywordListIds;
        hasTargetingChanges = true;
      }

      // Channels - send both if either changed (they share same targeting type)
      const channelsIncludedChanged = !arraysEqual(whiteListChannelIds, originalWhiteListChannelIds);
      const channelsExcludedChanged = !arraysEqual(blackListChannelIds, originalBlackListChannelIds);
      if (channelsIncludedChanged || channelsExcludedChanged) {
        targeting.channels_included = whiteListChannelIds;
        targeting.channels_excluded = blackListChannelIds;
        hasTargetingChanges = true;
      }

      // Geo targeting - compare by names
      // Original data can be string[] (legacy) or object[] (new format)
      // Note: geo_mode determines which type is active (regions or proximity)
      const getGeoNames = (geoArr: any[]): string[] => {
        return (geoArr || []).map(g => typeof g === 'string' ? g : g.name).filter(Boolean).sort();
      };
      const origGeoNames = getGeoNames(originalTargeting.geo || []);
      const currentGeoNames = targetingGeo.map(g => g.name).sort();
      const origGeoExcludedNames = getGeoNames(originalTargeting.geo_excluded || []);
      const currentGeoExcludedNames = targetingGeoExcluded.map(g => g.name).sort();

      const geoIncludedChanged = JSON.stringify(origGeoNames) !== JSON.stringify(currentGeoNames);
      const geoExcludedChanged = JSON.stringify(origGeoExcludedNames) !== JSON.stringify(currentGeoExcludedNames);

      // Check if geo mode changed (original mode stored in targeting.geo_mode or inferred)
      const origGeoMode = originalTargeting.geo_mode || 'regions';
      const geoModeChanged = geoMode !== origGeoMode;

      // Handle geo targeting based on mode
      if (geoMode === 'proximity') {
        // Proximity mode - send proximity lists instead of regions
        targeting.geo_mode = 'proximity';
        // Build proximity_lists from selected or editing list
        // Always use current proximityRadius state (user might have changed it after saving list)
        const activeList = resolveActiveProximityList();
        if (activeList) {
          targeting.proximity_lists = [{
            proximity_list_id: activeList.id,
            radius_km: proximityRadius  // Always use current radius from state
          }];
        } else {
          targeting.proximity_lists = [];
        }
        hasTargetingChanges = true;
      } else if (geoIncludedChanged || geoExcludedChanged || geoModeChanged) {
        // Regions mode - send geo data with structure expected by backend
        targeting.geo_mode = 'regions';
        // id is the real targetingOptionId (from search or from DB)
        targeting.geo_included = targetingGeo.map(g => ({
          targetingOptionId: g.id && !g.id.startsWith('existing-') ? g.id : null,
          displayName: g.name,
          geoRegionType: g.type
        }));
        targeting.geo_excluded = targetingGeoExcluded.map(g => ({
          targetingOptionId: g.id && !g.id.startsWith('existing-') ? g.id : null,
          displayName: g.name,
          geoRegionType: g.type
        }));
        hasTargetingChanges = true;
      }

      // Day & Time targeting
      // Convert Set<"DAY-HOUR"> to DayTimeSlot[] and compare with original
      const allDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
      const dayDisplayMap: Record<string, string> = {
        'MONDAY': 'Monday', 'TUESDAY': 'Tuesday', 'WEDNESDAY': 'Wednesday',
        'THURSDAY': 'Thursday', 'FRIDAY': 'Friday', 'SATURDAY': 'Saturday', 'SUNDAY': 'Sunday'
      };

      // Check if all 168 hours are selected (24 hours * 7 days = no restrictions = 24/7)
      const totalCells = 24 * 7;
      const isAllSelected = targetingDayTime.size === totalCells;

      // Convert current selection to slots (group consecutive hours per day)
      const newDayTimeSlots: DayTimeSlot[] = [];
      const slotsPerDayUpdate: Record<string, number> = {};
      if (!isAllSelected) {
        allDays.forEach(day => {
          slotsPerDayUpdate[day] = 0;
          // Get all selected hours for this day
          const selectedHours: number[] = [];
          for (let h = 0; h < 24; h++) {
            if (targetingDayTime.has(`${day}-${h}`)) {
              selectedHours.push(h);
            }
          }

          // Group consecutive hours into slots
          if (selectedHours.length > 0) {
            let slotStart = selectedHours[0];
            let prevHour = selectedHours[0];

            for (let i = 1; i <= selectedHours.length; i++) {
              const currentHour = selectedHours[i];
              // If not consecutive or end of array, create a slot
              if (currentHour !== prevHour + 1 || i === selectedHours.length) {
                newDayTimeSlots.push({
                  dayOfWeek: dayDisplayMap[day],
                  startHour: slotStart,
                  endHour: prevHour + 1  // endHour is exclusive
                });
                slotsPerDayUpdate[day]++;
                if (i < selectedHours.length) {
                  slotStart = currentHour;
                }
              }
              prevHour = currentHour;
            }
          }
        });

        // Validate: Sense allows max 6 periods per day
        const daysOver6Update = Object.entries(slotsPerDayUpdate)
          .filter(([_, count]) => count > 6)
          .map(([day]) => dayDisplayMap[day] || day);
        if (daysOver6Update.length > 0) {
          setMessageModalText(t('edit.tooManyGaps'));
          setMessageModalType('error');
          setShowMessageModal(true);
          setSavingAdGroup(false);
          return;
        }
      }

      // Compare with original - convert original to comparable format
      const origDayTimeSlots = originalTargeting.day_and_time || [];
      const origIsAllSelected = origDayTimeSlots.length === 0; // No slots = 24/7

      // Compare: both are 24/7, or slots are different
      let dayTimeChanged = isAllSelected !== origIsAllSelected;
      if (!dayTimeChanged && !isAllSelected) {
        // Compare slot arrays
        const newSlotsStr = JSON.stringify(newDayTimeSlots.sort((a, b) =>
          a.dayOfWeek.localeCompare(b.dayOfWeek) || a.startHour - b.startHour
        ));
        const origSlotsStr = JSON.stringify(origDayTimeSlots.map((s: DayTimeSlot) => ({
          dayOfWeek: dayDisplayMap[s.dayOfWeek] || s.dayOfWeek,  // Normalize to display format
          startHour: s.startHour,
          endHour: s.endHour
        })).sort((a: DayTimeSlot, b: DayTimeSlot) =>
          a.dayOfWeek.localeCompare(b.dayOfWeek) || a.startHour - b.startHour
        ));
        dayTimeChanged = newSlotsStr !== origSlotsStr;
      }

      if (dayTimeChanged) {
        // Send empty array for 24/7 (no restrictions), or the slots
        targeting.day_time = isAllSelected ? [] : newDayTimeSlots;
        hasTargetingChanges = true;
      }

      // Advanced Targeting (Audiences) - compare by internal IDs
      const origAudienceIds = originalAdvancedTargetingItems.map(a => a.id).sort((a, b) => a - b);
      const currentAudienceIds = advancedTargetingItems.map(a => a.id).sort((a, b) => a - b);
      if (JSON.stringify(origAudienceIds) !== JSON.stringify(currentAudienceIds)) {
        // Send full audience objects with google_audience_id and type for backend to process
        targeting.audiences = advancedTargetingItems.map(a => ({
          id: a.id,
          name: a.name,
          full_path: a.full_path,
          google_audience_id: a.google_audience_id,
          google_audience_type: a.google_audience_type
        }));
        hasTargetingChanges = true;
      }

      // Remarketing Audiences - compare by config
      const currentRemarketingConfig: RemarketingAudiencesConfig = {
        include: { operator: remarketingIncludeOperator, ids: [...remarketingIncludeIds].sort() },
        exclude: { ids: [...remarketingExcludeIds].sort() }
      };
      const origConfig = originalRemarketingConfig ? {
        include: { operator: originalRemarketingConfig.include?.operator || 'OR', ids: [...(originalRemarketingConfig.include?.ids || [])].sort() },
        exclude: { ids: [...(originalRemarketingConfig.exclude?.ids || [])].sort() }
      } : { include: { operator: 'OR' as const, ids: [] }, exclude: { ids: [] } };

      if (JSON.stringify(currentRemarketingConfig) !== JSON.stringify(origConfig)) {
        // Only send if there are actual selections
        if (remarketingIncludeIds.length > 0 || remarketingExcludeIds.length > 0) {
          targeting.remarketing_audiences = currentRemarketingConfig;
        } else {
          targeting.remarketing_audiences = null;  // Clear if empty
        }
        hasTargetingChanges = true;
      }

      // Look-Alike (Optimized Targeting) - only for CPC/CPA strategies
      const bidStrategyChanged = adGroupBidStrategy !== (selectedAdGroup.bid_strategy || '');
      const bidStrategyIsIncompatible = adGroupBidStrategy === 'AV_VIEWED' || adGroupBidStrategy === 'CIVA';
      const lookAlikeChanged = enableLookAlike !== originalEnableLookAlike;

      // Send enable_optimized_targeting when:
      // 1. Look-Alike toggle changed, OR
      // 2. Bid strategy changed TO incompatible (need to force disable)
      if (lookAlikeChanged || (bidStrategyChanged && bidStrategyIsIncompatible)) {
        if (adGroupBidStrategy === 'CPC' || adGroupBidStrategy === 'CPA') {
          targeting.enable_optimized_targeting = enableLookAlike;
        } else {
          targeting.enable_optimized_targeting = false;  // Force disable for incompatible strategies
        }
        hasTargetingChanges = true;
      }

      if (hasTargetingChanges) {
        // For draft groups, send full targeting object (not just changed fields)
        // because backend overwrites the entire targeting column
        const isGroupDraft = !selectedAdGroup.ads_io_id;
        if (isGroupDraft) {
          const fullTargeting: any = {};

          // Demographic split - age/gender combinations for Line Item creation
          const demographicSplit: any = {};
          if (targetingAgeRanges.length > 0) demographicSplit.age_ranges = targetingAgeRanges;
          if (targetingGenders.length > 0) demographicSplit.genders = targetingGenders;
          if (Object.keys(demographicSplit).length > 0) {
            fullTargeting.demographic_split = demographicSplit;
          }

          // Other demographics stay in targeting
          if (targetingParentalStatuses.length > 0) fullTargeting.parental_statuses = targetingParentalStatuses;
          if (targetingHouseholdIncome.length > 0) fullTargeting.household_income = targetingHouseholdIncome;

          // Devices & Environment
          if (targetingDevices.length > 0) fullTargeting.devices = targetingDevices;
          if (targetingEnvironment.length > 0) fullTargeting.environments = targetingEnvironment;

          // Languages - save as names (same format as loading expects)
          fullTargeting.languages = targetingLanguages;

          // Viewability & Brand Safety
          fullTargeting.viewability = targetingViewability;
          fullTargeting.on_screen_position = targetingOnScreenPosition;
          fullTargeting.content_labels_excluded = targetingContentLabels;
          fullTargeting.sensitive_categories_excluded = targetingSensitiveCategories;
          fullTargeting.authorized_sellers = targetingAuthorizedSellers;

          // Categories - save as objects with id and name for proper loading
          fullTargeting.categories = targetingCategories.map(c => c.id);
          fullTargeting.categories_data = targetingCategories;  // Full objects for loading

          // Video-specific targeting
          if (adGroupLineItemType === 'video') {
            if (targetingVideoPlayerSize.length > 0) fullTargeting.video_player_sizes = targetingVideoPlayerSize;
            // Always save instream/outstream
            fullTargeting.content_instream_position = targetingContentInstreamPosition;
            fullTargeting.content_outstream_position = targetingContentOutstreamPosition;
            // Stream Type and Rewarded Content temporarily disabled
            if (targetingOmid.length > 0) fullTargeting.omid = targetingOmid;
          }

          // Geo. Keep raw `geo` (UI reload reads it) AND set geo_included/
          // geo_excluded in API format — the backend targeting push reads
          // geo_included; raw `geo` alone is IGNORED by the reconcile, which then
          // treats geo as empty and DELETES every region (the geo-wipe bug).
          fullTargeting.geo_mode = geoMode;
          if (geoMode === 'regions') {
            fullTargeting.geo = targetingGeo.map(g => ({
              id: g.id,
              name: g.name,
              type: g.type
            }));
            fullTargeting.geo_included = targetingGeo.map(g => ({
              targetingOptionId: g.id && !g.id.startsWith('existing-') ? g.id : null,
              displayName: g.name,
              geoRegionType: g.type
            }));
            fullTargeting.geo_excluded = targetingGeoExcluded.map(g => ({
              targetingOptionId: g.id && !g.id.startsWith('existing-') ? g.id : null,
              displayName: g.name,
              geoRegionType: g.type
            }));
          } else if (geoMode === 'proximity') {
            const activeList = resolveActiveProximityList();
            if (activeList) {
              fullTargeting.proximity_lists = [{
                proximity_list_id: activeList.id,
                radius_km: proximityRadius
              }];
            }
          }

          // Day & Time - use UPPERCASE dayOfWeek (same as loading expects)
          if (targetingDayTime.size > 0 && targetingDayTime.size < 168) {
            const allDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
            const dayTimeSlots: any[] = [];
            allDays.forEach(day => {
              const selectedHours: number[] = [];
              for (let h = 0; h < 24; h++) {
                if (targetingDayTime.has(`${day}-${h}`)) selectedHours.push(h);
              }
              if (selectedHours.length > 0) {
                let slotStart = selectedHours[0];
                let prevHour = selectedHours[0];
                for (let i = 1; i <= selectedHours.length; i++) {
                  const currentHour = selectedHours[i];
                  if (currentHour !== prevHour + 1 || i === selectedHours.length) {
                    dayTimeSlots.push({
                      dayOfWeek: day,  // Keep UPPERCASE
                      startHour: slotStart,
                      endHour: prevHour + 1
                    });
                    if (i < selectedHours.length) slotStart = currentHour;
                  }
                  prevHour = currentHour;
                }
              }
            });
            fullTargeting.day_and_time = dayTimeSlots;
          }

          // Inventory - use same field names as loading expects
          fullTargeting.negative_keyword_lists = negativeKeywordListIds;
          fullTargeting.channels_included = whiteListChannelIds;
          fullTargeting.channels_excluded = blackListChannelIds;

          // Advanced Targeting (Audiences)
          fullTargeting.advanced_targeting = advancedTargetingItems.map(a => ({
            id: a.id,
            name: a.name,
            full_path: a.full_path,
            google_audience_id: a.google_audience_id,
            google_audience_type: a.google_audience_type
          }));

          // Remarketing Audiences
          if (remarketingIncludeIds.length > 0 || remarketingExcludeIds.length > 0) {
            fullTargeting.remarketing_audiences = {
              include: { operator: remarketingIncludeOperator, ids: remarketingIncludeIds },
              exclude: { ids: remarketingExcludeIds }
            };
          }

          // Look-Alike (Optimized Targeting) - only for CPC/CPA strategies
          if (enableLookAlike && (adGroupBidStrategy === 'CPC' || adGroupBidStrategy === 'CPA')) {
            fullTargeting.enable_optimized_targeting = true;
          } else {
            fullTargeting.enable_optimized_targeting = false;
          }

          requestBody.targeting = fullTargeting;
        } else {
          // For published groups, send only changed fields (partial update)
          requestBody.targeting = targeting;
        }
      }

      // Creative IDs - check if changed
      const origCreativeIdsSorted = [...originalCreativeIds].sort((a, b) => a - b);
      const currentCreativeIdsSorted = [...adGroupCreativeIds].sort((a, b) => a - b);
      if (JSON.stringify(origCreativeIdsSorted) !== JSON.stringify(currentCreativeIdsSorted)) {
        requestBody.creative_ids = adGroupCreativeIds;
      }

      // If nothing changed, just return
      if (Object.keys(requestBody).length === 0) {
        setSavingAdGroup(false);
        return;
      }

      // Use different endpoint for draft groups (local) vs published groups (Sense)
      // Draft groups use /api/sense/groups/{id} with local numeric ID
      // Published groups use /api/groups/{id} with ads_io_id
      const isGroupDraft = !selectedAdGroup.ads_io_id;
      const endpoint = isGroupDraft
        ? `/api/sense/groups/${selectedAdGroup.id}`
        : `/api/groups/${selectedAdGroup.ads_io_id}`;

      // Put the new values on screen BEFORE the request. The platform push now
      // runs in the background, so anything that waits for the response shows
      // the old name, dates and budget for as long as publishing takes.
      const previousAdGroups = adGroups;
      const previousSelected = selectedAdGroup;
      const optimisticAdGroup = {
        ...selectedAdGroup,
        name: adGroupName,
        status: adGroupStatus === 'active' ? 'active' : adGroupStatus === 'paused' ? 'paused' : selectedAdGroup.status,
        start_date: adGroupStartDate || selectedAdGroup.start_date,
        end_date: adGroupEndDate || selectedAdGroup.end_date,
        budget: newBudget ?? selectedAdGroup.budget,
        push_pending: true,
      };
      setSelectedAdGroup(optimisticAdGroup);
      setAdGroups(adGroups.map(ag =>
        (ag.id === selectedAdGroup.id) ||
        (ag.ads_io_id && selectedAdGroup.ads_io_id && ag.ads_io_id === selectedAdGroup.ads_io_id)
          ? optimisticAdGroup
          : ag
      ));

      let response;
      try {
        response = await api.patch(endpoint, requestBody);
      } catch (err) {
        // The save did not even reach us — put the old values back rather than
        // leaving the screen claiming a change that never happened.
        setSelectedAdGroup(previousSelected);
        setAdGroups(previousAdGroups);
        throw err;
      }

      // Response format differs: sense endpoint returns {message}, group_actions returns {success}
      if (response.data.success || response.data.message) {
        // Update local state
        // Build updated targeting for local state
        const updatedTargeting: Targeting | null | undefined = hasTargetingChanges ? {
          // Required fields with fallbacks
          // Geo - update with current state if changed
          geo: targeting.geo_included !== undefined
            ? targetingGeo.map(g => ({ id: g.id, name: g.name, type: g.type }))
            : selectedAdGroup.targeting?.geo || [],
          geo_excluded: targeting.geo_excluded !== undefined
            ? targetingGeoExcluded.map(g => ({ id: g.id, name: g.name, type: g.type }))
            : selectedAdGroup.targeting?.geo_excluded || [],
          devices: targeting.devices ?? selectedAdGroup.targeting?.devices ?? [],
          // Age/gender stored in demographic_split
          demographic_split: targeting.demographic_split ?? selectedAdGroup.targeting?.demographic_split ?? {
            age_ranges: targetingAgeRanges,
            genders: targetingGenders
          },
          parental_statuses: targeting.parental_statuses ?? selectedAdGroup.targeting?.parental_statuses ?? [],
          // Optional fields
          household_income: targeting.household_income ?? selectedAdGroup.targeting?.household_income,
          device_makes: targeting.device_makes ?? selectedAdGroup.targeting?.device_makes,
          operating_systems: targeting.operating_systems ?? selectedAdGroup.targeting?.operating_systems,
          browsers: targeting.browsers ?? selectedAdGroup.targeting?.browsers,
          environments: targeting.environments ?? selectedAdGroup.targeting?.environments,
          languages: targeting.languages ? targetingLanguages : selectedAdGroup.targeting?.languages,
          viewability: targeting.viewability !== undefined ? [targeting.viewability].filter(Boolean) : selectedAdGroup.targeting?.viewability,
          categories: targeting.categories ? targetingCategories.map(c => c.id) : selectedAdGroup.targeting?.categories,
          categories_data: targetingCategories,  // Full objects for draft groups
          on_screen_position: targeting.on_screen_position ?? selectedAdGroup.targeting?.on_screen_position,
          // Preserve other targeting fields
          content_labels_excluded: targeting.content_labels_excluded ?? selectedAdGroup.targeting?.content_labels_excluded,
          sensitive_categories_excluded: targeting.sensitive_categories_excluded ?? selectedAdGroup.targeting?.sensitive_categories_excluded,
          authorized_sellers: targeting.authorized_sellers !== undefined
            ? [targeting.authorized_sellers]
            : selectedAdGroup.targeting?.authorized_sellers,
          video_player_sizes: adGroupLineItemType === 'video' ? targetingVideoPlayerSize : selectedAdGroup.targeting?.video_player_sizes,
          content_instream_position: adGroupLineItemType === 'video' ? targetingContentInstreamPosition : selectedAdGroup.targeting?.content_instream_position,
          content_outstream_position: adGroupLineItemType === 'video' ? targetingContentOutstreamPosition : selectedAdGroup.targeting?.content_outstream_position,
          content_stream_type: adGroupLineItemType === 'video' ? targetingContentStreamType : selectedAdGroup.targeting?.content_stream_type,
          user_rewarded_content: adGroupLineItemType === 'video' ? targetingUserRewardedContent : selectedAdGroup.targeting?.user_rewarded_content,
          omid: adGroupLineItemType === 'video' ? targetingOmid : selectedAdGroup.targeting?.omid,
          negative_keyword_lists: targeting.negative_keyword_lists ?? selectedAdGroup.targeting?.negative_keyword_lists,
          channels_included: targeting.channels_included ?? selectedAdGroup.targeting?.channels_included,
          channels_excluded: targeting.channels_excluded ?? selectedAdGroup.targeting?.channels_excluded,
          day_and_time: (targeting.day_time !== undefined || targeting.day_and_time !== undefined)
            ? (targeting.day_time || targeting.day_and_time || []).map((slot: DayTimeSlot) => ({
                ...slot,
                dayOfWeek: slot.dayOfWeek.toUpperCase()  // Convert 'Monday' to 'MONDAY' for consistency
              }))
            : selectedAdGroup.targeting?.day_and_time,
          advanced_targeting: (targeting.audiences !== undefined || targeting.advanced_targeting !== undefined)
            ? advancedTargetingItems
            : selectedAdGroup.targeting?.advanced_targeting,
          // Proximity geo targeting
          geo_mode: targeting.geo_mode ?? selectedAdGroup.targeting?.geo_mode ?? 'regions',
          proximity_lists: targeting.proximity_lists ?? selectedAdGroup.targeting?.proximity_lists,
        } : selectedAdGroup.targeting;

        const updatedAdGroup = {
          ...selectedAdGroup,
          name: adGroupName,
          status: adGroupStatus === 'active' ? 'active' : adGroupStatus === 'paused' ? 'paused' : selectedAdGroup.status,
          start_date: adGroupStartDate || selectedAdGroup.start_date,
          end_date: adGroupEndDate || selectedAdGroup.end_date,
          budget: newBudget ?? undefined,
          frequency_cap: hasFrequencyLimit ? {
            unlimited: false,
            max_impressions: Number(adGroupFrequencyCount),
            time_unit: adGroupFrequencyPeriod,
            time_unit_count: 1
          } : { unlimited: true },
          bid_strategy: adGroupBidStrategy || selectedAdGroup.bid_strategy,
          optimization_goal_ids: selectedGoalIds.length > 0 ? selectedGoalIds : null,
          kpi_amount: adGroupKpiAmount ? Number(adGroupKpiAmount) : undefined,
          media_plan_type: adGroupMediaPlanType,
          media_plan_target: adGroupMediaPlanType !== 'budget' ? (adGroupPlanTarget || null) : null,
          line_item_type: adGroupLineItemType,
          type: adGroupLineItemType === 'video' ? 'Video' : 'Display',
          targeting: updatedTargeting,
          creative_ids: adGroupCreativeIds,
          creative_count: adGroupCreativeIds.length,
          // Set targeting_push_pending from response if targeting was updated in background
          targeting_push_pending: response.data.targeting_push_pending ?? selectedAdGroup.targeting_push_pending ?? false
        };

        setSelectedAdGroup(updatedAdGroup);
        setAdGroups(adGroups.map(ag =>
          (ag.id === selectedAdGroup.id) ||
          (ag.ads_io_id && selectedAdGroup.ads_io_id && ag.ads_io_id === selectedAdGroup.ads_io_id)
            ? updatedAdGroup
            : ag
        ));

        // Set separate pending state if targeting is being pushed in background
        if (response.data.targeting_push_pending) {
          setTargetingPushPending(true);
          targetingPushPendingRef.current = true;
        }

        // Update original values to match saved state
        setOriginalNegativeKeywordListIds([...negativeKeywordListIds]);
        setOriginalWhiteListChannelIds([...whiteListChannelIds]);
        setOriginalBlackListChannelIds([...blackListChannelIds]);
        setOriginalAdvancedTargetingItems([...advancedTargetingItems]);
        setOriginalCreativeIds([...adGroupCreativeIds]);

        // Force reload targeting from DB to show actual saved values
        // (only for published groups - draft groups use local state).
        // Skip the reload while the targeting push is still running in the
        // background: the DB doesn't reflect the new targeting yet, so reloading
        // now would overwrite the optimistic local state (set above) with stale
        // values — this is what made device targeting "revert" in the UI right
        // after save. The pending poll reloads from DB once the push completes.
        if (selectedAdGroup.ads_io_id && !response.data.targeting_push_pending) {
          setTargetingReloadKey(k => k + 1);
        }

        // Show success state
        setAdGroupSaved(true);
        setTimeout(() => setAdGroupSaved(false), 2500);
      } else {
        throw new Error(response.data.message || t('edit.failedUpdateGroup'));
      }
    } catch (error: any) {
      console.error('Error saving ad group:', error);
      const errorMessage = extractErrorMessage(error, t('edit.failedSaveGroup'));
      setMessageModalText(errorMessage);
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setSavingAdGroup(false);
    }
  };

  // Publish a single draft group to Sense
  const handlePublishGroup = async (group: AdGroup) => {
    if (!group.id || group.ads_io_id || publishingGroup) return;

    setPublishingGroup(true);
    try {
      const response = await api.post(
        `/api/sense/groups/${group.id}/push`
      );

      if (response.data.success) {
        // Update group in local state with new ads_io_id and targeting_push_pending
        // Keep existing targeting - don't reload from DB (local state is correct)
        const updatedGroup = {
          ...group,
          ads_io_id: response.data.ads_io_id,
          targeting_push_pending: response.data.push_pending ?? response.data.targeting_push_pending ?? true  // Mark as pending until background task completes
        };
        setAdGroups(adGroups.map(ag => ag.id === group.id ? updatedGroup : ag));
        if (selectedAdGroup?.id === group.id) {
          setSelectedAdGroup(updatedGroup);
          // Update URL to use new ads_io_id
          if (response.data.ads_io_id) {
            navigate(`/campaigns/${campaignId}/edit/${response.data.ads_io_id}`, { replace: true });
          }
          // Don't trigger targetingReloadKey - local state already has correct targeting
        }
      } else {
        throw new Error(response.data.message || t('edit.failedPublishGroup'));
      }
    } catch (error: any) {
      console.error('Error publishing group:', error);
      const errorMessage = extractErrorMessage(error, t('edit.failedPublishGroup'));
      setMessageModalText(errorMessage);
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setPublishingGroup(false);
    }
  };

  // Publish all draft groups that have required fields filled
  const handlePublishAllGroups = async () => {
    const draftGroups = adGroups.filter(ag => !ag.ads_io_id && canGroupBePublished(ag));
    if (draftGroups.length === 0 || publishingAllGroups) return;

    setPublishingAllGroups(true);

    // Immediately mark all draft groups as pending in local state
    setAdGroups(prev => prev.map(ag => {
      if (draftGroups.find(dg => dg.id === ag.id)) {
        return { ...ag, push_pending: true, targeting_push_pending: true };
      }
      return ag;
    }));

    // Also update selectedAdGroup if it's one of the groups being published
    if (selectedAdGroup && draftGroups.find(dg => dg.id === selectedAdGroup.id)) {
      setSelectedAdGroup(prev => prev ? { ...prev, push_pending: true, targeting_push_pending: true } : null);
      setTargetingPushPending(true);
    }

    // Fire all requests in parallel with background=true (don't wait for results)
    const promises = draftGroups.map(group =>
      api.post(`/api/sense/groups/${group.id}/push?background=true`)
        .catch(error => {
          console.error(`Error starting publish for group ${group.name}:`, error);
          // On error, clear pending flag for this group
          setAdGroups(prev => prev.map(ag =>
            ag.id === group.id ? { ...ag, push_pending: false, targeting_push_pending: false } : ag
          ));
          return { error: true };
        })
    );

    // Wait for all requests to be sent (not completed - they run in background)
    const results = await Promise.all(promises);
    const errorCount = results.filter(r => r && (r as any).error).length;

    setPublishingAllGroups(false);

    if (errorCount > 0) {
      setMessageModalText(t('edit.startedPublishingSome',
        { ok: draftGroups.length - errorCount, failed: errorCount }));
      setMessageModalType('error');
      setShowMessageModal(true);
    }
    // Polling will update state as each group completes
  };

  const handleSaveWhiteList = async () => {
    if (!editingWhiteListChannel || !campaign?.advertiser_id) return;
    const sites = whiteListInput.split('\n').filter(line => line.trim());
    setSavingWhiteList(true);
    try {
      const response = await api.put(
        `/api/channels/${editingWhiteListChannel.channel_id}/sites?advertiser_id=${campaign.advertiser_id}`,
        { sites }
      );
      if (response.data.success) {
        const validSites = response.data.valid_sites || [];
        const filtered = response.data.filtered || 0;

        // Update sites count and input with valid sites only
        setAllChannels(allChannels.map(ch =>
          ch.channel_id === editingWhiteListChannel.channel_id
            ? { ...ch, sites_count: validSites.length }
            : ch
        ));

        // Show message if some URLs were filtered
        if (filtered > 0) {
          setWhiteListInput(validSites.join('\n'));
          setOriginalWhiteListInput(validSites.join('\n'));
          setMessageModalText(t('edit.someUrlsInvalid'));
          setMessageModalType('info');
          setShowMessageModal(true);
        } else {
          // Close editor if all URLs were valid
          setEditingWhiteListChannel(null);
          setWhiteListInput('');
          setOriginalWhiteListInput('');
        }
      }
    } catch (err: any) {
      console.error('Failed to update channel:', err);
      setMessageModalText(t('edit.failedSaveUrlChanges'));
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setSavingWhiteList(false);
    }
  };

  const handleCreateWhiteList = async (listName: string) => {
    if (!listName.trim() || !campaign?.advertiser_id) return;
    const sites = whiteListInput.split('\n').filter(line => line.trim());
    setSavingWhiteList(true);
    try {
      const response = await api.post(`/api/channels/create`, {
        advertiser_id: campaign.advertiser_id,
        display_name: listName.trim(),
        sites: sites
      });
      if (response.data.success) {
        // A large list is split into several channels server-side — attach ALL.
        const ids: string[] = (response.data.channel_ids && response.data.channel_ids.length)
          ? response.data.channel_ids
          : [response.data.id];
        await fetchChannels(campaign.advertiser_id);  // pull in every created part
        setWhiteListChannelIds([...whiteListChannelIds, ...ids]);
        setNewWhiteListName('');
        setShowWhiteListSaveModal(false);
        setIsCreatingWhiteList(false);
        setWhiteListInput('');
      }
    } catch (err: any) {
      console.error('Failed to create channel:', err);
      const errorMessage = extractErrorMessage(err, t('edit.failedCreateListRetry'));
      alert(errorMessage);
    } finally {
      setSavingWhiteList(false);
    }
  };

  const handleRenameWhiteListChannel = async () => {
    if (!editingWhiteListChannel || !campaign?.advertiser_id || !editingWhiteListChannelName.trim()) {
      setIsEditingWhiteListChannelName(false);
      return;
    }
    if (editingWhiteListChannelName === editingWhiteListChannel.name) {
      setIsEditingWhiteListChannelName(false);
      return;
    }
    setIsRenamingWhiteListChannel(true);
    try {
      const response = await api.patch(
        `/api/channels/${editingWhiteListChannel.channel_id}?advertiser_id=${campaign.advertiser_id}`,
        { display_name: editingWhiteListChannelName.trim() }
      );
      if (response.data.success) {
        setAllChannels(allChannels.map(ch =>
          ch.channel_id === editingWhiteListChannel.channel_id
            ? { ...ch, name: editingWhiteListChannelName.trim() }
            : ch
        ));
        setEditingWhiteListChannel({ ...editingWhiteListChannel, name: editingWhiteListChannelName.trim() });
      }
    } catch (err) {
      console.error('Failed to rename channel:', err);
      alert(t('edit.failedRenameListRetry'));
      setEditingWhiteListChannelName(editingWhiteListChannel.name);
    } finally {
      setIsRenamingWhiteListChannel(false);
      setIsEditingWhiteListChannelName(false);
    }
  };

  const handleDeleteWhiteListChannel = async () => {
    if (!editingWhiteListChannel || !campaign?.advertiser_id) return;
    setIsDeletingWhiteListChannel(true);
    try {
      const response = await api.delete(
        `/api/channels/${editingWhiteListChannel.channel_id}?advertiser_id=${campaign.advertiser_id}`
      );
      if (response.data.success) {
        setAllChannels(allChannels.filter(ch => ch.channel_id !== editingWhiteListChannel.channel_id));
        setWhiteListChannelIds(whiteListChannelIds.filter(id => id !== editingWhiteListChannel.channel_id));
        setBlackListChannelIds(blackListChannelIds.filter(id => id !== editingWhiteListChannel.channel_id));
        setEditingWhiteListChannel(null);
        setWhiteListInput('');
        setOriginalWhiteListInput('');
        setShowDeleteWhiteListConfirm(false);
      }
    } catch (err) {
      console.error('Failed to delete channel:', err);
      alert(t('edit.failedDeleteListRetry'));
    } finally {
      setIsDeletingWhiteListChannel(false);
    }
  };

  // Black List handlers
  const handleSelectBlackListChannel = async (channel: Channel) => {
    // Toggle: if same channel is already open, close it
    if (editingBlackListChannel?.channel_id === channel.channel_id) {
      setEditingBlackListChannel(null);
      setBlackListInput('');
      setOriginalBlackListInput('');
      setEditingBlackListChannelName('');
      setIsEditingBlackListChannelName(false);
      return;
    }
    setEditingBlackListChannel(channel);
    setEditingBlackListChannelName(channel.name);
    setIsEditingBlackListChannelName(false);
    setIsCreatingBlackList(false);
    const urls = await fetchChannelUrls(channel.channel_id);
    const urlsText = urls.join('\n');
    setBlackListInput(urlsText);
    setOriginalBlackListInput(urlsText);
    setShowBlackListSaveModal(false);
    setNewBlackListName('');
  };

  const handleNewBlackList = () => {
    setEditingBlackListChannel(null);
    setBlackListInput('');
    setOriginalBlackListInput('');
    setShowBlackListSaveModal(false);
    setNewBlackListName('');
    setEditingBlackListChannelName('');
    setIsCreatingBlackList(true);
  };

  const handleRemoveBlackListChannel = (channelId: string) => {
    setBlackListChannelIds(blackListChannelIds.filter(id => id !== channelId));
    if (editingBlackListChannel?.channel_id === channelId) {
      setEditingBlackListChannel(null);
      setBlackListInput('');
    }
  };

  const handleSaveBlackList = async () => {
    if (!editingBlackListChannel || !campaign?.advertiser_id) return;
    const sites = blackListInput.split('\n').filter(line => line.trim());
    setSavingBlackList(true);
    try {
      const response = await api.put(
        `/api/channels/${editingBlackListChannel.channel_id}/sites?advertiser_id=${campaign.advertiser_id}`,
        { sites }
      );
      if (response.data.success) {
        const validSites = response.data.valid_sites || [];
        const filtered = response.data.filtered || 0;

        // Update sites count and input with valid sites only
        setAllChannels(allChannels.map(ch =>
          ch.channel_id === editingBlackListChannel.channel_id
            ? { ...ch, sites_count: validSites.length }
            : ch
        ));

        // Show message if some URLs were filtered
        if (filtered > 0) {
          setBlackListInput(validSites.join('\n'));
          setOriginalBlackListInput(validSites.join('\n'));
          setMessageModalText(t('edit.someUrlsInvalid'));
          setMessageModalType('info');
          setShowMessageModal(true);
        } else {
          // Close editor if all URLs were valid
          setEditingBlackListChannel(null);
          setBlackListInput('');
          setOriginalBlackListInput('');
        }
      }
    } catch (err: any) {
      console.error('Failed to update channel:', err);
      setMessageModalText(t('edit.failedSaveUrlChanges'));
      setMessageModalType('error');
      setShowMessageModal(true);
    } finally {
      setSavingBlackList(false);
    }
  };

  const handleCreateBlackList = async (listName: string) => {
    if (!listName.trim() || !campaign?.advertiser_id) return;
    const sites = blackListInput.split('\n').filter(line => line.trim());
    setSavingBlackList(true);
    try {
      const response = await api.post(`/api/channels/create`, {
        advertiser_id: campaign.advertiser_id,
        display_name: listName.trim(),
        sites: sites
      });
      if (response.data.success) {
        // A large list is split into several channels server-side — attach ALL
        // of them, not just the first, else only part 1/N gets excluded.
        const ids: string[] = (response.data.channel_ids && response.data.channel_ids.length)
          ? response.data.channel_ids
          : [response.data.id];
        await fetchChannels(campaign.advertiser_id);  // pull in every created part
        setBlackListChannelIds([...blackListChannelIds, ...ids]);
        setNewBlackListName('');
        setShowBlackListSaveModal(false);
        setIsCreatingBlackList(false);
        setBlackListInput('');
      }
    } catch (err: any) {
      console.error('Failed to create channel:', err);
      const errorMessage = extractErrorMessage(err, t('edit.failedCreateListRetry'));
      alert(errorMessage);
    } finally {
      setSavingBlackList(false);
    }
  };

  const handleRenameBlackListChannel = async () => {
    if (!editingBlackListChannel || !campaign?.advertiser_id || !editingBlackListChannelName.trim()) {
      setIsEditingBlackListChannelName(false);
      return;
    }
    if (editingBlackListChannelName === editingBlackListChannel.name) {
      setIsEditingBlackListChannelName(false);
      return;
    }
    setIsRenamingBlackListChannel(true);
    try {
      const response = await api.patch(
        `/api/channels/${editingBlackListChannel.channel_id}?advertiser_id=${campaign.advertiser_id}`,
        { display_name: editingBlackListChannelName.trim() }
      );
      if (response.data.success) {
        setAllChannels(allChannels.map(ch =>
          ch.channel_id === editingBlackListChannel.channel_id
            ? { ...ch, name: editingBlackListChannelName.trim() }
            : ch
        ));
        setEditingBlackListChannel({ ...editingBlackListChannel, name: editingBlackListChannelName.trim() });
      }
    } catch (err) {
      console.error('Failed to rename channel:', err);
      alert(t('edit.failedRenameListRetry'));
      setEditingBlackListChannelName(editingBlackListChannel.name);
    } finally {
      setIsRenamingBlackListChannel(false);
      setIsEditingBlackListChannelName(false);
    }
  };

  const handleDeleteBlackListChannel = async () => {
    if (!editingBlackListChannel || !campaign?.advertiser_id) return;
    setIsDeletingBlackListChannel(true);
    try {
      const response = await api.delete(
        `/api/channels/${editingBlackListChannel.channel_id}?advertiser_id=${campaign.advertiser_id}`
      );
      if (response.data.success) {
        setAllChannels(allChannels.filter(ch => ch.channel_id !== editingBlackListChannel.channel_id));
        setWhiteListChannelIds(whiteListChannelIds.filter(id => id !== editingBlackListChannel.channel_id));
        setBlackListChannelIds(blackListChannelIds.filter(id => id !== editingBlackListChannel.channel_id));
        setEditingBlackListChannel(null);
        setBlackListInput('');
        setOriginalBlackListInput('');
        setShowDeleteBlackListConfirm(false);
      }
    } catch (err) {
      console.error('Failed to delete channel:', err);
      alert(t('edit.failedDeleteListRetry'));
    } finally {
      setIsDeletingBlackListChannel(false);
    }
  };

  // Negative Keyword List handlers
  const handleSelectNegKwList = async (list: NegativeKeywordList) => {
    // Toggle: if same list is already open, close it
    if (editingNegKwList?.id === list.id) {
      setEditingNegKwList(null);
      setNegKwListInput('');
      setOriginalNegKwListInput('');
      setEditingNegKwListName('');
      setIsEditingNegKwListName(false);
      return;
    }
    setEditingNegKwList(list);
    setEditingNegKwListName(list.name);
    setIsEditingNegKwListName(false);
    setIsCreatingNegKwList(false);
    setShowNegKwListSaveModal(false);
    setNewNegKwListName('');

    // Fetch keywords for this list from API
    if (campaign?.advertiser_id) {
      try {
        const response = await api.get(
          `/api/negative-keyword-lists/${list.id}/keywords?advertiser_id=${campaign.advertiser_id}`
        );
        const keywordsText = (response.data.keywords || []).join('\n');
        setNegKwListInput(keywordsText);
        setOriginalNegKwListInput(keywordsText);
      } catch (err) {
        console.error('Failed to fetch keywords:', err);
        setNegKwListInput('');
        setOriginalNegKwListInput('');
      }
    }
  };

  const handleNewNegKwList = () => {
    setEditingNegKwList(null);
    setNegKwListInput('');
    setOriginalNegKwListInput('');
    setShowNegKwListSaveModal(false);
    setNewNegKwListName('');
    setEditingNegKwListName('');
    setIsCreatingNegKwList(true);
  };

  const handleRenameNegKwList = async () => {
    if (!editingNegKwList || !campaign?.advertiser_id || !editingNegKwListName.trim()) return;
    if (editingNegKwListName === editingNegKwList.name) return;

    setIsRenamingNegKwList(true);
    try {
      const response = await api.patch(
        `/api/negative-keyword-lists/${editingNegKwList.id}?advertiser_id=${campaign.advertiser_id}`,
        { display_name: editingNegKwListName.trim() }
      );

      if (response.data.success) {
        // Update in local state
        setAllNegativeKeywordLists(allNegativeKeywordLists.map(list =>
          list.id === editingNegKwList.id
            ? { ...list, name: editingNegKwListName.trim() }
            : list
        ));
        setEditingNegKwList({ ...editingNegKwList, name: editingNegKwListName.trim() });
      }
    } catch (err) {
      console.error('Failed to rename list:', err);
      alert(t('edit.failedRenameListRetry'));
      setEditingNegKwListName(editingNegKwList.name);
    } finally {
      setIsRenamingNegKwList(false);
    }
  };

  const [isDeletingNegKwList, setIsDeletingNegKwList] = useState(false);
  const [showDeleteNegKwListConfirm, setShowDeleteNegKwListConfirm] = useState(false);

  const handleDeleteNegKwList = async () => {
    if (!editingNegKwList || !campaign?.advertiser_id) return;

    setIsDeletingNegKwList(true);
    try {
      const response = await api.delete(
        `/api/negative-keyword-lists/${editingNegKwList.id}?advertiser_id=${campaign.advertiser_id}`
      );

      if (response.data.success) {
        // Remove from local state
        setAllNegativeKeywordLists(allNegativeKeywordLists.filter(list => list.id !== editingNegKwList.id));
        setNegativeKeywordListIds(negativeKeywordListIds.filter(id => id !== editingNegKwList.id));
        // Close editor and modal
        setEditingNegKwList(null);
        setNegKwListInput('');
        setOriginalNegKwListInput('');
        setEditingNegKwListName('');
        setShowDeleteNegKwListConfirm(false);
      }
    } catch (err) {
      console.error('Failed to delete list:', err);
      alert(t('edit.failedDeleteListRetry'));
    } finally {
      setIsDeletingNegKwList(false);
    }
  };

  const hasNegKwListChanges = () => negKwListInput !== originalNegKwListInput;

  const [savingNegKwList, setSavingNegKwList] = useState(false);

  const handleSaveNewNegKwList = async (listName: string) => {
    if (!listName.trim() || !campaign?.advertiser_id) return;

    const keywords = negKwListInput.split('\n').filter(line => line.trim());

    setSavingNegKwList(true);
    try {
      const response = await api.post(`/api/negative-keyword-lists/create`, {
        advertiser_id: campaign.advertiser_id,
        display_name: listName.trim(),
        keywords: keywords
      });

      if (response.data.success) {
        // Add new list to local state
        const newList: NegativeKeywordList = {
          id: response.data.id,
          name: response.data.display_name,
          targeted_count: 0,
          keywords_count: response.data.keywords_count || keywords.length
        };
        setAllNegativeKeywordLists([...allNegativeKeywordLists, newList]);

        // Auto-select the new list (if not at max)
        if (negativeKeywordListIds.length < 4) {
          setNegativeKeywordListIds([...negativeKeywordListIds, response.data.id]);
        }

        // Reset editor
        setNewNegKwListName('');
        setShowNegKwListSaveModal(false);
        setIsCreatingNegKwList(false);
        setNegKwListInput('');
      }
    } catch (err) {
      console.error('Failed to create negative keyword list:', err);
      alert(t('edit.failedCreateListRetry'));
    } finally {
      setSavingNegKwList(false);
    }
  };

  const handleUpdateNegKwList = async () => {
    if (!editingNegKwList || !campaign?.advertiser_id) return;

    const keywords = negKwListInput.split('\n').filter(line => line.trim());

    setSavingNegKwList(true);
    try {
      const response = await api.put(
        `/api/negative-keyword-lists/${editingNegKwList.id}/keywords?advertiser_id=${campaign.advertiser_id}`,
        { keywords }
      );

      if (response.data.success) {
        // Close editor after successful save
        setEditingNegKwList(null);
        setNegKwListInput('');
        setOriginalNegKwListInput('');
      }
    } catch (err) {
      console.error('Failed to update negative keyword list:', err);
      alert(t('edit.failedUpdateListRetry'));
    } finally {
      setSavingNegKwList(false);
    }
  };

  // Get channels for display
  const getWhiteListChannels = () => allChannels.filter(c => whiteListChannelIds.includes(c.channel_id));
  const getBlackListChannels = () => allChannels.filter(c => blackListChannelIds.includes(c.channel_id));

  const handleSelectGeoExcludedResult = (result: GeoSearchResult) => {
    // Check if already exists by name
    if (!targetingGeoExcluded.some(loc => loc.name === result.name)) {
      const newLocation: GeoLocation = {
        id: result.id,
        name: result.name,
        type: result.type
      };
      setTargetingGeoExcluded([...targetingGeoExcluded, newLocation]);
      // Remove from Included if exists (can't be in both lists)
      setTargetingGeo(targetingGeo.filter(loc => loc.name !== result.name));
    }
    setGeoExcludedInputValue('');
    setGeoExcludedSearchResults([]);
    setShowGeoExcludedInput(false);
  };

  if (isCreateMode && isAllClients) {
    return <ChooseClientPlaceholder pageKey="nav.campaignCreation" />;
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">{isCreateMode ? t('edit.loading') : t('edit.loadingCampaign')}</div>
      </div>
    );
  }

  if (error || (!isCreateMode && !campaign)) {
    return (
      <div className="page-container">
        <div className="error-state">
          {error || t('edit.campaignNotFound')}
          <button onClick={() => navigateToCampaigns()} className="btn-secondary-sm" style={{ marginTop: '16px' }}>
            {t('edit.backToCampaigns')}
          </button>
        </div>
      </div>
    );
  }

  if (isCreateMode && advertisers.length === 0) {
    return (
      <div className="editor-empty" style={{ minHeight: 'calc(100vh - 80px)' }}>
        <div className="editor-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
          </svg>
        </div>
        <h3>{t('edit.noClients')}</h3>
        <p>{t('edit.addClientFirstPre')} <a href="/clients" style={{ color: 'var(--accent-orange)', textDecoration: 'none', fontWeight: 500 }}>{t('edit.clientsLink')}</a> {t('edit.addClientFirstPost')}</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Back Link */}
      <a href="#" className="back-link" onClick={(e) => { e.preventDefault(); navigateToCampaigns(); }}>
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
        </svg>
        {t('edit.backToCampaigns')}
      </a>

      {/* Header */}
      <header className="edit-header">
        <div className="header-title">
          <h1>{isCreateMode ? t('edit.newCampaign') : t('edit.editCampaign')}</h1>
          <p className="header-subtitle">
            {isCreateMode ? t('edit.createSubtitle') : cleanName(campaign?.name)}
            {isDraft && !isCreateMode && <span className="draft-badge">{t('edit.draft')}</span>}
          </p>
        </div>
      </header>

      <div className="edit-layout">
        {/* Left Panel - Campaign Form + Ad Groups List */}
        <aside className="campaign-panel">
          {/* Campaign Details */}
          <div className={`panel-section campaign-details-section ${savingCampaign ? 'saving' : ''}`}>
            <div className="section-header">
              <h2 className="section-title">
                {isLocked && (
                  <span title={t('edit.legacyReadOnly')} style={{ marginRight: 6, color: '#888' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                    </svg>
                  </span>
                )}
                {t('edit.campaignDetails')}
              </h2>
              {!isLocked && (
              <div className="form-actions">
                <button
                  className="btn-icon"
                  title={t('edit.duplicateCampaign')}
                  onClick={handleDuplicateCampaign}
                  disabled={isDuplicatingCampaign}
                >
                  {isDuplicatingCampaign ? (
                    <svg className="spinner" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.4" strokeDashoffset="10"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                  )}
                </button>
                <button
                  className="btn-icon danger"
                  title={t(isDraft ? 'edit.deleteDraftCampaign' : 'edit.archiveCampaign')}
                  onClick={() => setShowDeleteModal(true)}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
              </div>
              )}
            </div>

            {/* Client — read-only, from sidebar selection */}
            {isCreateMode && contextAdvertiser && contextAdvertiser.advertiser_id !== '__all__' && (
              <div className="form-field">
                <label className="field-label">{t('edit.client')}</label>
                <input type="text" className="field-input" value={contextAdvertiser.display_name} disabled style={{ background: 'var(--bg-tertiary, #f1f3f5)', color: 'var(--text-primary)' }} />
              </div>
            )}

            <div className="form-field">
              <label className="field-label">{t('edit.campaignName')}</label>
              <input
                type="text"
                className="field-input"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label">{t('edit.startDate')}</label>
                {(() => {
                  const todayDate = getTodayDate();
                  // For new campaigns (isCreateMode) or draft campaigns: allow editing
                  // For published campaigns that have started: disable
                  const campaignStartedAlready = !isCreateMode && !isDraft && campaign?.start_date && campaign.start_date < todayDate;
                  return (
                    <span
                      className="input-wrapper"
                      title={campaignStartedAlready ? t('edit.cannotChangeStartCampaign') : undefined}
                    >
                      <input
                        type="date"
                        className={`field-input ${campaignStartedAlready ? 'disabled' : ''}`}
                        value={startDate}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          // For editable fields (new or draft), don't allow dates in the past
                          if (!campaignStartedAlready && newDate && newDate < todayDate) {
                            return; // Ignore past dates
                          }
                          setStartDate(newDate);
                          // Also check end date - it should not be before start date
                          if (newDate && endDate && endDate < newDate) {
                            setEndDate(newDate);
                          }
                        }}
                        disabled={!!campaignStartedAlready}
                        min={!campaignStartedAlready ? todayDate : undefined}
                      />
                    </span>
                  );
                })()}
              </div>
              <div className="form-field">
                <label className="field-label">{t('edit.endDate')}</label>
                <input
                  type="date"
                  className="field-input"
                  value={endDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const minDate = startDate || getTodayDate();
                    // Don't allow end date before start date (or today)
                    if (newDate && newDate < minDate) {
                      return; // Ignore invalid dates
                    }
                    setEndDate(newDate);
                  }}
                  min={startDate || getTodayDate()}
                />
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">{t('edit.frequencyLimit')}</label>
              <div className="frequency-input">
                <input
                  type="text"
                  inputMode="numeric"
                  className="field-input frequency-count"
                  value={frequencyCount}
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value, false);
                    setFrequencyCount(sanitized ? parseInt(sanitized) : '');
                  }}
                  placeholder=""
                />
                <span className="frequency-text">{t('edit.impressionsPer')}</span>
                <select
                  className="field-select frequency-period"
                  value={frequencyPeriod}
                  onChange={(e) => setFrequencyPeriod(e.target.value)}
                >
                  <option value="day">{t('edit.day')}</option>
                  <option value="week">{t('edit.week')}</option>
                  <option value="month">{t('edit.month')}</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">{t('edit.status')}</label>
              <div className="status-toggle">
                {campaignStatus === 'ended' ? (
                  <button className="status-btn active ended" disabled>
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
                    {t('edit.ended')}
                  </button>
                ) : (
                  <>
                    <button
                      className={`status-btn ${campaignStatus === 'active' ? 'active' : ''}`}
                      onClick={() => setCampaignStatus('active')}
                    >
                      <span className="status-dot live"></span>
                      {t('edit.active')}
                    </button>
                    <button
                      className={`status-btn ${campaignStatus === 'paused' ? 'active' : ''}`}
                      onClick={() => setCampaignStatus('paused')}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                      {t('edit.paused')}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Campaign Actions */}
            <div className="form-actions-bottom">
              <button className="btn-secondary-sm" onClick={() => navigateToCampaigns()}>{t('edit.cancel')}</button>
              {!isAnalyst && !isLocked && isDraft && !isCreateMode && (
                <button
                  className={`btn-publish ${isPublishing ? 'publishing' : ''} ${publishSuccess ? 'success' : ''}`}
                  onClick={handlePublishCampaign}
                  disabled={isPublishing || publishSuccess}
                >
                  {isPublishing ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('edit.publishing')}
                    </>
                  ) : publishSuccess ? (
                    <>
                      <svg className="btn-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      {t('edit.published')}
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      {t('edit.publish')}
                    </>
                  )}
                </button>
              )}
              {!isAnalyst && !isLocked && (
              <button
                className={`btn-primary-sm save-campaign-btn ${savingCampaign ? 'saving' : ''} ${campaignSaved ? 'saved' : ''} ${!hasCampaignChanges() && !savingCampaign && !campaignSaved ? 'inactive' : ''}`}
                onClick={handleSaveCampaign}
                disabled={savingCampaign || campaignSaved || !hasCampaignChanges()}
              >
                {savingCampaign ? (
                  <>
                    <span className="btn-spinner"></span>
                    {isCreateMode ? t('edit.creating') : t('edit.saving')}
                  </>
                ) : campaignSaved ? (
                  <>
                    <svg className="btn-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {isCreateMode ? t('edit.created') : t('edit.saved')}
                  </>
                ) : (
                  isCreateMode ? t('edit.createCampaign') : (isDraft && !isCreateMode ? t('edit.save') : t('edit.saveCampaign'))
                )}
              </button>
              )}
            </div>
          </div>

          {/* Ad Groups List - only show when not in create mode */}
          {!isCreateMode && (
          <div className="panel-section">
            <div className="section-header">
              <h2 className="section-title">{t('edit.adGroups')}</h2>
              {!isLocked && (
              <button className="btn-add" onClick={() => handleAddGroup()}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                {t('edit.addGroup')}
              </button>
              )}
            </div>

            <div className="ad-groups-list">
              {adGroups.map((adGroup) => {
                const status = getAdGroupStatus(adGroup);
                const isExternal = adGroup.source === 'google' || adGroup.source === 'meta';
                return (
                  <div
                    key={adGroup.id}
                    className={`ad-group-item ${status !== 'active' ? status : ''} ${selectedAdGroup?.id === adGroup.id ? 'active' : ''} ${(adGroup.targeting_push_pending || adGroup.push_pending) ? 'pending' : ''}`}
                    onClick={() => {
                      if (isExternal) return;
                      if (adGroup.push_pending || adGroup.targeting_push_pending) return;
                      handleAdGroupSelect(adGroup);
                    }}
                    style={
                      isExternal
                        ? { opacity: 0.55, cursor: 'default' }
                        : (adGroup.targeting_push_pending || adGroup.push_pending)
                          ? { opacity: 0.6, cursor: 'not-allowed' }
                          : undefined
                    }
                  >
                    {/* Show clock icon when publishing in background */}
                    {(adGroup.push_pending || adGroup.targeting_push_pending) ? (
                      <svg className="status-icon pending-clock" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#d4a853' }}>
                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                      </svg>
                    ) : status === 'active' ? (
                      <span className="status-dot live"></span>
                    ) : status === 'scheduled' ? (
                      <svg className="status-icon scheduled" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/>
                      </svg>
                    ) : status === 'paused' ? (
                      <svg className="status-icon paused" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      </svg>
                    ) : (
                      <svg className="status-icon ended" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                      </svg>
                    )}
                    <div className="ad-group-info">
                      <div className="ad-group-name">
                        {cleanName(adGroup.name)}
                        {!adGroup.ads_io_id && !adGroup.push_pending && <span className="draft-badge">{t('edit.draft')}</span>}
                        {adGroup.push_pending && <span className="draft-badge" style={{ background: '#d4a853' }}>{t('edit.publishing')}</span>}
                      </div>
                      {!isExternal && (
                        <div className="ad-group-meta">{tv(adGroup.type)} &bull; {t('campaigns.creativesCount', { count: adGroup.creative_count })}</div>
                      )}
                    </div>
                    {!isExternal && (
                      <svg className="chevron" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                      </svg>
                    )}
                  </div>
                );
              })}
              {adGroups.length === 0 && (
                <div className="empty-list-message">{t('edit.noAdGroups')}</div>
              )}
            </div>

            {/* Publish All button - only show when there are draft groups that are not already publishing */}
            {!isAnalyst && adGroups.some(ag => !ag.ads_io_id && !ag.push_pending) && (() => {
              const publishableCount = publishableDraftGroups.length;
              const hasPublishable = publishableCount > 0;
              const isDisabled = publishingAllGroups || isDraft || !hasPublishable;
              const tooltipText = isDraft
                ? t('edit.publishFirst')
                : !hasPublishable
                  ? t('edit.draftsMissing')
                  : '';
              return (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <span className={`tooltip-wrapper ${tooltipText ? 'has-tooltip' : ''}`} data-tooltip={tooltipText}>
                    <button
                      className={`btn-publish-all ${isDraft ? 'campaign-not-published' : ''} ${!hasPublishable && !isDraft ? 'fields-missing' : ''}`}
                      onClick={() => !isDisabled && handlePublishAllGroups()}
                      disabled={isDisabled}
                    >
                      {publishingAllGroups ? (
                        <>
                          <span className="btn-loading"></span>
                          {t('edit.publishing')}
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                          Publish All ({publishableCount})
                        </>
                      )}
                    </button>
                  </span>
                </div>
              );
            })()}
          </div>
          )}

          {/* Create mode: show message that groups can be added after saving */}
          {isCreateMode && (
            <div className="panel-section">
              <div className="create-mode-notice">
                <svg viewBox="0 0 24 24" fill="currentColor" className="notice-icon">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
                <div className="notice-text">
                  <div className="notice-title">{t('edit.adGroups')}</div>
                  <div className="notice-description">{t('edit.saveFirstToAdd')}</div>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Right Panel - Ad Group Edit Form */}
        {isCreateMode ? (
          <div className="adgroup-panel-empty">
            <div className="empty-state">
              <svg className="empty-state-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              <div className="empty-state-title">{t('edit.createCampaignFirst')}</div>
              <div className="empty-state-text">{t('edit.createCampaignFirstHint')}</div>
            </div>
          </div>
        ) : !selectedAdGroup ? (
          <div className="adgroup-panel-empty">
            <div className="empty-state">
              <svg className="empty-state-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
              </svg>
              <div className="empty-state-title">{t('edit.selectAdGroup')}</div>
              <div className="empty-state-text">{t('edit.selectAdGroupHint')}</div>
            </div>
          </div>
        ) : (
          <div className="adgroup-panel">
            <div className={`adgroup-form ${savingAdGroup ? 'saving' : ''}`}>
              <div className="form-header">
                <h2 className="form-title">{selectedAdGroup.id === '' ? t('edit.newAdGroup') : t('edit.editAdGroup')}</h2>
                <div className="form-actions">
                  {selectedAdGroup.id !== '' && (
                    <>
                      {!isAnalyst && !selectedAdGroup.ads_io_id && !selectedAdGroup.push_pending && (() => {
                        // Use saved group data for validation, not form state
                        const validation = getSavedGroupPublishValidation(selectedAdGroup);
                        const isDisabled = publishingGroup || isDraft || !validation.canPublish;
                        const tooltipText = isDraft
                          ? t('edit.publishFirst')
                          : !validation.canPublish
                            ? `${t('edit.requiredFields')} ${validation.missingFields.join(', ')}`
                            : '';
                        return (
                          <span className={`tooltip-wrapper ${tooltipText ? 'has-tooltip' : ''}`} data-tooltip={tooltipText}>
                            <button
                              className={`btn-publish ${isDraft ? 'campaign-not-published' : ''} ${!validation.canPublish && !isDraft ? 'fields-missing' : ''}`}
                              onClick={() => !isDisabled && handlePublishGroup(selectedAdGroup)}
                              disabled={isDisabled}
                            >
                              {publishingGroup ? (
                                <span className="btn-loading"></span>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                </svg>
                              )}
                              Publish
                            </button>
                          </span>
                        );
                      })()}
                      {/* Pending indicator - shows when group is being published or targeting is being pushed */}
                      {(selectedAdGroup.push_pending || (selectedAdGroup.ads_io_id && selectedAdGroup.targeting_push_pending)) && (
                        <span className="targeting-pending-indicator" title={t(selectedAdGroup.ads_io_id ? 'edit.syncingTargeting' : 'edit.publishing')}>
                          <svg viewBox="0 0 24 24" fill="currentColor" className="pending-clock-icon">
                            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                          </svg>
                        </span>
                      )}
                      <button
                        className="btn-icon"
                        title={t('edit.duplicate')}
                        disabled={isDuplicatingGroup}
                        onClick={() => handleDuplicateGroup(selectedAdGroup)}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                        </svg>
                      </button>
                      <button
                        className="btn-icon danger"
                        title={selectedAdGroup.ads_io_id ? "Archive" : "Delete"}
                        onClick={() => {
                          setGroupToDelete(selectedAdGroup);
                          setShowDeleteGroupModal(true);
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    </>
                  )}
                  <button className="btn-icon" title={t('edit.close')} onClick={handleCloseAdGroup}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Basic Info */}
              <div className="form-section">
                <div className="form-field">
                  <label className="field-label">
                    {t('edit.adGroupName')}
                    {isDraft && <span className="draft-badge">{t('edit.draft')}</span>}
                  </label>
                  <input
                    type="text"
                    className={`field-input ${!adGroupName.trim() && !selectedAdGroup.ads_io_id ? 'required-empty' : ''}`}
                    value={adGroupName}
                    onChange={(e) => setAdGroupName(e.target.value)}
                    autoFocus={selectedAdGroup.id === ''}
                    placeholder={selectedAdGroup.id === '' ? t('edit.adGroupNamePlaceholder') : ''}
                  />
                </div>

                <div className="form-field">
                  <label className="field-label">{t('edit.placementType')}</label>
                  <div className={`line-item-type-toggle ${selectedAdGroup.id !== '' && !!selectedAdGroup.ads_io_id ? 'disabled' : ''}`}>
                    <button
                      className={`line-item-type-btn ${adGroupLineItemType === 'display' ? 'active' : ''}`}
                      onClick={() => handlePlacementTypeChange('display')}
                      disabled={selectedAdGroup.id !== '' && !!selectedAdGroup.ads_io_id}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="14" rx="2"/>
                        <path d="M8 21h8M12 17v4"/>
                      </svg>
                      {t('edit.display')}
                    </button>
                    <button
                      className={`line-item-type-btn ${adGroupLineItemType === 'video' ? 'active' : ''}`}
                      onClick={() => handlePlacementTypeChange('video')}
                      disabled={selectedAdGroup.id !== '' && !!selectedAdGroup.ads_io_id}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                      </svg>
                      {t('edit.video')}
                    </button>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="field-label">{t('edit.startDate')}</label>
                    {(() => {
                      const todayAdGroup = new Date().toISOString().split('T')[0];
                      // Draft groups (no ads_io_id) and new groups (empty id) should always allow date editing
                      const isDraftOrNewGroup = !selectedAdGroup?.ads_io_id || selectedAdGroup?.id === '';
                      const adGroupStartedAlready = !isDraftOrNewGroup && selectedAdGroup?.start_date && selectedAdGroup.start_date < todayAdGroup;
                      return (
                        <span
                          className="input-wrapper"
                          title={adGroupStartedAlready ? t('edit.cannotChangeStartGroup') : undefined}
                        >
                          <input
                            type="date"
                            className={`field-input ${adGroupStartedAlready ? 'disabled' : ''} ${!adGroupStartDate && !selectedAdGroup.ads_io_id ? 'required-empty' : ''}`}
                            value={adGroupStartDate}
                            onChange={(e) => {
                              const newDate = e.target.value;
                              // For editable fields, don't allow dates in the past
                              if (!adGroupStartedAlready && newDate < todayAdGroup) {
                                return; // Ignore past dates
                              }
                              setAdGroupStartDate(newDate);
                              // If start date is later than end date, update end date
                              if (newDate && adGroupEndDate && newDate > adGroupEndDate) {
                                setAdGroupEndDate(newDate);
                              }
                            }}
                            disabled={!!adGroupStartedAlready}
                            min={!adGroupStartedAlready ? todayAdGroup : undefined}
                          />
                        </span>
                      );
                    })()}
                  </div>
                  <div className="form-field">
                    <label className="field-label">{t('edit.endDate')}</label>
                    <input
                      type="date"
                      className={`field-input ${!adGroupEndDate && !selectedAdGroup.ads_io_id ? 'required-empty' : ''}`}
                      value={adGroupEndDate}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        const minDate = adGroupStartDate || new Date().toISOString().split('T')[0];
                        // Don't allow end date before start date (or today)
                        if (newDate < minDate) {
                          return; // Ignore invalid dates
                        }
                        setAdGroupEndDate(newDate);
                      }}
                      min={adGroupStartDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label className="field-label">{t('edit.status')}</label>
                  <div className="status-toggle">
                    {adGroupStatus === 'ended' ? (
                      <button className="status-btn active ended" disabled>
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
                        {t('edit.ended')}
                      </button>
                    ) : (
                      <>
                        <button
                          className={`status-btn ${adGroupStatus === 'active' ? 'active' : ''} ${campaignStatus === 'paused' ? 'disabled' : ''}`}
                          onClick={() => campaignStatus !== 'paused' && setAdGroupStatus('active')}
                          disabled={campaignStatus === 'paused'}
                          title={campaignStatus === 'paused' ? t('edit.cannotSetActive') : undefined}
                        >
                          <span className="status-dot live"></span>
                          {t('edit.active')}
                        </button>
                        <button
                          className={`status-btn ${adGroupStatus === 'paused' ? 'active' : ''}`}
                          onClick={() => setAdGroupStatus('paused')}
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                          {t('edit.paused')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Budget & Bidding */}
              <div className="form-section">
                <div className="form-row form-row-3">
                  <div className="form-field">
                    <label className="field-label">{t('edit.budget')}</label>
                    <div className={`input-with-prefix ${(!adGroupBudget || adGroupBudget === '0') && !selectedAdGroup.ads_io_id ? 'required-empty' : ''}`}>
                      <span className="input-prefix">{currency}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="field-input budget-input"
                        value={adGroupBudget}
                        onChange={(e) => setAdGroupBudget(sanitizeNumericInput(e.target.value))}
                      />
                    </div>
                    {user?.role === 'admin' && currency !== 'EUR' && adGroupBudget && Number(adGroupBudget) > 0 && (
                      <div className="field-hint" style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                        ~ {(Number(adGroupBudget) / currencyRate).toFixed(2)} EUR
                      </div>
                    )}
                  </div>
                  <div className="form-field">
                    <label className="field-label">{t('edit.pricing')}</label>
                    <select
                      className="field-select"
                      value={adGroupBidMode}
                      onChange={(e) => setAdGroupBidMode(e.target.value as 'target' | 'fixed')}
                    >
                      <option value="target">{t('edit.targetPrice')}</option>
                      <option value="fixed">{t('edit.fixedPrice')}</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="field-label">{t('edit.bidStrategy')}</label>
                    <select
                      className="field-select"
                      value={adGroupBidStrategy}
                      onChange={(e) => {
                        setAdGroupBidStrategy(e.target.value);
                        // Clear selected goals when switching away from CPA
                        if (e.target.value !== 'CPA') {
                          setSelectedGoalIds([]);
                        }
                        // Disable Look-Alike when switching to incompatible strategies
                        if (e.target.value === 'AV_VIEWED' || e.target.value === 'CIVA') {
                          setEnableLookAlike(false);
                        }
                      }}
                    >
                      <option value="AV_VIEWED">{t('edit.maxViewable')}</option>
                      <option value="CPC">{t('edit.maxClicks')}</option>
                      <option value="CPA" disabled={conversionGoals.length === 0}>
                        {conversionGoals.length === 0 ? t('edit.maxConvNoGoals') : t('edit.maxConvCpa')}
                      </option>
                      {adGroupLineItemType === 'video' && (
                        <option value="CIVA">{t('edit.maxVideoViews')}</option>
                      )}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="field-label">
                      {adGroupBidMode === 'fixed' ? t('edit.fixed') : t('edit.targetWord')} {adGroupBidStrategy === 'AV_VIEWED' ? 'CPM' : adGroupBidStrategy === 'CPC' ? 'CPC' : adGroupBidStrategy === 'CPA' ? 'CPA' : 'CPV'}
                    </label>
                    <div className={`input-with-prefix ${(!adGroupKpiAmount || adGroupKpiAmount === '0') && !selectedAdGroup.ads_io_id ? 'required-empty' : ''}`}>
                      <span className="input-prefix">{currency}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="field-input"
                        value={adGroupKpiAmount}
                        onChange={(e) => setAdGroupKpiAmount(sanitizeNumericInput(e.target.value))}
                        placeholder="0.00"
                      />
                    </div>
                    {user?.role === 'admin' && currency !== 'EUR' && adGroupKpiAmount && Number(adGroupKpiAmount) > 0 && (
                      <div className="field-hint" style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                        ~ {(Number(adGroupKpiAmount) / currencyRate).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} EUR
                      </div>
                    )}
                  </div>
                </div>

                {/* Conversion Goals Selector (shown when CPA strategy is selected) */}
                {adGroupBidStrategy === 'CPA' && conversionGoals.length > 0 && (
                  <div className={`conversion-goals-row ${selectedGoalIds.length === 0 ? 'error' : ''}`}>
                    <span className="goals-label">{t('edit.optimizeFor')}</span>
                    <div className="goals-list">
                      {conversionGoals.map(goal => (
                        <span
                          key={goal.id}
                          className={`goal-chip ${selectedGoalIds.includes(goal.id) ? 'selected' : ''}`}
                          onClick={() => {
                            if (selectedGoalIds.includes(goal.id)) {
                              setSelectedGoalIds(selectedGoalIds.filter(id => id !== goal.id));
                            } else {
                              setSelectedGoalIds([...selectedGoalIds, goal.id]);
                            }
                          }}
                        >
                          <span className="goal-check"></span>
                          {goal.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Frequency Cap + Daily Max */}
                <div className="form-row-flex">
                  <div className="form-field form-field-auto">
                    <label className="field-label">{t('edit.frequencyLimit')}</label>
                    <div className="frequency-input">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="field-input frequency-count"
                        value={adGroupFrequencyCount}
                        onChange={(e) => {
                          const sanitized = sanitizeNumericInput(e.target.value, false);
                          setAdGroupFrequencyCount(sanitized ? parseInt(sanitized) : '');
                        }}
                        placeholder=""
                      />
                      <span className="frequency-text">{t('edit.impressionsPer')}</span>
                      <select
                        className="field-select frequency-period"
                        value={adGroupFrequencyPeriod}
                        onChange={(e) => setAdGroupFrequencyPeriod(e.target.value)}
                      >
                        <option value="day">{t('edit.day')}</option>
                        <option value="week">{t('edit.week')}</option>
                        <option value="month">{t('edit.month')}</option>
                        <option value="lifetime">{t('edit.lifetime')}</option>
                      </select>
                    </div>
                  </div>

                  <div className={`form-field ${adGroupMediaPlanType === 'budget' ? 'form-field-grow' : ''}`} style={{ minWidth: '120px' }}>
                    <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {t('edit.planning')}
                      <span className="planning-help-icon" title="">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="7" fill="#bbb"/>
                          <text x="8" y="11.5" textAnchor="middle" fontSize="10" fontWeight="600" fill="#fff">?</text>
                        </svg>
                        <div className="planning-tooltip">
                          <div className="planning-tooltip-item">
                            <strong>{t('edit.budget')}</strong>
                            <span>Spend the budget freely. Impressions and clicks are not limited by the system.</span>
                          </div>
                          <div className="planning-tooltip-item">
                            <strong>{t('edit.impressionsClicks')}</strong>
                            <span>{t('edit.planHint1')}<br/>{t('edit.planHint2')}</span>
                          </div>
                        </div>
                      </span>
                    </label>
                    <select
                      className="field-select"
                      value={adGroupMediaPlanType}
                      onChange={(e) => {
                        const newType = e.target.value as 'budget' | 'impressions' | 'clicks';
                        setAdGroupMediaPlanType(newType);
                        if (newType === 'budget') {
                          setAdGroupPlanTarget('');
                        }
                      }}
                    >
                      <option value="budget">{t('edit.budget')}</option>
                      <option value="impressions">{t('edit.impressions')}</option>
                      <option value="clicks">{t('edit.clicks')}</option>
                    </select>
                  </div>
                  {adGroupMediaPlanType !== 'budget' && (
                    <div className="form-field form-field-grow">
                      <label className="field-label">{t('edit.planned')}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="field-input"
                        style={!adGroupPlanTarget ? { borderColor: '#e74c3c' } : undefined}
                        placeholder={adGroupMediaPlanType === 'impressions' ? t('edit.totalImpressions') : t('edit.totalClicks')}
                        value={adGroupPlanTarget === '' ? '' : adGroupPlanTarget.toLocaleString('ru-RU')}
                        onChange={(e) => {
                          const sanitized = sanitizeNumericInput(e.target.value, false);
                          setAdGroupPlanTarget(sanitized ? parseInt(sanitized) : '');
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Targeting */}
              <div className="form-section">
                <div className="form-section-head">
                  <h3 className="form-section-title">{t('edit.targeting')}</h3>
                  {/* Templates act on the whole targeting block, so they sit in
                      its header rather than next to any one field. */}
                  {selectedAdGroup?.id && (
                    <div className="tpl-bar">
                      <div className="tpl-picker">
                        <button
                          type="button"
                          className="tpl-btn"
                          disabled={templateBusy}
                          onClick={() => {
                            if (!showTemplateMenu) loadTargetingTemplates(templateAdvertiserId);
                            setShowTemplateMenu((v) => !v);
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <rect x="3" y="3" width="7" height="7" rx="1.5" />
                            <rect x="14" y="3" width="7" height="7" rx="1.5" />
                            <rect x="3" y="14" width="7" height="7" rx="1.5" />
                            <rect x="14" y="14" width="7" height="7" rx="1.5" />
                          </svg>
                          {t('edit.templates')}
                          <svg className="tpl-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                        {showTemplateMenu && (
                          <>
                            <div className="tpl-backdrop" onClick={() => setShowTemplateMenu(false)} />
                            <div className="tpl-menu">
                              <div className="tpl-menu-head">
                                <span>{t('edit.applySavedTargeting')}</span>
                                <button type="button" className="tpl-menu-save"
                                  disabled={templateBusy}
                                  onClick={() => { setShowTemplateMenu(false); handleSaveTargetingTemplate(); }}>
                                  {t('edit.saveCurrent')}
                                </button>
                              </div>
                              {targetingTemplates.length === 0 ? (
                                <div className="tpl-empty">
                                  {t('edit.nothingSavedYet')}
                                </div>
                              ) : (
                                <div className="tpl-list">
                                  {targetingTemplates.map((t) => (
                                    <div key={t.id} className="tpl-row">
                                      <button
                                        type="button"
                                        className="tpl-row-main"
                                        disabled={templateBusy}
                                        onClick={() => handleApplyTargetingTemplate(t.id)}
                                      >
                                        <span className="tpl-row-name">{t.name}</span>
                                        <span className="tpl-row-meta">
                                          {t.settings_count} settings
                                          {t.line_item_type ? ` · ${t.line_item_type}` : ''}
                                          {t.from_other_client ? ' · from another client' : ''}
                                        </span>
                                      </button>
                                      <button
                                        type="button"
                                        className="tpl-row-del"
                                        title={`Delete "${t.name}"`}
                                        disabled={templateBusy}
                                        onClick={() => handleDeleteTargetingTemplate(t.id)}
                                      >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <polyline points="3 6 5 6 21 6" />
                                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Age */}
                <div className="targeting-group">
                  <div className="targeting-row">
                    <label className="targeting-label">{t('edit.age')}</label>
                    <div className="targeting-tags">
                      {['18-24', '25-34', '35-44', '45-54', '55-64', '65+'].map((age) => (
                        <span
                          key={age}
                          className={`targeting-tag targeting-tag-toggle ${targetingAgeRanges.length === 0 || targetingAgeRanges.includes(age) ? 'active' : ''}`}
                          onClick={() => {
                            const allAges = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
                            if (targetingAgeRanges.length === 0) {
                              // All selected (empty means all) - deselect clicked one
                              setTargetingAgeRanges(allAges.filter(a => a !== age));
                            } else if (targetingAgeRanges.includes(age)) {
                              // Already selected - deselect it
                              const newRanges = targetingAgeRanges.filter(a => a !== age);
                              // If all would be deselected, set to empty (meaning all)
                              setTargetingAgeRanges(newRanges.length === 0 ? [] : newRanges);
                            } else {
                              // Not selected - add it
                              const newRanges = [...targetingAgeRanges, age];
                              // If all are now selected, set to empty (meaning all)
                              setTargetingAgeRanges(newRanges.length === allAges.length ? [] : newRanges);
                            }
                          }}
                        >
                          {age}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Gender */}
                <div className="targeting-group">
                  <div className="targeting-row">
                    <label className="targeting-label">{t('edit.gender')}</label>
                    <div className="targeting-tags">
                      {['Male', 'Female'].map((gender) => (
                        <span
                          key={gender}
                          className={`targeting-tag targeting-tag-toggle ${targetingGenders.length === 0 || targetingGenders.includes(gender) ? 'active' : ''}`}
                          onClick={() => {
                            if (targetingGenders.length === 0) {
                              setTargetingGenders(['Male', 'Female'].filter(g => g !== gender));
                            } else if (targetingGenders.includes(gender)) {
                              setTargetingGenders(targetingGenders.filter(g => g !== gender));
                            } else {
                              setTargetingGenders([...targetingGenders, gender]);
                            }
                          }}
                        >
                          {tv(gender)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Parental Status */}
                <div className="targeting-group">
                  <div className="targeting-row">
                    <label className="targeting-label">{t('edit.parentalStatus')}</label>
                    <div className="targeting-tags">
                      {['Parent', 'Not A Parent', 'Unknown'].map((status) => (
                        <span
                          key={status}
                          className={`targeting-tag targeting-tag-toggle ${targetingParentalStatuses.length === 0 || targetingParentalStatuses.includes(status) ? 'active' : ''}`}
                          onClick={() => {
                            if (targetingParentalStatuses.length === 0) {
                              setTargetingParentalStatuses(['Parent', 'Not A Parent', 'Unknown'].filter(s => s !== status));
                            } else if (targetingParentalStatuses.includes(status)) {
                              setTargetingParentalStatuses(targetingParentalStatuses.filter(s => s !== status));
                            } else {
                              setTargetingParentalStatuses([...targetingParentalStatuses, status]);
                            }
                          }}
                        >
                          {tv(status)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Income */}
                <div className="targeting-group">
                  <div className="targeting-row">
                    <label className="targeting-label">{t('edit.income')}</label>
                    <div className="targeting-tags">
                      {(() => {
                        // Income levels in order (excluding Unknown)
                        const incomeLevels = [
                          { id: 'top10', label: 'Very High' },
                          { id: 'top20', label: 'High' },
                          { id: 'top30', label: 'Above Average' },
                          { id: 'top40', label: 'Average' },
                          { id: 'top50', label: 'Below Average' },
                          { id: 'lower50', label: 'Low' },
                        ];
                        const levelIds = incomeLevels.map(l => l.id);

                        // Get current range (excluding unknown)
                        const selectedLevels = targetingHouseholdIncome.filter(id => id !== 'unknown');
                        const hasUnknown = targetingHouseholdIncome.includes('unknown');

                        // Find min and max indices of selected range
                        const selectedIndices = selectedLevels.map(id => levelIds.indexOf(id)).filter(i => i >= 0);
                        const minIdx = selectedIndices.length > 0 ? Math.min(...selectedIndices) : -1;
                        const maxIdx = selectedIndices.length > 0 ? Math.max(...selectedIndices) : -1;

                        const handleLevelClick = (clickedId: string) => {
                          const clickedIdx = levelIds.indexOf(clickedId);

                          if (selectedLevels.length === 0) {
                            // Nothing selected - select just this one
                            setTargetingHouseholdIncome(hasUnknown ? [clickedId, 'unknown'] : [clickedId]);
                          } else if (selectedLevels.length === levelIds.length) {
                            // All selected - deselect clicked, keep continuous range
                            if (clickedIdx === minIdx) {
                              // Clicked on first - remove it
                              const newRange = levelIds.slice(clickedIdx + 1);
                              setTargetingHouseholdIncome(hasUnknown ? [...newRange, 'unknown'] : newRange);
                            } else if (clickedIdx === maxIdx) {
                              // Clicked on last - remove it
                              const newRange = levelIds.slice(0, clickedIdx);
                              setTargetingHouseholdIncome(hasUnknown ? [...newRange, 'unknown'] : newRange);
                            } else {
                              // Clicked in middle - shrink to just this one
                              setTargetingHouseholdIncome(hasUnknown ? [clickedId, 'unknown'] : [clickedId]);
                            }
                          } else if (selectedLevels.includes(clickedId)) {
                            // Clicked on selected item
                            if (selectedLevels.length === 1) {
                              // Only one selected - select all
                              setTargetingHouseholdIncome(hasUnknown ? [...levelIds, 'unknown'] : [...levelIds]);
                            } else if (clickedIdx === minIdx) {
                              // Clicked on first in range - shrink from start
                              const newRange = levelIds.slice(clickedIdx + 1, maxIdx + 1);
                              setTargetingHouseholdIncome(hasUnknown ? [...newRange, 'unknown'] : newRange);
                            } else if (clickedIdx === maxIdx) {
                              // Clicked on last in range - shrink from end
                              const newRange = levelIds.slice(minIdx, clickedIdx);
                              setTargetingHouseholdIncome(hasUnknown ? [...newRange, 'unknown'] : newRange);
                            } else {
                              // Clicked in middle of range - shrink to just this one
                              setTargetingHouseholdIncome(hasUnknown ? [clickedId, 'unknown'] : [clickedId]);
                            }
                          } else {
                            // Clicked on unselected item - extend range to include it
                            const newMinIdx = Math.min(minIdx, clickedIdx);
                            const newMaxIdx = Math.max(maxIdx, clickedIdx);
                            const newRange = levelIds.slice(newMinIdx, newMaxIdx + 1);
                            setTargetingHouseholdIncome(hasUnknown ? [...newRange, 'unknown'] : newRange);
                          }
                        };

                        const isLevelActive = (id: string) => {
                          if (targetingHouseholdIncome.length === 0) return true; // All active when empty
                          return selectedLevels.includes(id);
                        };

                        return (
                          <>
                            {incomeLevels.map((income) => (
                              <span
                                key={income.id}
                                className={`targeting-tag targeting-tag-toggle ${isLevelActive(income.id) ? 'active' : ''}`}
                                onClick={() => handleLevelClick(income.id)}
                              >
                                {tv(income.label)}
                              </span>
                            ))}
                            <span
                              className={`targeting-tag targeting-tag-toggle ${targetingHouseholdIncome.length === 0 || hasUnknown ? 'active' : ''}`}
                              onClick={() => {
                                if (targetingHouseholdIncome.length === 0) {
                                  // All selected - deselect Unknown, keep levels
                                  setTargetingHouseholdIncome([...levelIds]);
                                } else if (hasUnknown) {
                                  // Has Unknown - remove it
                                  setTargetingHouseholdIncome(targetingHouseholdIncome.filter(id => id !== 'unknown'));
                                } else {
                                  // No Unknown - add it
                                  setTargetingHouseholdIncome([...targetingHouseholdIncome, 'unknown']);
                                }
                              }}
                            >
                              {tv('Unknown')}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Devices */}
                <div className="targeting-group">
                  <div className="targeting-row">
                    <label className="targeting-label">{t('edit.devices')}</label>
                    <div className="targeting-tags">
                      {['Computer', 'Smart Phone', 'Tablet', 'Connected TV'].map((device) => (
                        <span
                          key={device}
                          className={`targeting-tag targeting-tag-toggle ${targetingDevices.length === 0 || targetingDevices.includes(device) ? 'active' : ''}`}
                          onClick={() => {
                            if (targetingDevices.length === 0) {
                              setTargetingDevices(['Computer', 'Smart Phone', 'Tablet', 'Connected TV'].filter(d => d !== device));
                            } else if (targetingDevices.includes(device)) {
                              setTargetingDevices(targetingDevices.filter(d => d !== device));
                            } else {
                              setTargetingDevices([...targetingDevices, device]);
                            }
                          }}
                        >
                          {tv(device)}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Environment */}
                  <div className="targeting-row">
                    <label className="targeting-label">{t('edit.environment')}</label>
                    <div className="targeting-tags">
                      {['Web', 'App'].map((env) => (
                        <span
                          key={env}
                          className={`targeting-tag targeting-tag-toggle ${targetingEnvironment.length === 0 || targetingEnvironment.includes(env) ? 'active' : ''}`}
                          onClick={() => {
                            if (targetingEnvironment.length === 0) {
                              setTargetingEnvironment(['Web', 'App'].filter(e => e !== env));
                            } else if (targetingEnvironment.includes(env)) {
                              setTargetingEnvironment(targetingEnvironment.filter(e => e !== env));
                            } else {
                              setTargetingEnvironment([...targetingEnvironment, env]);
                            }
                          }}
                        >
                          {tv(env)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Geography - Two columns */}
                <div className="targeting-group">
                  {/* Mode Toggle */}
                  <div className="geo-mode-header">
                    <label className="targeting-label">{t('edit.geography')}</label>
                    <div className="geo-mode-toggle">
                    <button
                      className={`geo-mode-btn ${geoMode === 'regions' ? 'active' : ''}`}
                      onClick={() => setGeoMode('regions')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      {t('edit.regions')}
                    </button>
                    <button
                      className={`geo-mode-btn ${geoMode === 'proximity' ? 'active' : ''}`}
                      onClick={() => setGeoMode('proximity')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <circle cx="12" cy="12" r="8" strokeDasharray="2 2"/>
                        <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
                      </svg>
                      {t('edit.superGeo')}
                    </button>
                    </div>
                  </div>

                  {/* Regions Mode */}
                  {geoMode === 'regions' && (
                  <div className="geo-columns">
                    {/* Included Locations - Left Column */}
                    <div className="geo-column">
                      <label className="targeting-label">{t('edit.locations')}</label>
                      <div className="geo-email-container">
                      {/* Location list */}
                      {targetingGeo.length > 0 && (
                        <div className="geo-email-list">
                          {targetingGeo.map((loc) => {
                            const commaIndex = loc.name.indexOf(',');
                            const mainName = commaIndex > 0 ? loc.name.substring(0, commaIndex) : loc.name;
                            const restName = commaIndex > 0 ? loc.name.substring(commaIndex) : '';
                            return (
                              <div key={loc.id} className="geo-email-item">
                                <span className="geo-email-name">
                                  {mainName}
                                  {restName && <span className="geo-name-secondary">{restName}</span>}
                                </span>
                                <button
                                  className="geo-email-remove"
                                  onClick={() => setTargetingGeo(targetingGeo.filter(g => g.id !== loc.id))}
                                  title={t('edit.remove')}
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Search input - always visible */}
                      <div className="geo-email-search">
                        <div className="geo-email-search-wrapper">
                          <svg className="geo-email-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="M21 21l-4.35-4.35"/>
                          </svg>
                          <input
                            type="text"
                            className="geo-email-input"
                            placeholder={t('edit.searchLocation')}
                            value={geoInputValue}
                            onChange={(e) => setGeoInputValue(e.target.value)}
                            onFocus={() => setShowGeoInput(true)}
                            onBlur={() => {
                              // Delay to allow clicking on dropdown items
                              setTimeout(() => {
                                setGeoSearchResults([]);
                                setGeoInputValue('');
                              }, 200);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setGeoInputValue('');
                                setGeoSearchResults([]);
                                (e.target as HTMLInputElement).blur();
                              }
                            }}
                          />
                          {geoSearchLoading && (
                            <span className="geo-email-loading">...</span>
                          )}
                        </div>

                        {/* Autocomplete dropdown */}
                        {geoSearchResults.length > 0 && (
                          <div className="geo-email-dropdown">
                            {geoSearchResults.map((result) => (
                              <div
                                key={result.id}
                                className="geo-email-dropdown-item"
                                onClick={() => handleSelectGeoResult(result)}
                              >
                                <span className="geo-dropdown-name">{result.name}</span>
                                <span className="geo-dropdown-type">{tv(result.type)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {geoInputValue.length >= 2 && !geoSearchLoading && geoSearchResults.length === 0 && (
                          <div className="geo-email-dropdown">
                            <div className="geo-email-dropdown-empty">{t('edit.noLocations')}</div>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>

                    {/* Excluded Locations - Right Column */}
                    <div className="geo-column">
                      <label className="targeting-label targeting-label-excluded">
                        <svg className="excluded-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="4" y1="4" x2="20" y2="20"/>
                        </svg>
                        {t('edit.excludedLocations')}
                      </label>
                      <div className="geo-email-container geo-excluded-container">
                      {/* Excluded location list */}
                      {targetingGeoExcluded.length > 0 && (
                        <div className="geo-email-list">
                          {targetingGeoExcluded.map((loc) => {
                            const commaIndex = loc.name.indexOf(',');
                            const mainName = commaIndex > 0 ? loc.name.substring(0, commaIndex) : loc.name;
                            const restName = commaIndex > 0 ? loc.name.substring(commaIndex) : '';
                            return (
                              <div key={loc.id} className="geo-email-item geo-excluded-item">
                                <span className="geo-email-name">
                                  {mainName}
                                  {restName && <span className="geo-name-secondary">{restName}</span>}
                                </span>
                                <button
                                  className="geo-email-remove"
                                  onClick={() => setTargetingGeoExcluded(targetingGeoExcluded.filter(g => g.id !== loc.id))}
                                  title={t('edit.remove')}
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Search input for excluded */}
                      <div className="geo-email-search">
                        <div className="geo-email-search-wrapper">
                          <svg className="geo-email-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="M21 21l-4.35-4.35"/>
                          </svg>
                          <input
                            type="text"
                            className="geo-email-input"
                            placeholder={t('edit.searchLocationExclude')}
                            value={geoExcludedInputValue}
                            onChange={(e) => setGeoExcludedInputValue(e.target.value)}
                            onFocus={() => setShowGeoExcludedInput(true)}
                            onBlur={() => {
                              setTimeout(() => {
                                setGeoExcludedSearchResults([]);
                                setGeoExcludedInputValue('');
                              }, 200);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setGeoExcludedInputValue('');
                                setGeoExcludedSearchResults([]);
                                (e.target as HTMLInputElement).blur();
                              }
                            }}
                          />
                          {geoExcludedSearchLoading && (
                            <span className="geo-email-loading"></span>
                          )}
                        </div>

                        {/* Autocomplete dropdown */}
                        {geoExcludedSearchResults.length > 0 && (
                          <div className="geo-email-dropdown">
                            {geoExcludedSearchResults.map((result) => (
                              <div
                                key={result.id}
                                className="geo-email-dropdown-item"
                                onClick={() => handleSelectGeoExcludedResult(result)}
                              >
                                <span className="geo-dropdown-name">{result.name}</span>
                                <span className="geo-dropdown-type">{tv(result.type)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {geoExcludedInputValue.length >= 2 && !geoExcludedSearchLoading && geoExcludedSearchResults.length === 0 && (
                          <div className="geo-email-dropdown">
                            <div className="geo-email-dropdown-empty">{t('edit.noLocations')}</div>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Proximity Mode */}
                  {geoMode === 'proximity' && (
                  <div className="proximity-container">
                    {/* Saved Lists - compact chips */}
                    {proximityLists.length > 0 && (
                      <div className="proximity-saved-row">
                        <span className="proximity-saved-label">{t('edit.savedLabel')}</span>
                        {proximityLists.map((list) => (
                          <button
                            key={list.id}
                            className={`proximity-chip ${editingProximityList?.id === list.id ? 'active' : ''}`}
                            onClick={async () => {
                              // Toggle: if already selected, deselect
                              if (editingProximityList?.id === list.id) {
                                setEditingProximityList(null);
                                setSelectedProximityList(null);
                                setProximityInput('');
                                setProximityRadius(10);
                                return;
                              }
                              // Load locations from API if not already loaded
                              let locations = list.locations;
                              if (!locations || locations.length === 0) {
                                locations = await loadProximityListLocations(list.id);
                              }
                              const updatedList = { ...list, locations };
                              setEditingProximityList(updatedList);
                              setSelectedProximityList(updatedList);
                              setProximityInput(locations.map(l => `${l.lat}, ${l.lng}`).join('\n'));
                              // Radius is per-group-assignment, not per-list. list.radius
                              // is a hardcoded default (10) and would clobber the group's
                              // real radius on chip select. Prefer the group's stored radius
                              // for this list.
                              const assignedProx = selectedAdGroup?.targeting?.proximity_lists?.find(
                                (p: any) => String(p.proximity_list_id) === String(list.id)
                              );
                              setProximityRadius(
                                typeof assignedProx?.radius_km === 'number' ? assignedProx.radius_km : (list.radius ?? 10)
                              );
                              // Update the list in state with loaded locations
                              setProximityLists(proximityLists.map(l => l.id === list.id ? updatedList : l));
                            }}
                          >
                            {list.name}
                            <span className="proximity-chip-count">{list.locations.length || list.locationCount || 0}</span>
                          </button>
                        ))}
                        <button
                          className="proximity-chip proximity-chip-new"
                          onClick={() => {
                            setEditingProximityList(null);
                            setProximityInput('');
                            setProximityRadius(10);
                          }}
                        >
                          + New
                        </button>
                      </div>
                    )}

                    {/* Editor with Map */}
                    <div className="proximity-editor-grid">
                      {/* Left: textarea */}
                      <div className="proximity-editor">
                        {editingProximityList ? (
                          <div className="proximity-name-edit">
                            {isEditingProximityListName ? (
                              <>
                                <input
                                  type="text"
                                  className="proximity-name-input"
                                  value={editingProximityListName}
                                  onChange={(e) => setEditingProximityListName(e.target.value)}
                                  onBlur={() => handleRenameProximityList()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                    else if (e.key === 'Escape') {
                                      setEditingProximityListName(editingProximityList.name);
                                      setIsEditingProximityListName(false);
                                    }
                                  }}
                                  disabled={isRenamingProximityList}
                                  autoFocus
                                />
                                {isRenamingProximityList && <span className="btn-spinner"></span>}
                              </>
                            ) : (
                              <>
                                <span className="proximity-name-label">{editingProximityList.name}</span>
                                <button
                                  className="proximity-name-edit-btn"
                                  onClick={() => {
                                    setEditingProximityListName(editingProximityList.name);
                                    setIsEditingProximityListName(true);
                                  }}
                                  title={t('edit.renameList')}
                                >
                                  ✎
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <label className="targeting-label">{t('edit.newList')}</label>
                        )}
                        <textarea
                          className={`proximity-textarea ${proximityValidationError ? 'has-error' : ''}`}
                          placeholder="53.3498, -6.2603&#10;53.3456, -6.2649&#10;53.3382, -6.2591"
                          value={proximityInput}
                          onChange={(e) => {
                            setProximityInput(e.target.value);
                            if (proximityValidationError) setProximityValidationError(null);
                          }}
                          rows={6}
                        />
                        <span className="proximity-hint">{t('edit.proximityFormat')}</span>
                        {proximityValidationError && (
                          <div className="proximity-validation-error">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="12" y1="8" x2="12" y2="12"/>
                              <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            {proximityValidationError}
                          </div>
                        )}

                        <div className="proximity-options">
                          <div className="proximity-radius-row">
                            <label>{t('edit.radius')}</label>
                            <div className="proximity-radius-control">
                              <button
                                type="button"
                                className="proximity-radius-btn"
                                onClick={() => {
                                  const step = proximityRadius <= 1 ? 0.1 : 1;
                                  const newValue = Math.max(0.1, proximityRadius - step);
                                  setProximityRadius(Math.round(newValue * 10) / 10);
                                }}
                              >
                                −
                              </button>
                              <input
                                type="number"
                                className="proximity-radius-input"
                                value={proximityRadius}
                                onChange={(e) => setProximityRadius(Number(e.target.value))}
                                min={0.1}
                                max={800}
                                step={proximityRadius < 1 ? 0.1 : 1}
                              />
                              <button
                                type="button"
                                className="proximity-radius-btn"
                                onClick={() => {
                                  const step = proximityRadius < 1 ? 0.1 : 1;
                                  const newValue = Math.min(800, proximityRadius + step);
                                  setProximityRadius(Math.round(newValue * 10) / 10);
                                }}
                              >
                                +
                              </button>
                            </div>
                            <span className="proximity-radius-unit">km</span>
                          </div>
                        </div>

                        <div className="proximity-actions">
                          {editingProximityList ? (
                            <>
                              <button
                                className={`proximity-save-btn ${savingProximityList ? 'saving' : ''}`}
                                disabled={!hasProximityChanges() || savingProximityList}
                                onClick={async () => {
                                  if (!hasProximityChanges()) return;
                                  // Validate input first
                                  const validation = validateProximityInput(proximityInput);
                                  if (!validation.isValid) {
                                    setProximityValidationError(t('edit.invalidCoordinates', { count: validation.invalidLines.length }));
                                    setProximityInput(reorderProximityInputWithErrors(proximityInput));
                                    return;
                                  }
                                  setProximityValidationError(null);
                                  // Parse and update existing list via API (Sense + local DB)
                                  const success = await updateProximityListLocations(editingProximityList.id, validation.valid);
                                  if (success) {
                                    // Radius is stored locally (not in Sense list, applied during targeting)
                                    const updatedList = { ...editingProximityList, locations: validation.valid, radius: proximityRadius };
                                    setSelectedProximityList(updatedList);
                                    setEditingProximityList(updatedList);
                                  }
                                }}
                              >
                                {savingProximityList ? t('edit.saving') : t('edit.saveList')}
                              </button>
                              {showSaveListModal ? (
                                <div className="proximity-inline-save">
                                  <input
                                    type="text"
                                    className="proximity-inline-input"
                                    placeholder={t('edit.listNamePlaceholder')}
                                    value={newListName}
                                    onChange={(e) => setNewListName(e.target.value)}
                                    autoFocus
                                    onKeyDown={async (e) => {
                                      if (e.key === 'Enter' && newListName.trim() && !savingProximityList) {
                                        const validation = validateProximityInput(proximityInput);
                                        if (!validation.isValid) {
                                          setProximityValidationError(t('edit.invalidCoordinates', { count: validation.invalidLines.length }));
                                          setProximityInput(reorderProximityInputWithErrors(proximityInput));
                                          return;
                                        }
                                        setProximityValidationError(null);
                                        const newList = await createProximityList(newListName.trim(), validation.valid, proximityRadius);
                                        if (newList) {
                                          setSelectedProximityList(newList);
                                          setEditingProximityList(newList);
                                          setNewListName('');
                                          setShowSaveListModal(false);
                                        }
                                      } else if (e.key === 'Escape') {
                                        setShowSaveListModal(false);
                                        setNewListName('');
                                      }
                                    }}
                                  />
                                  <button
                                    className="proximity-inline-confirm"
                                    disabled={!newListName.trim() || savingProximityList}
                                    onClick={async () => {
                                      const validation = validateProximityInput(proximityInput);
                                      if (!validation.isValid) {
                                        setProximityValidationError(t('edit.invalidCoordinates', { count: validation.invalidLines.length }));
                                        setProximityInput(reorderProximityInputWithErrors(proximityInput));
                                        return;
                                      }
                                      setProximityValidationError(null);
                                      const newList = await createProximityList(newListName.trim(), validation.valid, proximityRadius);
                                      if (newList) {
                                        setSelectedProximityList(newList);
                                        setEditingProximityList(newList);
                                        setNewListName('');
                                        setShowSaveListModal(false);
                                      }
                                    }}
                                  >
                                    ✓
                                  </button>
                                  <button
                                    className="proximity-inline-cancel"
                                    onClick={() => {
                                      setShowSaveListModal(false);
                                      setNewListName('');
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="proximity-save-new-btn"
                                  onClick={() => setShowSaveListModal(true)}
                                >
                                  {t('edit.saveAsNewList')}
                                </button>
                              )}
                            </>
                          ) : showSaveListModal ? (
                            <div className="proximity-inline-save">
                              <input
                                type="text"
                                className="proximity-inline-input"
                                placeholder={t('edit.listNamePlaceholder')}
                                value={newListName}
                                onChange={(e) => setNewListName(e.target.value)}
                                autoFocus
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter' && newListName.trim() && !savingProximityList) {
                                    const validation = validateProximityInput(proximityInput);
                                    if (!validation.isValid) {
                                      setProximityValidationError(t('edit.invalidCoordinates', { count: validation.invalidLines.length }));
                                      setProximityInput(reorderProximityInputWithErrors(proximityInput));
                                      return;
                                    }
                                    setProximityValidationError(null);
                                    const newList = await createProximityList(newListName.trim(), validation.valid, proximityRadius);
                                    if (newList) {
                                      setSelectedProximityList(newList);
                                      setEditingProximityList(newList);
                                      setNewListName('');
                                      setShowSaveListModal(false);
                                    }
                                  } else if (e.key === 'Escape') {
                                    setShowSaveListModal(false);
                                    setNewListName('');
                                  }
                                }}
                              />
                              <button
                                className="proximity-inline-confirm"
                                disabled={!newListName.trim() || savingProximityList}
                                onClick={async () => {
                                  const validation = validateProximityInput(proximityInput);
                                  if (!validation.isValid) {
                                    setProximityValidationError(t('edit.invalidCoordinates', { count: validation.invalidLines.length }));
                                    setProximityInput(reorderProximityInputWithErrors(proximityInput));
                                    return;
                                  }
                                  setProximityValidationError(null);
                                  const newList = await createProximityList(newListName.trim(), validation.valid, proximityRadius);
                                  if (newList) {
                                    setSelectedProximityList(newList);
                                    setEditingProximityList(newList);
                                    setNewListName('');
                                    setShowSaveListModal(false);
                                  }
                                }}
                              >
                                ✓
                              </button>
                              <button
                                className="proximity-inline-cancel"
                                onClick={() => {
                                  setShowSaveListModal(false);
                                  setNewListName('');
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              className="proximity-save-btn"
                              onClick={() => setShowSaveListModal(true)}
                              disabled={!proximityInput.trim() || savingProximityList}
                            >
                              {t('edit.saveList')}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Right: Map */}
                      <div className="proximity-map">
                        {(() => {
                          const locations = proximityInput
                            ? parseProximityInput(proximityInput)
                            : editingProximityList?.locations || [];

                          if (locations.length === 0) {
                            return (
                              <div className="proximity-map-empty">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                                  <circle cx="12" cy="9" r="2.5"/>
                                </svg>
                                <span>{t('edit.addLocationsHint')}</span>
                              </div>
                            );
                          }

                          // Calculate center and bounds for map
                          const avgLat = locations.reduce((sum, l) => sum + l.lat, 0) / locations.length;
                          const avgLng = locations.reduce((sum, l) => sum + l.lng, 0) / locations.length;

                          // Map recenter component - always use current proximityRadius for real-time updates
                          const radiusKm = proximityRadius;
                          // Approximate km to degrees (1 degree ≈ 111 km at equator)
                          const radiusDeg = radiusKm / 111;

                          const MapUpdater = ({ center, locations: locs }: { center: [number, number], locations: ProximityLocation[] }) => {
                            const map = useMap();
                            useEffect(() => {
                              if (locs.length === 1) {
                                // Zoom to show the radius circle
                                const loc = locs[0];
                                const bounds: [[number, number], [number, number]] = [
                                  [loc.lat - radiusDeg, loc.lng - radiusDeg],
                                  [loc.lat + radiusDeg, loc.lng + radiusDeg]
                                ];
                                map.fitBounds(bounds, { padding: [20, 20] });
                              } else if (locs.length > 1) {
                                // Expand bounds to include radius circles
                                const minLat = Math.min(...locs.map(l => l.lat)) - radiusDeg;
                                const maxLat = Math.max(...locs.map(l => l.lat)) + radiusDeg;
                                const minLng = Math.min(...locs.map(l => l.lng)) - radiusDeg;
                                const maxLng = Math.max(...locs.map(l => l.lng)) + radiusDeg;
                                map.fitBounds([[minLat, minLng], [maxLat, maxLng]], { padding: [20, 20] });
                              }
                            }, [center, locs, map]);
                            return null;
                          };

                          return (
                            <>
                              <MapContainer
                                center={[avgLat, avgLng]}
                                zoom={12}
                                className="proximity-map-container"
                                scrollWheelZoom={false}
                              >
                                {/* Esri "Light Gray Canvas": clean light-grey basemap (like the
                                    old CARTO Positron), keyless. Base + labels overlay on top. */}
                                <TileLayer
                                  attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a>'
                                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                                />
                                <TileLayer
                                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                                />
                                <MapUpdater center={[avgLat, avgLng]} locations={locations} />
                                {locations.map((loc, idx) => {
                                  const radiusKm = proximityRadius;
                                  const radiusMeters = radiusKm * 1000;
                                  return (
                                    <React.Fragment key={loc.id || idx}>
                                      {/* Radius circle */}
                                      <Circle
                                        center={[loc.lat, loc.lng]}
                                        radius={radiusMeters}
                                        pathOptions={{
                                          color: '#FF6B4A',
                                          fillColor: '#FF6B4A',
                                          fillOpacity: 0.15,
                                          weight: 1
                                        }}
                                      />
                                      {/* Center point */}
                                      <CircleMarker
                                        center={[loc.lat, loc.lng]}
                                        radius={4}
                                        pathOptions={{
                                          color: '#fff',
                                          fillColor: '#FF6B4A',
                                          fillOpacity: 1,
                                          weight: 2
                                        }}
                                      />
                                    </React.Fragment>
                                  );
                                })}
                              </MapContainer>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {/* Advanced Targeting */}
              <div className="form-section form-section-collapsible">
                <button
                  className="form-section-header"
                  onClick={() => setShowAdvancedTargetingSection(!showAdvancedTargetingSection)}
                >
                  <h3 className="form-section-title">{t('edit.advancedTargeting')}</h3>
                  <svg className={`form-section-chevron ${showAdvancedTargetingSection ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {showAdvancedTargetingSection && (
                  <AdvancedTargeting
                    selectedItems={advancedTargetingItems}
                    onSelectionChange={handleAudienceSelectionChange}
                  />
                )}
              </div>

              {/* Remarketing Audiences - only show if advertiser has remarketing lists */}
              {availableRemarketingLists.length > 0 && (
              <div className="form-section form-section-collapsible">
                <button
                  className="form-section-header"
                  onClick={() => setShowRemarketingSection(!showRemarketingSection)}
                >
                  <h3 className="form-section-title">{t('edit.remarketing')}</h3>
                  <svg className={`form-section-chevron ${showRemarketingSection ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {showRemarketingSection && (
                  <>
                    {availableRemarketingLists.length === 0 ? (
                      <div className="targeting-empty">
                        {t('edit.noRemarketingLists')}
                      </div>
                    ) : (
                      <>
                        {/* Two columns like Inventory */}
                        <div className="inventory-columns">
                          {/* Include Column */}
                          <div className="inventory-column">
                            <div className="inventory-column-header inventory-column-header-with-toggle">
                              <div className="inventory-column-title">
                                <svg className="inventory-icon inventory-icon-whitelist" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M9 12l2 2 4-4" />
                                </svg>
                                <span>{t('edit.include')}</span>
                              </div>
                              <div
                                className="remarketing-toggle"
                                onClick={() => setRemarketingIncludeOperator(remarketingIncludeOperator === 'OR' ? 'AND' : 'OR')}
                              >
                                <span className={`remarketing-toggle-option ${remarketingIncludeOperator === 'OR' ? 'active' : ''}`}>OR</span>
                                <span className={`remarketing-toggle-option ${remarketingIncludeOperator === 'AND' ? 'active' : ''}`}>AND</span>
                              </div>
                            </div>

                            {/* Lists */}
                            <div className="proximity-saved-row">
                              {availableRemarketingLists.map((list) => {
                                const isIncluded = remarketingIncludeIds.includes(list.remote_id);
                                const isExcluded = remarketingExcludeIds.includes(list.remote_id);
                                return (
                                  <div
                                    key={list.remote_id}
                                    className={`proximity-chip proximity-chip-clickable ${isIncluded ? 'selected' : ''} ${isExcluded ? 'disabled-chip' : ''}`}
                                    onClick={() => {
                                      if (isExcluded) return;
                                      if (isIncluded) {
                                        setRemarketingIncludeIds(remarketingIncludeIds.filter(id => id !== list.remote_id));
                                      } else {
                                        setRemarketingIncludeIds([...remarketingIncludeIds, list.remote_id]);
                                      }
                                    }}
                                  >
                                    <span className={`chip-checkbox ${isIncluded ? 'checked' : ''}`}>
                                      {isIncluded && '✓'}
                                    </span>
                                    <span className="chip-label">{list.name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Exclude Column */}
                          <div className="inventory-column">
                            <div className="inventory-column-header">
                              <svg className="inventory-icon inventory-icon-blacklist" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                              </svg>
                              <span>{t('edit.exclude')}</span>
                            </div>

                            {/* Lists */}
                            <div className="proximity-saved-row">
                              {availableRemarketingLists.map((list) => {
                                const isIncluded = remarketingIncludeIds.includes(list.remote_id);
                                const isExcluded = remarketingExcludeIds.includes(list.remote_id);
                                return (
                                  <div
                                    key={list.remote_id}
                                    className={`proximity-chip proximity-chip-clickable proximity-chip-exclude ${isExcluded ? 'selected' : ''} ${isIncluded ? 'disabled-chip' : ''}`}
                                    onClick={() => {
                                      if (isIncluded) return;
                                      if (isExcluded) {
                                        setRemarketingExcludeIds(remarketingExcludeIds.filter(id => id !== list.remote_id));
                                      } else {
                                        setRemarketingExcludeIds([...remarketingExcludeIds, list.remote_id]);
                                      }
                                    }}
                                  >
                                    <span className={`chip-checkbox chip-checkbox-exclude ${isExcluded ? 'checked' : ''}`}>
                                      {isExcluded && '✓'}
                                    </span>
                                    <span className="chip-label">{list.name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Look-Alike (Optimized Targeting) */}
                        <div className={`lookalike-section ${(adGroupBidStrategy === 'AV_VIEWED' || adGroupBidStrategy === 'CIVA') ? 'lookalike-unavailable' : ''}`}>
                          <div className="lookalike-row">
                            <label className="targeting-label">{t('edit.lookAlike')}</label>
                            <div
                              className="lookalike-toggle"
                              onClick={() => {
                                if (adGroupBidStrategy !== 'AV_VIEWED' && adGroupBidStrategy !== 'CIVA') {
                                  setEnableLookAlike(!enableLookAlike);
                                }
                              }}
                            >
                              <span className={`lookalike-toggle-option ${(!enableLookAlike || adGroupBidStrategy === 'AV_VIEWED' || adGroupBidStrategy === 'CIVA') ? 'active-off' : ''}`}>OFF</span>
                              <span className={`lookalike-toggle-option ${(enableLookAlike && adGroupBidStrategy !== 'AV_VIEWED' && adGroupBidStrategy !== 'CIVA') ? 'active-on' : ''}`}>ON</span>
                            </div>
                          </div>
                          <p className="lookalike-hint">
                            {t('edit.lookAlikeHint')}
                          </p>
                          {(adGroupBidStrategy === 'AV_VIEWED' || adGroupBidStrategy === 'CIVA') && (
                            <div className="lookalike-warning">
                              <svg className="lookalike-warning-icon" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              <span><strong className="lookalike-warning-highlight">{t('edit.lookAlike')}</strong> is only available with <strong>{t('edit.maxClicks')}</strong> or <strong>{t('edit.maximizeConversions')}</strong> strategies. Change the Bid Strategy to enable this feature.</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              )}

              {/* Viewability & Brand Safety */}
              <div className="form-section form-section-collapsible">
                <button
                  className="form-section-header"
                  onClick={() => setShowViewabilitySection(!showViewabilitySection)}
                >
                  <h3 className="form-section-title">{t('edit.viewabilityBrandSafety')}</h3>
                  <svg className={`form-section-chevron ${showViewabilitySection ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {showViewabilitySection && (
                  <>
                    {/* Viewability */}
                    <div className="targeting-group">
                      <div className="targeting-row">
                        <label className="targeting-label">{t('edit.viewability')}</label>
                        <div className="targeting-tags">
                          {['40%+', '50%+', '60%+', '70%+', '80%+', '90%+'].map((level) => (
                            <span
                              key={level}
                              className={`targeting-tag targeting-tag-toggle ${targetingViewability.includes(level) ? 'active' : ''}`}
                              onClick={() => {
                                // Single select
                                if (targetingViewability.includes(level)) {
                                  setTargetingViewability([]);
                                } else {
                                  setTargetingViewability([level]);
                                }
                              }}
                            >
                              {level}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* On Screen Position */}
                      <div className="targeting-row">
                        <label className="targeting-label">{t('edit.onScreenPosition')}</label>
                        <div className="targeting-tags">
                          {['Above The Fold', 'Below The Fold', 'Unknown'].map((position) => (
                            <span
                              key={position}
                              className={`targeting-tag targeting-tag-toggle ${targetingOnScreenPosition.length === 0 || targetingOnScreenPosition.includes(position) ? 'active' : ''}`}
                              onClick={() => {
                                if (targetingOnScreenPosition.length === 0) {
                                  // All selected, deselect this one
                                  setTargetingOnScreenPosition(['Above The Fold', 'Below The Fold', 'Unknown'].filter(p => p !== position));
                                } else if (targetingOnScreenPosition.includes(position)) {
                                  setTargetingOnScreenPosition(targetingOnScreenPosition.filter(p => p !== position));
                                } else {
                                  setTargetingOnScreenPosition([...targetingOnScreenPosition, position]);
                                }
                              }}
                            >
                              {tv(position)}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Authorized Sellers */}
                      <div className="targeting-row">
                        <label className="targeting-label">{t('edit.authorizedSellers')}</label>
                        <div className="targeting-tags">
                          {['Authorized Only', 'Any'].map((seller) => (
                            <span
                              key={seller}
                              className={`targeting-tag targeting-tag-toggle ${targetingAuthorizedSellers.includes(seller) ? 'active' : ''}`}
                              onClick={() => {
                                // Single select — clicking a tag always
                                // makes it the only selected one. Older
                                // bad pushes can leave the array with
                                // both values; this collapses it back to
                                // exactly one as soon as the user clicks.
                                setTargetingAuthorizedSellers([seller]);
                              }}
                            >
                              {tv(seller)}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Content Labels Exclusion */}
                      <div className="targeting-row">
                        <label className="targeting-label targeting-label-excluded">
                          <svg className="excluded-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="4" y1="4" x2="20" y2="20"/>
                          </svg>
                          {t('edit.contentLabels')}
                        </label>
                        <div className="targeting-tags">
                          {['General (G)', 'Parental Guidance (PG)', 'Teens (T)', 'Mature (MA)'].map((label) => (
                            <span
                              key={label}
                              className={`targeting-tag targeting-tag-toggle targeting-tag-exclude ${targetingContentLabels.includes(label) ? 'active excluded' : ''}`}
                              onClick={() => {
                                if (targetingContentLabels.includes(label)) {
                                  setTargetingContentLabels(targetingContentLabels.filter(l => l !== label));
                                } else {
                                  setTargetingContentLabels([...targetingContentLabels, label]);
                                }
                              }}
                            >
                              {tv(label)}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Sensitive Categories Exclusion */}
                      <div className="targeting-row">
                        <label className="targeting-label targeting-label-excluded">
                          <svg className="excluded-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="4" y1="4" x2="20" y2="20"/>
                          </svg>
                          {t('edit.sensitiveCategories')}
                        </label>
                        <div className="targeting-tags targeting-tags-wrap">
                          {['Adult', 'Alcohol', 'Derogatory', 'Downloads Sharing', 'Drugs', 'Gambling', 'Politics', 'Profanity', 'Religion', 'Sensitive Social Issues', 'Shocking', 'Suggestive', 'Tobacco', 'Tragedy', 'Transportation Accidents', 'Violence', 'Weapons'].map((category) => (
                            <span
                              key={category}
                              className={`targeting-tag targeting-tag-toggle targeting-tag-exclude ${targetingSensitiveCategories.includes(category) ? 'active excluded' : ''}`}
                              onClick={() => {
                                if (targetingSensitiveCategories.includes(category)) {
                                  setTargetingSensitiveCategories(targetingSensitiveCategories.filter(c => c !== category));
                                } else {
                                  setTargetingSensitiveCategories([...targetingSensitiveCategories, category]);
                                }
                              }}
                            >
                              {tv(category)}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Video Player Size - only for Video type */}
                      {adGroupLineItemType === 'video' && (
                        <div className="targeting-row targeting-row-separated">
                          <label className="targeting-label">{t('edit.videoPlayerSize')}</label>
                          <div className="targeting-tags">
                            {['Small', 'Large', 'HD', 'Unknown'].map((size) => (
                              <span
                                key={size}
                                className={`targeting-tag targeting-tag-toggle ${targetingVideoPlayerSize.length === 0 || targetingVideoPlayerSize.includes(size) ? 'active' : ''}`}
                                onClick={() => {
                                  if (targetingVideoPlayerSize.length === 0) {
                                    setTargetingVideoPlayerSize(['Small', 'Large', 'HD', 'Unknown'].filter(s => s !== size));
                                  } else if (targetingVideoPlayerSize.includes(size)) {
                                    setTargetingVideoPlayerSize(targetingVideoPlayerSize.filter(s => s !== size));
                                  } else {
                                    setTargetingVideoPlayerSize([...targetingVideoPlayerSize, size]);
                                  }
                                }}
                              >
                                {tv(size)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Content Instream Position - only for Video type */}
                      {adGroupLineItemType === 'video' && (
                        <div className="targeting-row">
                          <label className="targeting-label">{t('edit.instreamPosition')}</label>
                          <div className="targeting-tags">
                            {['Pre Roll', 'Mid Roll', 'Post Roll'].map((position) => {
                              const isActive = targetingContentInstreamPosition.includes(position);
                              const totalEnabled = targetingContentInstreamPosition.length + targetingContentOutstreamPosition.length;

                              return (
                                <span
                                  key={position}
                                  className={`targeting-tag targeting-tag-toggle ${isActive ? 'active' : ''}`}
                                  onClick={() => {
                                    if (isActive) {
                                      // Disable - but check if last one
                                      const newInstream = targetingContentInstreamPosition.filter(p => p !== position);
                                      if (newInstream.length + targetingContentOutstreamPosition.length === 0) {
                                        // Can't disable all - reset both to all
                                        setTargetingContentInstreamPosition(ALL_INSTREAM);
                                        setTargetingContentOutstreamPosition(ALL_OUTSTREAM);
                                      } else {
                                        setTargetingContentInstreamPosition(newInstream);
                                      }
                                    } else {
                                      // Enable
                                      setTargetingContentInstreamPosition([...targetingContentInstreamPosition, position]);
                                    }
                                  }}
                                >
                                  {tv(position)}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Content Outstream Position - only for Video type */}
                      {adGroupLineItemType === 'video' && (
                        <div className="targeting-row">
                          <label className="targeting-label">{t('edit.outstreamPosition')}</label>
                          <div className="targeting-tags">
                            {[
                              { value: 'In Article', label: 'Sticky Video' },
                              { value: 'In Banner', label: 'In Banner' },
                              { value: 'In Feed', label: 'In Feed' },
                              { value: 'Interstitial', label: 'Overlay' }
                            ].map((position) => {
                              const isActive = targetingContentOutstreamPosition.includes(position.value);

                              return (
                                <span
                                  key={position.value}
                                  className={`targeting-tag targeting-tag-toggle ${isActive ? 'active' : ''}`}
                                  onClick={() => {
                                    if (isActive) {
                                      // Disable - but check if last one
                                      const newOutstream = targetingContentOutstreamPosition.filter(p => p !== position.value);
                                      if (targetingContentInstreamPosition.length + newOutstream.length === 0) {
                                        // Can't disable all - reset both to all
                                        setTargetingContentInstreamPosition(ALL_INSTREAM);
                                        setTargetingContentOutstreamPosition(ALL_OUTSTREAM);
                                      } else {
                                        setTargetingContentOutstreamPosition(newOutstream);
                                      }
                                    } else {
                                      // Enable
                                      setTargetingContentOutstreamPosition([...targetingContentOutstreamPosition, position.value]);
                                    }
                                  }}
                                >
                                  {tv(position.label)}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Content Stream Type and User Rewarded Content - temporarily disabled, requires targetingOptionId lookup */}
                    </div>
                  </>
                )}
              </div>

              {/* Inventory (URL Lists) */}
              <div className="form-section form-section-collapsible">
                <button
                  className="form-section-header"
                  onClick={() => setShowInventorySection(!showInventorySection)}
                >
                  <h3 className="form-section-title">{t('edit.inventory')}</h3>
                  <svg className={`form-section-chevron ${showInventorySection ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {showInventorySection && (
                  <>
                  <div className="inventory-columns">
                    {/* White List Column */}
                    <div className="inventory-column">
                      <div className="inventory-column-header">
                        <svg className="inventory-icon inventory-icon-whitelist" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          <path d="M9 12l2 2 4-4" />
                        </svg>
                        <span>{t('edit.urlWhiteList')}</span>
                      </div>

                      {/* All available lists */}
                      <div className="proximity-saved-row">
                        <span className="proximity-saved-label">{t('edit.listsLabel')}</span>
                        {allChannels.map((channel) => {
                          const isSelected = whiteListChannelIds.includes(channel.channel_id);
                          return (
                            <div
                              key={channel.channel_id}
                              className={`proximity-chip ${isSelected ? 'selected' : ''}`}
                            >
                              <span
                                className={`chip-checkbox ${isSelected ? 'checked' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) {
                                    setWhiteListChannelIds(whiteListChannelIds.filter(id => id !== channel.channel_id));
                                  } else {
                                    // Remove from black list if present
                                    setBlackListChannelIds(blackListChannelIds.filter(id => id !== channel.channel_id));
                                    setWhiteListChannelIds([...whiteListChannelIds, channel.channel_id]);
                                  }
                                }}
                              >
                                {isSelected && '✓'}
                              </span>
                              <span
                                className="chip-label"
                                onClick={() => handleSelectWhiteListChannel(channel)}
                              >
                                {channel.name}
                              </span>
                              <span className="proximity-chip-count">{channel.sites_count}</span>
                            </div>
                          );
                        })}
                        <button
                          className="proximity-chip proximity-chip-new"
                          onClick={handleNewWhiteList}
                        >
                          + New
                        </button>
                      </div>

                      {/* Editor - only show when editing or creating */}
                      {(editingWhiteListChannel || isCreatingWhiteList) && (
                        <div className="proximity-editor">
                          {editingWhiteListChannel ? (
                            <div className="proximity-name-edit">
                              {isEditingWhiteListChannelName ? (
                                <>
                                  <input
                                    type="text"
                                    className="proximity-name-input"
                                    value={editingWhiteListChannelName}
                                    onChange={(e) => setEditingWhiteListChannelName(e.target.value)}
                                    onBlur={() => {
                                      handleRenameWhiteListChannel();
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.currentTarget.blur();
                                      else if (e.key === 'Escape') {
                                        setEditingWhiteListChannelName(editingWhiteListChannel.name);
                                        setIsEditingWhiteListChannelName(false);
                                      }
                                    }}
                                    disabled={isRenamingWhiteListChannel}
                                    autoFocus
                                  />
                                  {isRenamingWhiteListChannel && <span className="btn-spinner"></span>}
                                </>
                              ) : (
                                <>
                                  <span className="proximity-name-label">{editingWhiteListChannel.name}</span>
                                  <button
                                    className="proximity-name-edit-btn"
                                    onClick={() => setIsEditingWhiteListChannelName(true)}
                                    title={t('edit.renameList')}
                                  >
                                    ✎
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <label className="targeting-label">{t('edit.newList')}</label>
                          )}
                          <textarea
                            className="proximity-textarea"
                            placeholder="example.com&#10;another-site.com&#10;good-publisher.com"
                            value={whiteListInput}
                            onChange={(e) => setWhiteListInput(e.target.value)}
                            rows={6}
                          />
                          <span className="proximity-hint">
                            {whiteListInput.split('\n').filter(line => line.trim()).length} URLs (one per line)
                          </span>

                          <div className="proximity-actions">
                            {editingWhiteListChannel ? (
                              <>
                                <button
                                  className={`proximity-save-btn ${savingWhiteList ? 'saving' : ''}`}
                                  disabled={!hasWhiteListChanges() || !whiteListInput.trim() || savingWhiteList}
                                  onClick={handleSaveWhiteList}
                                >
                                  {savingWhiteList ? (
                                    <>
                                      <span className="btn-spinner"></span>
                                      {t('edit.saving')}
                                    </>
                                  ) : t('edit.saveList')}
                                </button>
                                {showWhiteListSaveModal ? (
                                  <div className="proximity-inline-save">
                                    <input
                                      type="text"
                                      className="proximity-inline-input"
                                      placeholder={t('edit.listNamePlaceholder')}
                                      value={newWhiteListName}
                                      onChange={(e) => setNewWhiteListName(e.target.value)}
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newWhiteListName.trim() && !savingWhiteList) {
                                          handleCreateWhiteList(newWhiteListName);
                                        } else if (e.key === 'Escape') {
                                          setShowWhiteListSaveModal(false);
                                          setNewWhiteListName('');
                                        }
                                      }}
                                    />
                                    <button
                                      className="proximity-inline-confirm"
                                      disabled={!newWhiteListName.trim() || savingWhiteList}
                                      onClick={() => handleCreateWhiteList(newWhiteListName)}
                                    >
                                      {savingWhiteList ? '...' : '✓'}
                                    </button>
                                    <button
                                      className="proximity-inline-cancel"
                                      onClick={() => {
                                        setShowWhiteListSaveModal(false);
                                        setNewWhiteListName('');
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="proximity-save-new-btn"
                                    onClick={() => setShowWhiteListSaveModal(true)}
                                  >
                                    {t('edit.saveAsNewList')}
                                  </button>
                                )}
                              </>
                            ) : showWhiteListSaveModal ? (
                              <div className="proximity-inline-save">
                                <input
                                  type="text"
                                  className="proximity-inline-input"
                                  placeholder={t('edit.listNamePlaceholder')}
                                  value={newWhiteListName}
                                  onChange={(e) => setNewWhiteListName(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newWhiteListName.trim() && !savingWhiteList) {
                                      handleCreateWhiteList(newWhiteListName);
                                    } else if (e.key === 'Escape') {
                                      setShowWhiteListSaveModal(false);
                                      setNewWhiteListName('');
                                    }
                                  }}
                                />
                                <button
                                  className="proximity-inline-confirm"
                                  disabled={!newWhiteListName.trim() || savingWhiteList}
                                  onClick={() => handleCreateWhiteList(newWhiteListName)}
                                >
                                  {savingWhiteList ? '...' : '✓'}
                                </button>
                                <button
                                  className="proximity-inline-cancel"
                                  onClick={() => {
                                    setShowWhiteListSaveModal(false);
                                    setNewWhiteListName('');
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <button
                                className="proximity-save-btn"
                                disabled={!whiteListInput.trim()}
                                onClick={() => setShowWhiteListSaveModal(true)}
                              >
                                {t('edit.saveList')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Black List Column */}
                    <div className="inventory-column">
                      <div className="inventory-column-header">
                        <svg className="inventory-icon inventory-icon-blacklist" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          <path d="M15 9l-6 6M9 9l6 6" />
                        </svg>
                        <span>{t('edit.urlBlackList')}</span>
                      </div>

                      {/* All available lists */}
                      <div className="proximity-saved-row">
                        <span className="proximity-saved-label">{t('edit.listsLabel')}</span>
                        {allChannels.map((channel) => {
                          const isSelected = blackListChannelIds.includes(channel.channel_id);
                          return (
                            <div
                              key={channel.channel_id}
                              className={`proximity-chip ${isSelected ? 'selected' : ''}`}
                            >
                              <span
                                className={`chip-checkbox ${isSelected ? 'checked' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) {
                                    setBlackListChannelIds(blackListChannelIds.filter(id => id !== channel.channel_id));
                                  } else {
                                    // Remove from white list if present
                                    setWhiteListChannelIds(whiteListChannelIds.filter(id => id !== channel.channel_id));
                                    setBlackListChannelIds([...blackListChannelIds, channel.channel_id]);
                                  }
                                }}
                              >
                                {isSelected && '✓'}
                              </span>
                              <span
                                className="chip-label"
                                onClick={() => handleSelectBlackListChannel(channel)}
                              >
                                {channel.name}
                              </span>
                              <span className="proximity-chip-count">{channel.sites_count}</span>
                            </div>
                          );
                        })}
                        <button
                          className="proximity-chip proximity-chip-new"
                          onClick={handleNewBlackList}
                        >
                          + New
                        </button>
                      </div>

                      {/* Editor - only show when editing or creating */}
                      {(editingBlackListChannel || isCreatingBlackList) && (
                        <div className="proximity-editor">
                          {editingBlackListChannel ? (
                            <div className="proximity-name-edit">
                              {isEditingBlackListChannelName ? (
                                <>
                                  <input
                                    type="text"
                                    className="proximity-name-input"
                                    value={editingBlackListChannelName}
                                    onChange={(e) => setEditingBlackListChannelName(e.target.value)}
                                    onBlur={() => {
                                      handleRenameBlackListChannel();
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.currentTarget.blur();
                                      else if (e.key === 'Escape') {
                                        setEditingBlackListChannelName(editingBlackListChannel.name);
                                        setIsEditingBlackListChannelName(false);
                                      }
                                    }}
                                    disabled={isRenamingBlackListChannel}
                                    autoFocus
                                  />
                                  {isRenamingBlackListChannel && <span className="btn-spinner"></span>}
                                </>
                              ) : (
                                <>
                                  <span className="proximity-name-label">{editingBlackListChannel.name}</span>
                                  <button
                                    className="proximity-name-edit-btn"
                                    onClick={() => setIsEditingBlackListChannelName(true)}
                                    title={t('edit.renameList')}
                                  >
                                    ✎
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <label className="targeting-label">{t('edit.newList')}</label>
                          )}
                          <textarea
                            className="proximity-textarea"
                            placeholder="spam-site.com&#10;low-quality.com&#10;exclude-this.com"
                            value={blackListInput}
                            onChange={(e) => setBlackListInput(e.target.value)}
                            rows={6}
                          />
                          <span className="proximity-hint">
                            {blackListInput.split('\n').filter(line => line.trim()).length} URLs (one per line)
                          </span>

                          <div className="proximity-actions">
                            {editingBlackListChannel ? (
                              <>
                                <button
                                  className={`proximity-save-btn ${savingBlackList ? 'saving' : ''}`}
                                  disabled={!hasBlackListChanges() || !blackListInput.trim() || savingBlackList}
                                  onClick={handleSaveBlackList}
                                >
                                  {savingBlackList ? (
                                    <>
                                      <span className="btn-spinner"></span>
                                      {t('edit.saving')}
                                    </>
                                  ) : t('edit.saveList')}
                                </button>
                                {showBlackListSaveModal ? (
                                  <div className="proximity-inline-save">
                                    <input
                                      type="text"
                                      className="proximity-inline-input"
                                      placeholder={t('edit.listNamePlaceholder')}
                                      value={newBlackListName}
                                      onChange={(e) => setNewBlackListName(e.target.value)}
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newBlackListName.trim() && !savingBlackList) {
                                          handleCreateBlackList(newBlackListName);
                                        } else if (e.key === 'Escape') {
                                          setShowBlackListSaveModal(false);
                                          setNewBlackListName('');
                                        }
                                      }}
                                    />
                                    <button
                                      className="proximity-inline-confirm"
                                      disabled={!newBlackListName.trim() || savingBlackList}
                                      onClick={() => handleCreateBlackList(newBlackListName)}
                                    >
                                      {savingBlackList ? '...' : '✓'}
                                    </button>
                                    <button
                                      className="proximity-inline-cancel"
                                      onClick={() => {
                                        setShowBlackListSaveModal(false);
                                        setNewBlackListName('');
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="proximity-save-new-btn"
                                    onClick={() => setShowBlackListSaveModal(true)}
                                  >
                                    {t('edit.saveAsNewList')}
                                  </button>
                                )}
                              </>
                            ) : showBlackListSaveModal ? (
                              <div className="proximity-inline-save">
                                <input
                                  type="text"
                                  className="proximity-inline-input"
                                  placeholder={t('edit.listNamePlaceholder')}
                                  value={newBlackListName}
                                  onChange={(e) => setNewBlackListName(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newBlackListName.trim() && !savingBlackList) {
                                      handleCreateBlackList(newBlackListName);
                                    } else if (e.key === 'Escape') {
                                      setShowBlackListSaveModal(false);
                                      setNewBlackListName('');
                                    }
                                  }}
                                />
                                <button
                                  className="proximity-inline-confirm"
                                  disabled={!newBlackListName.trim() || savingBlackList}
                                  onClick={() => handleCreateBlackList(newBlackListName)}
                                >
                                  ✓
                                </button>
                                <button
                                  className="proximity-inline-cancel"
                                  onClick={() => {
                                    setShowBlackListSaveModal(false);
                                    setNewBlackListName('');
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <button
                                className="proximity-save-btn"
                                disabled={!blackListInput.trim()}
                                onClick={() => setShowBlackListSaveModal(true)}
                              >
                                {t('edit.saveList')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Page Languages */}
                  <div className="targeting-group" style={{ marginTop: '20px' }}>
                    <div className="targeting-row">
                      <label className="targeting-label">{t('edit.pageLanguages')}</label>
                      <div className="targeting-dropdown-container" ref={languageDropdownRef}>
                        <button
                          className={`targeting-dropdown-trigger language-trigger ${showLanguageDropdown ? 'active' : ''} ${targetingLanguages.length > 0 ? 'has-selection' : ''}`}
                          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                        >
                          <span className="dropdown-trigger-text">
                            {targetingLanguages.length === 0 ? (
                              t('edit.allLanguages')
                            ) : (
                              <><span className="language-count">{targetingLanguages.length}</span> {t('edit.selectedSuffix')}</>
                            )}
                          </span>
                          <svg className={`dropdown-chevron ${showLanguageDropdown ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                        {showLanguageDropdown && (
                          <div className="targeting-dropdown-panel language-panel">
                            <div className="language-search-wrapper">
                              <svg className="language-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="M21 21l-4.35-4.35" />
                              </svg>
                              <input
                                type="text"
                                className="language-search-input"
                                placeholder={t('edit.searchLanguages')}
                                value={languageSearchValue}
                                onChange={(e) => setLanguageSearchValue(e.target.value)}
                                autoFocus
                              />
                              {languageSearchValue && (
                                <button
                                  className="language-search-clear"
                                  onClick={() => setLanguageSearchValue('')}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            <div className="language-list">
                              {availableLanguages
                                .filter(lang => (lang.name + ' ' + languageLabel(lang.name)).toLowerCase().includes(languageSearchValue.toLowerCase()))
                                .map((lang, index) => (
                                  <div
                                    key={lang.id}
                                    className={`language-item ${targetingLanguages.includes(lang.name) ? 'selected' : ''}`}
                                    style={{ animationDelay: `${index * 15}ms` }}
                                  >
                                    <label className="language-checkbox-wrapper">
                                      <input
                                        type="checkbox"
                                        className="language-checkbox-input"
                                        checked={targetingLanguages.includes(lang.name)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setTargetingLanguages([...targetingLanguages, lang.name]);
                                          } else {
                                            setTargetingLanguages(targetingLanguages.filter(l => l !== lang.name));
                                          }
                                        }}
                                      />
                                      <span className="language-checkbox-custom">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      </span>
                                      <span className="language-name">{languageLabel(lang.name)}</span>
                                    </label>
                                  </div>
                                ))}
                              {availableLanguages.filter(lang =>
                                (lang.name + ' ' + languageLabel(lang.name)).toLowerCase().includes(languageSearchValue.toLowerCase())
                              ).length === 0 && (
                                <div className="language-empty">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M8 15s1.5-2 4-2 4 2 4 2" />
                                    <line x1="9" y1="9" x2="9.01" y2="9" />
                                    <line x1="15" y1="9" x2="15.01" y2="9" />
                                  </svg>
                                  <span>{t('edit.noLanguages')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Show selected languages as tags below */}
                    {targetingLanguages.length > 0 && (
                      <div className="targeting-row targeting-selected-tags">
                        <label className="targeting-label"></label>
                        <div className="language-selected-tags">
                          {targetingLanguages.map((lang, index) => (
                            <span
                              key={lang}
                              className="language-tag"
                              style={{ animationDelay: `${index * 30}ms` }}
                            >
                              <span className="language-tag-text">{languageLabel(lang)}</span>
                              <button
                                className="language-tag-remove"
                                onClick={() => setTargetingLanguages(targetingLanguages.filter(l => l !== lang))}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ))}
                          {targetingLanguages.length > 1 && (
                            <button
                              className="language-clear-all"
                              onClick={() => setTargetingLanguages([])}
                            >
                              {t('edit.clearAllLower')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content Category (Display groups only) */}
                  {adGroupLineItemType === 'display' && (
                    <div className="targeting-group" style={{ marginTop: '20px' }}>
                      <div className="targeting-row">
                        <label className="targeting-label">{t('edit.contentCategory')}</label>
                        <div className="targeting-dropdown-container" ref={categoryDropdownRef}>
                          <button
                            className={`targeting-dropdown-trigger category-trigger ${showCategoryDropdown ? 'active' : ''} ${targetingCategories.length > 0 ? 'has-selection' : ''}`}
                            onClick={() => {
                              const opening = !showCategoryDropdown;
                              setShowCategoryDropdown(opening);
                              if (opening) {
                                setCategoryParentId(null);
                                setCategorySearchValue('');
                              }
                            }}
                          >
                            <span className="dropdown-trigger-text">
                              {targetingCategories.length === 0 ? (
                                t('edit.allCategories')
                              ) : (
                                <><span className="category-count">{targetingCategories.length}</span> {t('edit.selectedSuffix')}</>
                              )}
                            </span>
                            <svg className={`dropdown-chevron ${showCategoryDropdown ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </button>
                          {showCategoryDropdown && (
                            <div className="targeting-dropdown-panel category-tree-panel">
                              {/* Breadcrumb navigation */}
                              <div className="category-nav-header">
                                <button
                                  className={`category-home-btn ${categoryBreadcrumb.length === 0 ? 'current' : ''}`}
                                  onClick={() => {
                                    setCategoryParentId(null);
                                    setCategorySearchValue('');
                                  }}
                                  title={t('edit.allCategories')}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                  </svg>
                                </button>
                                {categoryBreadcrumb.map((item, index) => (
                                  <React.Fragment key={item.id}>
                                    <svg className="category-nav-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M9 18l6-6-6-6" />
                                    </svg>
                                    <button
                                      className={`category-nav-item ${index === categoryBreadcrumb.length - 1 ? 'current' : ''}`}
                                      onClick={() => {
                                        setCategoryParentId(item.id);
                                        setCategorySearchValue('');
                                      }}
                                    >
                                      {item.name}
                                    </button>
                                  </React.Fragment>
                                ))}
                              </div>
                              <div className="category-search-wrapper">
                                <svg className="category-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="11" cy="11" r="8" />
                                  <path d="M21 21l-4.35-4.35" />
                                </svg>
                                <input
                                  type="text"
                                  className="category-search-input"
                                  placeholder={t('edit.searchCategories')}
                                  value={categorySearchValue}
                                  onChange={(e) => setCategorySearchValue(e.target.value)}
                                />
                                {categorySearchValue && (
                                  <button
                                    className="category-search-clear"
                                    onClick={() => setCategorySearchValue('')}
                                  >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                              <div className="category-tree-list">
                                {categoriesLoading ? (
                                  <div className="category-loading">
                                    <div className="category-loading-spinner"></div>
                                    <span>{t('edit.loadingCategories')}</span>
                                  </div>
                                ) : (
                                  <>
                                    {filteredCategories.map((cat, index) => (
                                      <div
                                        key={cat.id}
                                        className={`category-tree-item ${targetingCategories.some(tc => tc.id === cat.id) ? 'selected' : ''} ${cat.hasChildren ? 'has-children' : ''}`}
                                        style={{ animationDelay: `${index * 20}ms` }}
                                        onClick={() => {
                                          if (cat.hasChildren) {
                                            setCategoryParentId(cat.id);
                                            setCategorySearchValue('');
                                          }
                                        }}
                                      >
                                        <label
                                          className="category-checkbox-wrapper"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <input
                                            type="checkbox"
                                            className="category-checkbox-input"
                                            checked={targetingCategories.some(tc => tc.id === cat.id)}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setTargetingCategories([...targetingCategories, { id: cat.id, name: cat.name }]);
                                              } else {
                                                setTargetingCategories(targetingCategories.filter(tc => tc.id !== cat.id));
                                              }
                                            }}
                                          />
                                          <span className="category-checkbox-custom">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                              <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                          </span>
                                        </label>
                                        <span className="category-name">{cat.name}</span>
                                        {cat.hasChildren && (
                                          <span className="category-drill-icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <path d="M9 18l6-6-6-6" />
                                            </svg>
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                    {filteredCategories.length === 0 && (
                                      <div className="category-empty">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                          <circle cx="12" cy="12" r="10" />
                                          <path d="M8 15s1.5-2 4-2 4 2 4 2" />
                                          <line x1="9" y1="9" x2="9.01" y2="9" />
                                          <line x1="15" y1="9" x2="15.01" y2="9" />
                                        </svg>
                                        <span>{categorySearchValue ? t('edit.noMatchingCategories') : t('edit.noSubcategories')}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Show selected categories as tags below */}
                      {targetingCategories.length > 0 && (
                        <div className="targeting-row targeting-selected-tags">
                          <label className="targeting-label"></label>
                          <div className="category-selected-tags">
                            {targetingCategories.map((cat, index) => (
                              <span
                                key={cat.id}
                                className="category-tag"
                                style={{ animationDelay: `${index * 30}ms` }}
                              >
                                <span className="category-tag-text">{cat.name}</span>
                                <button
                                  className="category-tag-remove"
                                  onClick={() => setTargetingCategories(targetingCategories.filter(tc => tc.id !== cat.id))}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                  </svg>
                                </button>
                              </span>
                            ))}
                            {targetingCategories.length > 1 && (
                              <button
                                className="category-clear-all"
                                onClick={() => setTargetingCategories([])}
                              >
                                {t('edit.clearAllLower')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                  {/* Negative Keyword Lists */}
                  <div className="targeting-group" style={{ marginTop: '20px' }}>
                    <div className="inventory-column-header" style={{ marginBottom: '12px' }}>
                      <svg className="inventory-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-muted)' }}>
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                      <span>{t('edit.negativeKeywordLists')}</span>
                      {negativeKeywordListIds.length >= 4 && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>(max 4)</span>
                      )}
                    </div>

                    <div className="proximity-saved-row">
                      <span className="proximity-saved-label">{t('edit.listsLabel')}</span>
                      {allNegativeKeywordLists.map((list) => {
                        const isSelected = negativeKeywordListIds.includes(list.id);
                        return (
                          <div
                            key={list.id}
                            className={`proximity-chip ${isSelected ? 'selected' : ''}`}
                          >
                            <span
                              className={`chip-checkbox ${isSelected ? 'checked' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSelected) {
                                  setNegativeKeywordListIds(negativeKeywordListIds.filter(id => id !== list.id));
                                } else {
                                  if (negativeKeywordListIds.length >= 4) return;
                                  setNegativeKeywordListIds([...negativeKeywordListIds, list.id]);
                                }
                              }}
                            >
                              {isSelected && '✓'}
                            </span>
                            <span
                              className="chip-label"
                              onClick={() => handleSelectNegKwList(list)}
                            >
                              {list.name}
                              <span className="proximity-chip-count">{list.keywords_count || 0}</span>
                            </span>
                          </div>
                        );
                      })}
                      <button
                        className="proximity-chip proximity-chip-new"
                        onClick={handleNewNegKwList}
                      >
                        + New
                      </button>
                    </div>

                    {/* Editor - only show when editing or creating */}
                    {(editingNegKwList || isCreatingNegKwList) && (
                      <div className="proximity-editor">
                        {editingNegKwList ? (
                          <div className="proximity-name-edit">
                            {isEditingNegKwListName ? (
                              <>
                                <input
                                  type="text"
                                  className="proximity-name-input"
                                  value={editingNegKwListName}
                                  onChange={(e) => setEditingNegKwListName(e.target.value)}
                                  onBlur={() => {
                                    handleRenameNegKwList();
                                    setIsEditingNegKwListName(false);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.currentTarget.blur();
                                    } else if (e.key === 'Escape') {
                                      setEditingNegKwListName(editingNegKwList.name);
                                      setIsEditingNegKwListName(false);
                                    }
                                  }}
                                  disabled={isRenamingNegKwList}
                                  autoFocus
                                />
                                {isRenamingNegKwList && <span className="btn-spinner"></span>}
                              </>
                            ) : (
                              <>
                                <span className="proximity-name-label">{editingNegKwList.name}</span>
                                <button
                                  className="proximity-name-edit-btn"
                                  onClick={() => setIsEditingNegKwListName(true)}
                                  title={t('edit.renameList')}
                                >
                                  ✎
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <label className="targeting-label">{t('edit.newList')}</label>
                        )}
                        <textarea
                          className="proximity-textarea"
                          placeholder={t('edit.negKwPlaceholder')}
                          value={negKwListInput}
                          onChange={(e) => setNegKwListInput(e.target.value)}
                          rows={6}
                        />
                        <span className="proximity-hint">
                          {negKwListInput.split('\n').filter(line => line.trim()).length} keywords (one per line)
                        </span>

                        <div className="proximity-actions">
                          {editingNegKwList ? (
                            <>
                              <button
                                className={`proximity-save-btn ${savingNegKwList ? 'saving' : ''}`}
                                disabled={!hasNegKwListChanges() || !negKwListInput.trim() || savingNegKwList}
                                onClick={handleUpdateNegKwList}
                              >
                                {savingNegKwList ? (
                                  <>
                                    <span className="btn-spinner"></span>
                                    {t('edit.saving')}
                                  </>
                                ) : t('edit.saveList')}
                              </button>
                              {showNegKwListSaveModal ? (
                                <div className="proximity-inline-save">
                                  <input
                                    type="text"
                                    className="proximity-inline-input"
                                    placeholder={t('edit.listNamePlaceholder')}
                                    value={newNegKwListName}
                                    onChange={(e) => setNewNegKwListName(e.target.value)}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && newNegKwListName.trim() && !savingNegKwList) {
                                        handleSaveNewNegKwList(newNegKwListName);
                                      } else if (e.key === 'Escape') {
                                        setShowNegKwListSaveModal(false);
                                        setNewNegKwListName('');
                                      }
                                    }}
                                  />
                                  <button
                                    className="proximity-inline-confirm"
                                    disabled={!newNegKwListName.trim() || savingNegKwList}
                                    onClick={() => handleSaveNewNegKwList(newNegKwListName)}
                                  >
                                    {savingNegKwList ? '...' : '✓'}
                                  </button>
                                  <button
                                    className="proximity-inline-cancel"
                                    onClick={() => {
                                      setShowNegKwListSaveModal(false);
                                      setNewNegKwListName('');
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="proximity-save-new-btn"
                                  onClick={() => setShowNegKwListSaveModal(true)}
                                >
                                  {t('edit.saveAsNewList')}
                                </button>
                              )}
                            </>
                          ) : showNegKwListSaveModal ? (
                            <div className="proximity-inline-save">
                              <input
                                type="text"
                                className="proximity-inline-input"
                                placeholder={t('edit.listNamePlaceholder')}
                                value={newNegKwListName}
                                onChange={(e) => setNewNegKwListName(e.target.value)}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && newNegKwListName.trim() && !savingNegKwList) {
                                    handleSaveNewNegKwList(newNegKwListName);
                                  } else if (e.key === 'Escape') {
                                    setShowNegKwListSaveModal(false);
                                    setNewNegKwListName('');
                                  }
                                }}
                              />
                              <button
                                className="proximity-inline-confirm"
                                disabled={!newNegKwListName.trim() || savingNegKwList}
                                onClick={() => handleSaveNewNegKwList(newNegKwListName)}
                              >
                                {savingNegKwList ? '...' : '✓'}
                              </button>
                              <button
                                className="proximity-inline-cancel"
                                onClick={() => {
                                  setShowNegKwListSaveModal(false);
                                  setNewNegKwListName('');
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              className="proximity-save-btn"
                              disabled={!negKwListInput.trim() || savingNegKwList}
                              onClick={() => setShowNegKwListSaveModal(true)}
                            >
                              {t('edit.saveList')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  </>
                )}
              </div>

              {/* Day & Time */}
              <div className="form-section form-section-collapsible">
                <div
                  className="form-section-header"
                  onClick={() => setShowDayTimeSection(!showDayTimeSection)}
                >
                  <h3 className="form-section-title">{t('edit.dayTime')}</h3>
                  <svg className={`form-section-chevron ${showDayTimeSection ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {showDayTimeSection && (
                  <div className="day-time-section">
                    <div className="day-time-header">
                      <div className="day-time-actions">
                        <button
                          className="day-time-action-link"
                          onClick={() => {
                            // Select all
                            const allCells = new Set<string>();
                            const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
                            days.forEach(day => {
                              for (let h = 0; h < 24; h++) {
                                allCells.add(`${day}-${h}`);
                              }
                            });
                            setTargetingDayTime(allCells);
                          }}
                        >
                          {t('edit.selectAll')}
                        </button>
                        <button
                          className="day-time-action-link"
                          onClick={() => setTargetingDayTime(new Set())}
                        >
                          {t('edit.clearAll')}
                        </button>
                      </div>
                    </div>

                    <div className="day-time-grid">
                      {/* Hour headers */}
                      {(() => {
                        const allDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
                        // Check which columns have any selected cells
                        const columnHasSelection = Array.from({ length: 24 }, (_, h) =>
                          allDays.some(day => targetingDayTime.has(`${day}-${h}`))
                        );
                        return (
                          <div className="day-time-row day-time-header-row">
                            <div className="day-time-day-label"></div>
                            {Array.from({ length: 24 }, (_, h) => (
                              <div
                                key={h}
                                className={`day-time-hour-label ${!columnHasSelection[h] ? 'inactive' : ''}`}
                                onClick={() => {
                                  // Toggle entire column - if more than half selected, clear all; otherwise select all
                                  const selectedCount = allDays.filter(day => targetingDayTime.has(`${day}-${h}`)).length;
                                  const shouldClear = selectedCount > allDays.length / 2;
                                  const newSet = new Set(targetingDayTime);
                                  allDays.forEach(day => {
                                    const key = `${day}-${h}`;
                                    if (shouldClear) {
                                      newSet.delete(key);
                                    } else {
                                      newSet.add(key);
                                    }
                                  });
                                  setTargetingDayTime(newSet);
                                }}
                              >
                                {h}
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Day rows */}
                      {[
                        { key: 'MONDAY', label: 'Mon', weekend: false },
                        { key: 'TUESDAY', label: 'Tue', weekend: false },
                        { key: 'WEDNESDAY', label: 'Wed', weekend: false },
                        { key: 'THURSDAY', label: 'Thu', weekend: false },
                        { key: 'FRIDAY', label: 'Fri', weekend: false },
                        { key: 'SATURDAY', label: 'Sat', weekend: true },
                        { key: 'SUNDAY', label: 'Sun', weekend: true },
                      ].map(day => {
                        // Check if this row has any selected cells
                        const rowHasSelection = Array.from({ length: 24 }, (_, h) => h).some(h =>
                          targetingDayTime.has(`${day.key}-${h}`)
                        );
                        return (
                        <div key={day.key} className={`day-time-row ${day.weekend ? 'weekend' : ''} ${!rowHasSelection ? 'inactive' : ''}`}>
                          <div
                            className="day-time-day-label"
                            onClick={() => {
                              // Toggle entire row - if more than half selected, clear all; otherwise select all
                              const selectedCount = Array.from({ length: 24 }, (_, h) => h).filter(h =>
                                targetingDayTime.has(`${day.key}-${h}`)
                              ).length;
                              const shouldClear = selectedCount > 12; // more than half of 24
                              const newSet = new Set(targetingDayTime);
                              for (let h = 0; h < 24; h++) {
                                const key = `${day.key}-${h}`;
                                if (shouldClear) {
                                  newSet.delete(key);
                                } else {
                                  newSet.add(key);
                                }
                              }
                              setTargetingDayTime(newSet);
                            }}
                          >
                            {tv(day.label)}
                          </div>
                          {Array.from({ length: 24 }, (_, h) => {
                            const cellKey = `${day.key}-${h}`;
                            const isSelected = targetingDayTime.has(cellKey);
                            return (
                              <div
                                key={h}
                                className={`day-time-cell ${isSelected ? 'selected' : ''}`}
                                onMouseDown={(e) => {
                                  e.preventDefault(); // Prevent text selection
                                  setIsDayTimeDragging(true);
                                  // Set mode based on current cell state
                                  const mode = isSelected ? 'deselect' : 'select';
                                  setDayTimeDragMode(mode);
                                  // Apply to this cell
                                  const newSet = new Set(targetingDayTime);
                                  if (mode === 'deselect') {
                                    newSet.delete(cellKey);
                                  } else {
                                    newSet.add(cellKey);
                                  }
                                  setTargetingDayTime(newSet);
                                }}
                                onMouseEnter={() => {
                                  if (isDayTimeDragging) {
                                    const newSet = new Set(targetingDayTime);
                                    if (dayTimeDragMode === 'deselect') {
                                      newSet.delete(cellKey);
                                    } else {
                                      newSet.add(cellKey);
                                    }
                                    setTargetingDayTime(newSet);
                                  }
                                }}
                              />
                            );
                          })}
                        </div>
                        );
                      })}
                    </div>

                  </div>
                )}
              </div>

              {/* Creatives */}
              {campaign?.advertiser_id && selectedAdGroup && (
                <GroupCreativesSection
                  groupId={selectedAdGroup.id}
                  advertiserId={campaign.advertiser_id}
                  groupType={adGroupLineItemType}
                  assignedCreativeIds={adGroupCreativeIds}
                  onCreativesChange={(ids) => {
                    setAdGroupCreativeIds(ids);
                  }}
                  disabled={isLocked || !!selectedAdGroup.push_pending || !!selectedAdGroup.targeting_push_pending}
                />
              )}

              {/* Ad Group Actions */}
              <div className={`form-actions-bottom ${savingAdGroup ? 'saving-state' : ''}`}>
                <button className="btn-secondary-sm" onClick={handleCloseAdGroup} disabled={savingAdGroup}>{t('edit.cancel')}</button>
                {!isAnalyst && !isLocked && <button
                  className={`btn-primary-sm save-campaign-btn ${savingAdGroup ? 'saving' : ''} ${adGroupSaved ? 'saved' : ''} ${targetingPushPending ? 'inactive pending' : ''} ${(adGroupBidStrategy === 'CPA' && selectedGoalIds.length === 0) ? 'inactive' : ''} ${(adGroupMediaPlanType !== 'budget' && !adGroupPlanTarget) ? 'inactive' : ''} ${selectedAdGroup.id === '' ? (!adGroupName.trim() ? 'inactive' : '') : ((!hasAdGroupChanges() && !savingAdGroup && !adGroupSaved && !targetingPushPending) ? 'inactive' : '')}`}
                  onClick={handleSaveAdGroup}
                  disabled={targetingPushPending ? true : ((adGroupBidStrategy === 'CPA' && selectedGoalIds.length === 0) || (adGroupMediaPlanType !== 'budget' && !adGroupPlanTarget) || savingAdGroup || adGroupSaved || (selectedAdGroup.id === '' ? !adGroupName.trim() : !hasAdGroupChanges()))}
                >
                  {savingAdGroup ? (
                    <>
                      <span className="btn-spinner"></span>
                      {selectedAdGroup.id === '' ? t('edit.creating') : t('edit.saving')}
                    </>
                  ) : adGroupSaved ? (
                    <>
                      <svg className="btn-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      {selectedAdGroup.id === '' ? t('edit.created') : t('edit.saved')}
                    </>
                  ) : (
                    selectedAdGroup.id === '' ? t('edit.createGroup') : t('edit.saveAdGroup')
                  )}
                </button>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Negative Keyword List Confirmation Modal */}
      {showDeleteNegKwListConfirm && editingNegKwList && (
        <div className="modal-overlay" onClick={() => !isDeletingNegKwList && setShowDeleteNegKwListConfirm(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('edit.deleteListQ')}</h3>
            <p>
              {t('edit.aboutToDelete')} <strong>"{editingNegKwList.name}"</strong>.<br/>
              {t('edit.cannotUndo')}
            </p>
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowDeleteNegKwListConfirm(false)}
                disabled={isDeletingNegKwList}
              >
                {t('edit.cancel')}
              </button>
              <button
                className="message-modal-btn-danger"
                onClick={handleDeleteNegKwList}
                disabled={isDeletingNegKwList}
              >
                {isDeletingNegKwList ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('edit.deleting')}
                  </>
                ) : t('edit.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete White List Channel Confirmation Modal */}
      {showDeleteWhiteListConfirm && editingWhiteListChannel && (
        <div className="modal-overlay" onClick={() => !isDeletingWhiteListChannel && setShowDeleteWhiteListConfirm(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('edit.deleteChannelQ')}</h3>
            <p>
              {t('edit.aboutToDelete')} <strong>"{editingWhiteListChannel.name}"</strong>.<br/>
              {t('edit.cannotUndo')}
            </p>
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowDeleteWhiteListConfirm(false)}
                disabled={isDeletingWhiteListChannel}
              >
                {t('edit.cancel')}
              </button>
              <button
                className="message-modal-btn-danger"
                onClick={handleDeleteWhiteListChannel}
                disabled={isDeletingWhiteListChannel}
              >
                {isDeletingWhiteListChannel ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('edit.deleting')}
                  </>
                ) : t('edit.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Black List Channel Confirmation Modal */}
      {showDeleteBlackListConfirm && editingBlackListChannel && (
        <div className="modal-overlay" onClick={() => !isDeletingBlackListChannel && setShowDeleteBlackListConfirm(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('edit.deleteChannelQ')}</h3>
            <p>
              {t('edit.aboutToDelete')} <strong>"{editingBlackListChannel.name}"</strong>.<br/>
              {t('edit.cannotUndo')}
            </p>
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowDeleteBlackListConfirm(false)}
                disabled={isDeletingBlackListChannel}
              >
                {t('edit.cancel')}
              </button>
              <button
                className="message-modal-btn-danger"
                onClick={handleDeleteBlackListChannel}
                disabled={isDeletingBlackListChannel}
              >
                {isDeletingBlackListChannel ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('edit.deleting')}
                  </>
                ) : t('edit.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Proximity List Confirmation Modal */}
      {showDeleteProximityListConfirm && editingProximityList && (
        <div className="modal-overlay" onClick={() => !deletingProximityList && setShowDeleteProximityListConfirm(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('edit.deleteSuperGeoQ')}</h3>
            <p>
              {t('edit.aboutToDelete')} <strong>"{editingProximityList.name}"</strong>.<br/>
              {t('edit.cannotUndo')}
            </p>
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowDeleteProximityListConfirm(false)}
                disabled={deletingProximityList}
              >
                {t('edit.cancel')}
              </button>
              <button
                className="message-modal-btn-danger"
                onClick={handleDeleteProximityList}
                disabled={deletingProximityList}
              >
                {deletingProximityList ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('edit.deleting')}
                  </>
                ) : t('edit.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {/* Delete/Archive Campaign Confirmation Modal */}
      {showDeleteModal && (() => {
        const isPublished = !!campaign?.ads_campaign_id;
        return (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-icon danger">
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </div>
            <h3>{isPublished ? t('edit.archiveCampaignQ') : t('edit.deleteCampaignQ')}</h3>
            <p>
              {isPublished ? t('edit.archiveCampaignPre') : t('edit.deleteCampaignPre')} <strong>"{campaignName}"</strong>{isPublished ? t('edit.archiveCampaignPost') : t('edit.deleteCampaignPost')}
              <br /><br />
              <span style={{ color: '#ef4444' }}>{t('edit.cannotUndo')}</span>
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn-secondary-sm"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('edit.cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteCampaign}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="btn-spinner"></span>
                    {isPublished ? t('edit.archiving') : t('edit.deleting')}
                  </>
                ) : (
                  isPublished ? t('edit.archiveCampaign') : t('edit.deleteCampaign')
                )}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Delete/Archive Group Confirmation Modal */}
      {showDeleteGroupModal && groupToDelete && (() => {
        const isPublished = !!groupToDelete.ads_io_id;
        return (
        <div className="modal-overlay" onClick={() => !isDeletingGroup && setShowDeleteGroupModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-icon danger">
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </div>
            <h3>{isPublished ? t('edit.archiveGroupQ') : t('edit.deleteGroupQ')}</h3>
            <p>
              {isPublished ? t('edit.archiveGroupPre') : t('edit.deleteGroupPre')} <strong>"{cleanName(groupToDelete.name)}"</strong>{isPublished ? t('edit.archiveGroupPost') : t('edit.deleteGroupPost')}
              <br /><br />
              <span style={{ color: '#ef4444' }}>{t('edit.cannotUndo')}</span>
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn-secondary-sm"
                onClick={() => setShowDeleteGroupModal(false)}
                disabled={isDeletingGroup}
              >
                {t('edit.cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteGroup}
                disabled={isDeletingGroup}
              >
                {isDeletingGroup ? (
                  <>
                    <span className="btn-spinner"></span>
                    {isPublished ? t('edit.archiving') : t('edit.deleting')}
                  </>
                ) : (
                  isPublished ? t('edit.archiveGroup') : t('edit.deleteGroup')
                )}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {showMessageModal && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`message-modal-icon ${messageModalType}`}>
              {messageModalType === 'error' ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </div>
            <h3>{messageModalType === 'error' ? t('edit.attention') : t('edit.notice')}</h3>
            <p>{messageModalText}</p>
            <button
              className={`message-modal-btn ${messageModalType === 'error' ? 'warning' : ''}`}
              onClick={() => setShowMessageModal(false)}
            >
              {t('edit.gotIt')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCampaignPage;
